/**
 * MapController - Centralized service for managing Cesium map layers and filters
 *
 * This service handles all interactions with the Cesium viewer, including:
 * - Adding/removing reference layers (bathymetry, NOAA charts, fishing areas, etc.)
 * - Managing filters (trawl survey, LEK, depth ranges, etc.)
 * - Handling layer dependencies (RFMO parent/child relationships)
 * - Initializing layers from initialLayers configuration
 *
 * Public API uses kebab-case slugs only. Internal Cesium logic uses internal keys (camelCase).
 */

/** Kebab-case slug (layout API) -> internal layer key (Cesium). Public API is slug-only. */
const SLUG_TO_INTERNAL_KEY = {
  'rocky-bottom-type': 'rockyBottom',
  'enhanced-bathymetry': 'enhancedBathymetry',
  'noaa-fishing-closed-areas': 'fishingClosedAreas',
  'scallop-areas': 'scallopAreas',
  'lobster-management-areas': 'lobsterAreas',
  'international-fishery-mgmt-areas': 'internationalFisheryManagement',
  'us-regional-fishery-mgmt-councils': 'fisheriesManagementCouncil',
  'us-congressional-districts': 'congressionalDistricts',
  'noaa-navigation-charts': 'noaaCharts',
  'fishing-ports': 'ports',
  'cold-storage': 'coldStorage',
  'ports-with-landing': 'portsWithLanding',
  'bottom-temperature': 'bottomTemperature',
  'state-boundaries': 'stateBoundaries',
  'lek': 'lekHabitat',
  'trawl-survey': 'trawlSurvey',
  'vessel-tracking': 'aisVessels',
  'iattc': 'iattc',
  'iccat': 'iccat',
  'iphc': 'iphc',
  'wcpfc': 'wcpfc',
};

/** Reverse map: internal key -> slug (first slug wins for getActiveLayers). */
const INTERNAL_KEY_TO_SLUG = (() => {
  const out = {};
  for (const [slug, key] of Object.entries(SLUG_TO_INTERNAL_KEY)) {
    if (!(key in out)) out[key] = slug;
  }
  return out;
})();

class MapController {
  constructor(viewer, initialLayers = {}) {
    if (!viewer) {
      throw new Error('MapController requires a Cesium viewer instance');
    }
    
    this.viewer = viewer;
    this.ready = false;
    this.initializationPromise = null; // Promise that resolves when initialization completes
    
    // Track active layers
    this.activeLayers = new Set();
    
    // Track layers currently being added (prevents concurrent adds on high-latency environments)
    this.layersBeingAdded = new Set();
    
    // Track filters
    this.filters = {
      trawlSurveySeason: null,
      trawlSurveyYear: null,
      trawlSurveySpeciesFilter: null,
      trawlSurveyTimeOfDay: null,
      trawlSurveyMinDepth: null,
      trawlSurveyMaxDepth: null,
      trawlSurveyMinBotTemp: null,
      trawlSurveyMaxBotTemp: null,
      lekSpecies: null,
      lekSeason: null,
      lekMonthRange: null, // { start: number, end: number }
      bottomTemperature: false,
      simulationSession: null,
      playGroundfishSimulation: false,
      playSeaScallopSimulation: false,
    };
    
    // Store imagery providers for layers
    this.imageryProviders = {};
    
    // RFMO layers grouped under international fishery management
    this.rfmoLayers = ['iattc', 'iccat', 'iphc', 'wcpfc'];
    
    // Port entities tracking
    this.portsEntities = [];
    this.portsData = [];
    this.showSimulationPortsOnly = false;
    
    // Infrastructure layers data sources and entities
    this.coldStorageDataSource = null;
    this.coldStorageEntities = [];
    this.coldStorageData = [];
    
    this.portsWithLandingDataSource = null;
    this.portsWithLandingEntities = [];
    this.portsWithLandingData = [];
    
    this.bottomTemperatureDataSource = null;
    this.bottomTemperatureData = [];
    
    this.stateBoundariesDataSource = null;
    this.stateBoundariesEntities = [];
    this.stateBoundariesData = null; // Will store GeoJSON data
    
    // LEK habitat data layer
    this.lekHabitatDataSource = null;
    this.lekHabitatEntities = [];
    this.lekHabitatData = [];
    
    // Trawl survey data layer
    this.trawlSurveyDataSource = null;
    this.trawlSurveyEntities = [];
    this.trawlSurveyData = null; // Can be { filtered, unfiltered } or plain object

    // AIS vessel tracking data layer
    this.aisVesselsDataSource = null;
    this.aisVesselsEntities = [];
    this.aisVesselsData = [];
    this.aisDisplayOptions = {
      mapStyle: 'scatterPoints' // 'scatterPoints', 'trajectoryLines', 'heatMap', 'animatedTrajectory'
    };

    // AIS Animation state
    this.aisAnimationState = {
      isPlaying: false,
      currentFrame: 0,
      totalFrames: 0,
      animationSpeed: 1, // 1 = normal, 2 = 2x speed, etc.
      animationId: null,
      vesselPaths: {}, // Store processed paths per vessel
      currentTimestamp: null,
      startTimestamp: null,
      endTimestamp: null
    };

    // Callbacks for layer interactions
    this.onColdStorageClick = null;
    this.onPortsWithLandingClick = null;
    this.onTrawlSurveyStationClick = null;
    this.onAISVesselClick = null;
    this.onLekGridCellClick = null;
    this.onMapEmptyClick = null;
    
    // Click event handlers (one per layer type)
    this.clickHandlers = {
      coldStorage: null,
      portsWithLanding: null,
      bottomTemperature: null,
      trawlSurvey: null,
      aisVessels: null
    };
    
    // API functions for popups (e.g., getAtlanticState for ports)
    this.apiFunctions = {};
    
    // Global click handler for infrastructure layers
    this.globalClickHandler = null;
    
    // Store initial layers for later initialization
    this.pendingInitialLayers = initialLayers;
  }
  
  /**
   * Initialize the controller and set up initial layers
   */
  async initialize(initialLayers = {}) {
    if (!this.viewer || !window.Cesium) {
      console.warn('MapController: Viewer or Cesium not ready, will retry');
      return;
    }
    
    // Store the initialization promise so components can await it
    this.initializationPromise = this._doInitialize(initialLayers);
    return this.initializationPromise;
  }
  
  /**
   * Internal initialization method
   */
  async _doInitialize(initialLayers = {}) {
    try {
      // First, clear any existing layers from the viewer that might be from a previous instance
      // This is important when a new MapController instance is created but the viewer still has old layers
      // We need to search the viewer directly since this.activeLayers is empty for a new instance
      console.log('🧹 Clearing any existing layers from viewer before initializing new MapController instance');
      await this._clearAllLayersFromViewer();
      
      // Initialize imagery providers
      await this.initializeProviders();
      
      // Mark as ready AFTER providers are initialized but BEFORE initial layers
      // This allows addLayer() to work during initializeLayers()
      this.ready = true;
      
      // Set up global click handler for infrastructure layers
      this.setupGlobalClickHandler();
      
      // Process initial layers
      if (Object.keys(initialLayers).length > 0) {
        await this.initializeLayers(initialLayers);
      }
      
      console.log('✅ MapController initialized');

      // Notify listeners that the controller is ready (e.g. PanelLayout can apply initial layers)
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('mapControllerReady', { detail: {} }));
      }
    } catch (error) {
      console.error('❌ MapController initialization failed:', error);
      throw error;
    }
  }
  
  /**
   * Get the initialization promise - resolves when initialization is complete
   */
  getInitializationPromise() {
    return this.initializationPromise || Promise.resolve();
  }

  /**
   * Resolve kebab-case slug to internal layer key. Returns null if unknown.
   */
  _getInternalKey(slug) {
    return SLUG_TO_INTERNAL_KEY[slug] ?? null;
  }

  /**
   * Notify subscribers that a layer was added or removed (so UI can load data without polling).
   */
  _emitLayerChanged(slug, active) {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('mapControllerLayerChanged', {
        detail: { slug, active }
      }));
    }
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  /**
   * Normalize longitude to [-180, 180] range
   */
  normalizeLongitude(lon) {
    while (lon > 180) lon -= 360;
    while (lon < -180) lon += 360;
    return lon;
  }
  
  /**
   * Calculate centroid of a polygon from its coordinates
   * Handles both Polygon and MultiPolygon geometries
   */
  calculatePolygonCentroid(coordinates) {
    if (!coordinates || !Array.isArray(coordinates)) return null;
    
    let allCoords = [];
    
    // Check if this is a MultiPolygon (nested array of polygons)
    if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0]) && Array.isArray(coordinates[0][0][0])) {
      // MultiPolygon: coordinates is array of polygons, each polygon is array of rings
      const normalizedCoords = [];
      
      for (const polygon of coordinates) {
        if (Array.isArray(polygon) && polygon.length > 0) {
          const ring = polygon[0];
          if (Array.isArray(ring)) {
            for (const coord of ring) {
              if (Array.isArray(coord) && coord.length >= 2 && typeof coord[0] === 'number') {
                const normalizedLon = this.normalizeLongitude(coord[0]);
                normalizedCoords.push([normalizedLon, coord[1]]);
                allCoords.push(coord);
              }
            }
          }
        }
      }
      
      if (normalizedCoords.length === 0) return null;
      
      const lons = normalizedCoords.map(c => c[0]);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      const spansDateline = (maxLon - minLon) > 180;
      
      if (spansDateline) {
        const shiftedCoords = normalizedCoords.map(coord => {
          const shiftedLon = coord[0] < 0 ? coord[0] + 360 : coord[0];
          return [shiftedLon, coord[1]];
        });
        
        let totalLon = 0;
        let totalLat = 0;
        for (const coord of shiftedCoords) {
          totalLon += coord[0];
          totalLat += coord[1];
        }
        
        const avgLon = totalLon / shiftedCoords.length;
        const finalLon = avgLon > 180 ? avgLon - 360 : avgLon;
        
        return {
          longitude: finalLon,
          latitude: totalLat / shiftedCoords.length
        };
      } else {
        const lats = normalizedCoords.map(c => c[1]);
        return {
          longitude: (minLon + maxLon) / 2,
          latitude: (Math.min(...lats) + Math.max(...lats)) / 2
        };
      }
    } else {
      // Regular Polygon: coordinates is array of rings
      const outerRing = coordinates[0];
      if (Array.isArray(outerRing)) {
        for (const coord of outerRing) {
          if (Array.isArray(coord) && coord.length >= 2 && typeof coord[0] === 'number') {
            allCoords.push(coord);
          }
        }
      }
      
      if (allCoords.length === 0) return null;
      
      let totalLon = 0;
      let totalLat = 0;
      let count = allCoords.length;
      
      for (const coord of allCoords) {
        totalLon += coord[0];
        totalLat += coord[1];
      }
      
      return {
        longitude: this.normalizeLongitude(totalLon / count),
        latitude: totalLat / count
      };
    }
  }
  
  /**
   * Simplify polygon ring coordinates by removing points that are too close together
   */
  simplifyPolygonRing(ring, minDistanceDegrees = 0.0001, maxPoints = null) {
    if (!Array.isArray(ring) || ring.length < 4) return ring;
    
    const originalLength = ring.length;
    let threshold = 0.0001;
    
    if (originalLength > 50000) {
      threshold = 0.0005;
    }
    
    let simplified = [ring[0]];
    
    for (let i = 1; i < ring.length - 1; i++) {
      const prev = simplified[simplified.length - 1];
      const curr = ring[i];
      
      const latDiff = Math.abs(curr[1] - prev[1]);
      const lonDiff = Math.abs(curr[0] - prev[0]);
      const adjustedLonDiff = Math.min(lonDiff, 360 - lonDiff);
      const distance = Math.sqrt(latDiff * latDiff + adjustedLonDiff * adjustedLonDiff);
      
      if (distance >= threshold) {
        simplified.push(curr);
      }
    }
    
    simplified.push(ring[ring.length - 1]);
    
    if (maxPoints && originalLength > 100000 && simplified.length > maxPoints) {
      const sampled = [simplified[0]];
      const step = (simplified.length - 2) / (maxPoints - 2);
      for (let i = 1; i < maxPoints - 1; i++) {
        const idx = Math.round(1 + i * step);
        if (idx < simplified.length - 1) {
          sampled.push(simplified[idx]);
        }
      }
      sampled.push(simplified[simplified.length - 1]);
      simplified = sampled;
    }
    
    if (simplified.length < 4) {
      return ring;
    }
    
    return simplified;
  }
  
  /**
   * Validate and clean GeoJSON geometries
   */
  cleanGeoJson(geojsonData, simplify = false) {
    if (!geojsonData || !geojsonData.features) {
      return geojsonData;
    }

    if (geojsonData.crs) {
      console.log('⚠️ Removing CRS property from GeoJSON:', geojsonData.crs);
      delete geojsonData.crs;
    }

    const cleanedFeatures = geojsonData.features.map(feature => {
      if (!feature.geometry || !feature.geometry.coordinates) {
        return null;
      }

      if (feature.geometry.type === 'Polygon') {
        const coordinates = feature.geometry.coordinates;
        if (!Array.isArray(coordinates) || coordinates.length === 0) {
          return null;
        }

        let ring = coordinates[0];
        if (!Array.isArray(ring) || ring.length < 4) {
          return null;
        }

        const hasValidCoords = ring.every(coord => 
          Array.isArray(coord) && 
          coord.length >= 2 && 
          typeof coord[0] === 'number' && 
          typeof coord[1] === 'number' &&
          !isNaN(coord[0]) && 
          !isNaN(coord[1]) &&
          isFinite(coord[0]) && 
          isFinite(coord[1])
        );

        if (!hasValidCoords) {
          return null;
        }

        if (simplify) {
          ring = this.simplifyPolygonRing(ring, 0.0001, 10000);
        }

        const holes = coordinates.slice(1).map(hole => {
          if (simplify && Array.isArray(hole) && hole.length >= 4) {
            return this.simplifyPolygonRing(hole, 0.0001, 10000);
          }
          return hole;
        });

        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: [ring, ...holes]
          }
        };
      } else if (feature.geometry.type === 'MultiPolygon') {
        const coordinates = feature.geometry.coordinates;
        if (!Array.isArray(coordinates) || coordinates.length === 0) {
          return null;
        }
        
        const processedPolygons = coordinates.map(polygon => {
          if (!Array.isArray(polygon) || polygon.length === 0) return null;
          let ring = polygon[0];
          if (!Array.isArray(ring) || ring.length < 4) return null;
          
          if (simplify) {
            ring = this.simplifyPolygonRing(ring, 0.0001, 10000);
          }
          
          const holes = polygon.slice(1).map(hole => {
            if (simplify && Array.isArray(hole) && hole.length >= 4) {
              return this.simplifyPolygonRing(hole, 0.0001, 10000);
            }
            return hole;
          });
          
          return [ring, ...holes];
        }).filter(p => p !== null);
        
        if (processedPolygons.length === 0) {
          return null;
        }
        
        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: processedPolygons
          }
        };
      }

      return feature;
    }).filter(feature => feature !== null);

    return {
      ...geojsonData,
      features: cleanedFeatures
    };
  }
  
  /**
   * Simplify ring coordinates for polylines
   */
  simplifyRingForPolyline(ring, maxPoints = null) {
    if (!Array.isArray(ring) || ring.length === 0) return ring;
    
    const simplified = this.simplifyPolygonRing(ring, 0.0001, maxPoints);
    
    if (maxPoints && simplified.length > maxPoints && ring.length > 10000) {
      const step = simplified.length / maxPoints;
      const sampled = [simplified[0]];
      for (let i = 1; i < maxPoints - 1; i++) {
        const idx = Math.round(i * step);
        if (idx < simplified.length - 1) {
          sampled.push(simplified[idx]);
        }
      }
      if (simplified.length > 0) {
        sampled.push(simplified[simplified.length - 1]);
      }
      return sampled.length >= 4 ? sampled : simplified;
    }
    
    return simplified;
  }
  
  /**
   * Create polyline outlines from geometry
   */
  createPolylineOutlines(geometry, provider, layerPrefix, index, color, arcType = null, simplify = true) {
    if (!geometry) return;
    
    const coords = geometry.coordinates;
    let rings = [];
    
    if (geometry.type === 'Polygon') {
      rings = [coords[0]];
    } else if (geometry.type === 'MultiPolygon') {
      rings = coords.map(polygon => polygon[0]);
    }
    
    rings.forEach((ring, ringIndex) => {
      if (Array.isArray(ring) && ring.length > 0) {
        const maxPoints = ring.length > 10000 ? 5000 : null;
        const ringToUse = simplify ? this.simplifyRingForPolyline(ring, maxPoints) : ring;
        const positions = ringToUse.map(coord => 
          window.Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
        );
        positions.push(positions[0]);
        
        const polylineConfig = {
          id: `${layerPrefix}-outline-${index}-${ringIndex}-${Date.now()}`,
          polyline: {
            positions: positions,
            width: 2,
            material: color,
            clampToGround: true
          }
        };
        
        if (arcType) {
          polylineConfig.polyline.arcType = arcType;
          if (arcType === window.Cesium.ArcType.RHUMB_LINE) {
            polylineConfig.polyline.granularity = window.Cesium.Math.RADIANS_PER_DEGREE * 0.1;
          }
        }
        
        provider.entities.add(polylineConfig);
      }
    });
  }
  
  /**
   * Create label at centroid
   */
  createLabelAtCentroid(centroid, provider, layerPrefix, index, entityName, color) {
    if (!centroid) return;
    
    provider.entities.add({
      id: `${layerPrefix}-label-${index}-${Date.now()}`,
      position: window.Cesium.Cartesian3.fromDegrees(centroid.longitude, centroid.latitude),
      label: {
        text: entityName,
        font: '14pt sans-serif',
        fillColor: color,
        outlineWidth: 0,
        style: window.Cesium.LabelStyle.FILL,
        horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: window.Cesium.VerticalOrigin.CENTER,
        pixelOffset: new window.Cesium.Cartesian2(0, 0),
        scale: 1.0,
        showBackground: true,
        backgroundColor: color.withAlpha(0.2),
        backgroundPadding: new window.Cesium.Cartesian2(8, 4),
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      }
    });
  }
  
  /**
   * Process RFMO polygon entities and replace with polylines
   */
  processRFMOPolygon(entity, index, originalFeature, provider, layerPrefix, defaultName, color, arcType = null) {
    if (!originalFeature) {
      console.warn(`⚠️ No matching feature for ${layerPrefix.toUpperCase()} entity ${index}`);
      provider.entities.remove(entity);
      return;
    }
    
    const entityName = entity.properties?.name?.getValue() || defaultName;
    const centroid = this.calculatePolygonCentroid(originalFeature?.geometry?.coordinates);
    
    provider.entities.remove(entity);
    
    const geometry = originalFeature?.geometry;
    if (geometry) {
      this.createPolylineOutlines(geometry, provider, layerPrefix, index, color, arcType);
    }
    
    this.createLabelAtCentroid(centroid, provider, layerPrefix, index, entityName, color);
  }
  
  /**
   * Get property value from Cesium property object or plain object
   */
  getPropValue(propObj, key) {
    if (!propObj) return null;
    
    if (propObj[key] !== undefined && propObj[key] !== null) {
      const value = propObj[key];
      if (value && typeof value.getValue === 'function') {
        try {
          return value.getValue();
        } catch (e) {
          return value;
        }
      }
      return value;
    }
    
    if (propObj.getProperty && typeof propObj.getProperty === 'function') {
      try {
        const value = propObj.getProperty(key);
        if (value && typeof value.getValue === 'function') {
          try {
            return value.getValue();
          } catch (e) {
            return value;
          }
        }
        return value;
      } catch (e) {
        return null;
      }
    }
    
    return null;
  }
  
  /**
   * Initialize all imagery providers
   */
  async initializeProviders() {
    if (!this.viewer || !window.Cesium) {
      throw new Error('Viewer or Cesium not available');
    }

    console.log('🗺️ Initializing reference layer providers...');

    try {
      const providers = {};

      // 1. NOAA Basic Bathymetry WMS
      try {
        providers.bathymetry = new window.Cesium.WebMapServiceImageryProvider({
          url: 'https://gis.ngdc.noaa.gov/arcgis/services/DEM_mosaics/DEM_all/ImageServer/WMSServer',
          layers: '0',
          parameters: {
            transparent: true,
            format: 'image/png',
            styles: 'default'
          },
          getFeatureInfoParameters: {
            feature_count: 1
          }
        });
        console.log('✅ NOAA Bathymetry provider created');
      } catch (error) {
        console.warn('⚠️ Failed to create NOAA Bathymetry provider:', error);
      }

      // 2. NOAA Electronic Navigational Charts
      try {
        providers.noaaCharts = new window.Cesium.UrlTemplateImageryProvider({
          url: '/noaa_13009_1_tiles/{z}/{x}/{reverseY}.png',
          minimumLevel: 5,
          maximumLevel: 11,
          rectangle: window.Cesium.Rectangle.fromDegrees(-71.23855060975119, 39.70829221155743, -63.91555549474022, 44.42443978803006),
          tilingScheme: new window.Cesium.WebMercatorTilingScheme(),
          credit: 'NOAA Navigational Charts',
          pickFeaturesUrl: undefined,
          enablePickFeatures: false
        });
        console.log('✅ NOAA Charts custom tile provider created');
      } catch (error) {
        console.warn('⚠️ Failed to create NOAA Charts provider:', error);
      }

      // 3. NOAA Fishing Areas and Statistical Zones  
      try {
        providers.fishingZones = new window.Cesium.WebMapServiceImageryProvider({
          url: 'https://gis.fisheries.noaa.gov/arcgis/services/FishStats/Statistical_Areas_2010/MapServer/WMSServer',
          layers: '0',
          parameters: {
            transparent: true,
            format: 'image/png',
            styles: 'default'
          }
        });
        console.log('✅ Fishing Zones provider created');
      } catch (error) {
        console.warn('⚠️ Failed to create Fishing Zones provider:', error);
      }

      // 4. Rocky Bottom Type GeoTIFF
      try {
        providers.rockyBottom = new window.Cesium.SingleTileImageryProvider({
          url: '/rocky_bottom_type.png',
          rectangle: window.Cesium.Rectangle.fromDegrees(-74.52708334388409, 39.68979977412271, -60.78125000859998, 46.61053412004796),
          tileWidth: 256,
          tileHeight: 256,
          credit: 'Rocky Bottom Type Data'
        });
        console.log('✅ Rocky Bottom Type provider created');
      } catch (error) {
        console.error('❌ Failed to create Rocky Bottom Type provider:', error);
        providers.rockyBottom = null;
      }

      // 5. NOAA Fishing Closed Areas GeoJSON
      try {
        providers.fishingClosedAreas = new window.Cesium.GeoJsonDataSource();
        await providers.fishingClosedAreas.load('/noaa_fishing_closed_areas.geojson');
        console.log('✅ NOAA Fishing Closed Areas loaded successfully');
        
        const entities = providers.fishingClosedAreas.entities.values;
        entities.forEach(entity => {
          if (entity.polygon) {
            const closureType = entity.properties?.closure_type?.getValue();
            if (closureType === 'Year-round') {
              entity.polygon.material = window.Cesium.Color.RED.withAlpha(0.3);
              entity.polygon.outline = true;
              entity.polygon.outlineColor = window.Cesium.Color.RED;
            } else if (closureType === 'Habitat Protection') {
              entity.polygon.material = window.Cesium.Color.YELLOW.withAlpha(0.3);
              entity.polygon.outline = true;
              entity.polygon.outlineColor = window.Cesium.Color.YELLOW;
            } else if (closureType === 'Cod Protection') {
              entity.polygon.material = window.Cesium.Color.ORANGE.withAlpha(0.3);
              entity.polygon.outline = true;
              entity.polygon.outlineColor = window.Cesium.Color.ORANGE;
            } else {
              entity.polygon.material = window.Cesium.Color.BLUE.withAlpha(0.3);
              entity.polygon.outline = true;
              entity.polygon.outlineColor = window.Cesium.Color.BLUE;
            }
            
            const name = entity.properties?.name?.getValue() || 'Unnamed Area';
            const description = entity.properties?.description?.getValue() || 'No description available';
            const areaSqNm = entity.properties?.area_sq_nm?.getValue();
            const fid = entity.properties?.fid?.getValue();
            
            entity.name = name;
            entity.description = `
              <div style="font-family: Arial, sans-serif; max-width: 300px;">
                <h3 style="margin: 0 0 10px 0; color: #2c3e50;">${name}</h3>
                <p style="margin: 5px 0;"><strong>Description:</strong> ${description}</p>
                <p style="margin: 5px 0;"><strong>Closure Type:</strong> ${closureType || 'Unknown'}</p>
                ${areaSqNm ? `<p style="margin: 5px 0;"><strong>Area:</strong> ${areaSqNm} sq nm</p>` : ''}
                ${fid ? `<p style="margin: 5px 0;"><strong>Feature ID:</strong> ${fid}</p>` : ''}
              </div>
            `;
            
            entity.properties = {
              name: name,
              description: description,
              closureType: closureType,
              areaSqNm: areaSqNm,
              fid: fid,
              dataType: 'fishingClosedArea'
            };
          }
        });
        
        console.log('✅ NOAA Fishing Closed Areas provider created');
      } catch (error) {
        console.error('❌ Failed to create NOAA Fishing Closed Areas provider:', error);
        providers.fishingClosedAreas = null;
      }

      // 6. Scallop Areas GeoJSON (Georges Bank)
      try {
        providers.scallopAreas = new window.Cesium.GeoJsonDataSource();
        
        const response = await fetch('/georges_bank_sams.geojson');
        const geojsonData = await response.json();
        
        await providers.scallopAreas.load(geojsonData);
        console.log('✅ Georges Bank SAMS loaded successfully');
        
        // Dark purple color for scallop area borders (not black)
        const scallopBorderColor = window.Cesium.Color.fromCssColorString('#6B21A8'); // Deep violet
        const scallopFillColor = window.Cesium.Color.fromCssColorString('#7C3AED').withAlpha(0.15); // Very subtle fill

        const entities = providers.scallopAreas.entities.values;
        entities.forEach((entity, index) => {
          if (entity.polygon) {
            entity.polygon.material = scallopFillColor;
            entity.polygon.outline = true;
            entity.polygon.outlineColor = scallopBorderColor;
            entity.polygon.outlineWidth = 1.5;
            
            const name = entity.properties?.name?.getValue() || 'Unnamed Scallop Area';
            const areaKm2 = entity.properties?.area_km2?.getValue();
            const id = entity.properties?.id?.getValue();
            
            entity.name = name;
            entity.description = `
              <div style="font-family: Arial, sans-serif; max-width: 300px;">
                <h3 style="margin: 0 0 10px 0; color: #2c3e50;">${name}</h3>
                <p style="margin: 5px 0;"><strong>Region:</strong> Georges Bank</p>
                <p style="margin: 5px 0;"><strong>Type:</strong> Scallop Access Management Area</p>
                ${areaKm2 ? `<p style="margin: 5px 0;"><strong>Area:</strong> ${areaKm2.toLocaleString()} km²</p>` : ''}
                ${id !== undefined ? `<p style="margin: 5px 0;"><strong>Area ID:</strong> ${id}</p>` : ''}
              </div>
            `;
            
            entity.properties = {
              name: name,
              region: 'Georges Bank',
              areaKm2: areaKm2,
              id: id,
              dataType: 'scallopArea'
            };
            
            if (name && name !== 'Unnamed Scallop Area') {
              const originalFeature = geojsonData.features[index];
              if (originalFeature && originalFeature.geometry && originalFeature.geometry.coordinates) {
                const coordinates = originalFeature.geometry.coordinates[0];
                if (coordinates && coordinates.length > 0) {
                  let totalLat = 0, totalLon = 0, count = 0;
                  coordinates.forEach(coord => {
                    totalLon += coord[0];
                    totalLat += coord[1];
                    count++;
                  });
                  
                  if (count > 0) {
                    const centroidLon = totalLon / count;
                    const centroidLat = totalLat / count;
                    
                    providers.scallopAreas.entities.add({
                      id: `scallop-label-georges-${name}-${id}`,
                      position: window.Cesium.Cartesian3.fromDegrees(centroidLon, centroidLat),
                      label: {
                        text: name,
                        font: 'bold 14px Arial, Helvetica, sans-serif',
                        fillColor: window.Cesium.Color.WHITE,
                        outlineColor: window.Cesium.Color.fromCssColorString('#4C1D95'),
                        outlineWidth: 2,
                        style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
                        pixelOffset: new window.Cesium.Cartesian2(0, 0),
                        horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
                        verticalOrigin: window.Cesium.VerticalOrigin.CENTER,
                        showBackground: true,
                        backgroundColor: window.Cesium.Color.fromCssColorString('#7C3AED').withAlpha(0.9),
                        backgroundPadding: new window.Cesium.Cartesian2(8, 5),
                        disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        scaleByDistance: undefined,
                        translucencyByDistance: undefined
                      }
                    });
                  }
                }
              }
            }
          }
        });

        console.log('✅ Scallop Areas provider created');
      } catch (error) {
        console.error('❌ Failed to create Scallop Areas provider:', error);
        providers.scallopAreas = null;
      }

      // 7. Mid-Atlantic Scallop Areas GeoJSON
      try {
        providers.midAtlanticScallopAreas = new window.Cesium.GeoJsonDataSource();

        const response = await fetch('/mid_atlantic_sams.geojson');
        const geojsonData = await response.json();

        await providers.midAtlanticScallopAreas.load(geojsonData);
        console.log('✅ Mid-Atlantic SAMS loaded successfully');

        // Dark purple color for scallop area borders (not black)
        const midAtlanticBorderColor = window.Cesium.Color.fromCssColorString('#6B21A8'); // Deep violet
        const midAtlanticFillColor = window.Cesium.Color.fromCssColorString('#7C3AED').withAlpha(0.15); // Very subtle fill

        const entities = providers.midAtlanticScallopAreas.entities.values;
        entities.forEach((entity, index) => {
          if (entity.polygon) {
            entity.polygon.material = midAtlanticFillColor;
            entity.polygon.outline = true;
            entity.polygon.outlineColor = midAtlanticBorderColor;
            entity.polygon.outlineWidth = 1.5;
            
            const name = entity.properties?.name?.getValue() || 'Unnamed Scallop Area';
            const areaKm2 = entity.properties?.area_km2?.getValue();
            const id = entity.properties?.id?.getValue();
            
            entity.name = name;
            entity.description = `
              <div style="font-family: Arial, sans-serif; max-width: 300px;">
                <h3 style="margin: 0 0 10px 0; color: #2c3e50;">${name}</h3>
                <p style="margin: 5px 0;"><strong>Region:</strong> Mid-Atlantic</p>
                <p style="margin: 5px 0;"><strong>Type:</strong> Scallop Access Management Area</p>
                ${areaKm2 ? `<p style="margin: 5px 0;"><strong>Area:</strong> ${areaKm2.toLocaleString()} km²</p>` : ''}
                ${id !== undefined ? `<p style="margin: 5px 0;"><strong>Area ID:</strong> ${id}</p>` : ''}
              </div>
            `;
            
            entity.properties = {
              name: name,
              region: 'Mid-Atlantic',
              areaKm2: areaKm2,
              id: id,
              dataType: 'scallopArea'
            };
            
            if (name && name !== 'Unnamed Scallop Area') {
              const originalFeature = geojsonData.features[index];
              if (originalFeature && originalFeature.geometry && originalFeature.geometry.coordinates) {
                const coordinates = originalFeature.geometry.coordinates[0];
                if (coordinates && coordinates.length > 0) {
                  let totalLat = 0, totalLon = 0, count = 0;
                  coordinates.forEach(coord => {
                    totalLon += coord[0];
                    totalLat += coord[1];
                    count++;
                  });
                  
                  if (count > 0) {
                    const centroidLon = totalLon / count;
                    const centroidLat = totalLat / count;
                    
                    providers.midAtlanticScallopAreas.entities.add({
                      id: `scallop-label-midatlantic-${name}-${id}`,
                      position: window.Cesium.Cartesian3.fromDegrees(centroidLon, centroidLat),
                      label: {
                        text: name,
                        font: 'bold 14px Arial, Helvetica, sans-serif',
                        fillColor: window.Cesium.Color.WHITE,
                        outlineColor: window.Cesium.Color.fromCssColorString('#4C1D95'),
                        outlineWidth: 2,
                        style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
                        pixelOffset: new window.Cesium.Cartesian2(0, 0),
                        horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
                        verticalOrigin: window.Cesium.VerticalOrigin.CENTER,
                        showBackground: true,
                        backgroundColor: window.Cesium.Color.fromCssColorString('#7C3AED').withAlpha(0.9),
                        backgroundPadding: new window.Cesium.Cartesian2(8, 5),
                        disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        scaleByDistance: undefined,
                        translucencyByDistance: undefined
                      }
                    });
                  }
                }
              }
            }
          }
        });
        
        console.log('✅ Mid-Atlantic Scallop Areas provider created');
      } catch (error) {
        console.error('❌ Failed to create Mid-Atlantic Scallop Areas provider:', error);
        providers.midAtlanticScallopAreas = null;
      }

      // 8. Lobster Management Areas GeoJSON
      try {
        providers.lobsterAreas = new window.Cesium.GeoJsonDataSource();
        
        const response = await fetch('/lobster_management_areas.geojson');
        const geojsonData = await response.json();
        
        await providers.lobsterAreas.load(geojsonData);
        console.log('✅ Lobster Management Areas loaded successfully');
        
        // Color palette for different polygons
        const colors = [
          window.Cesium.Color.fromCssColorString('#5C9EAD').withAlpha(0.7), // Soft teal
          window.Cesium.Color.fromCssColorString('#E8B4B8').withAlpha(0.7), // Dusty rose
          window.Cesium.Color.fromCssColorString('#A8DADC').withAlpha(0.7), // Light blue
          window.Cesium.Color.fromCssColorString('#F1FAEE').withAlpha(0.7), // Off white
          window.Cesium.Color.fromCssColorString('#E63946').withAlpha(0.7), // Red accent
          window.Cesium.Color.fromCssColorString('#457B9D').withAlpha(0.7), // Deep blue
          window.Cesium.Color.fromCssColorString('#FFB4A2').withAlpha(0.7), // Peach
          window.Cesium.Color.fromCssColorString('#B5838D').withAlpha(0.7), // Mauve
          window.Cesium.Color.fromCssColorString('#6D6875').withAlpha(0.7), // Dusty purple
          window.Cesium.Color.fromCssColorString('#95B8D1').withAlpha(0.7), // Powder blue
          window.Cesium.Color.fromCssColorString('#D4A5A5').withAlpha(0.7), // Dusty pink
          window.Cesium.Color.fromCssColorString('#9CA986').withAlpha(0.7)  // Sage green
        ];
        
        // Get entities as an array to avoid issues with collection modification during iteration
        const entities = Array.from(providers.lobsterAreas.entities.values);
        entities.forEach((entity, index) => {
          if (entity.polygon) {
            // Use different color for each polygon
            const colorIndex = index % colors.length;
            const color = colors[colorIndex];
            
            entity.polygon.material = color.withAlpha(0.3);
            entity.polygon.outline = true;
            entity.polygon.outlineColor = color;
            entity.polygon.outlineWidth = 2;
            
            const commName = entity.properties?.COMMNAME?.getValue() || 'Unnamed Lobster Area';
            const areaName = entity.properties?.AREANAME?.getValue() || '';
            const garfoId = entity.properties?.GARFO_ID?.getValue() || '';
            
            entity.name = commName;
            entity.description = `
              <div style="font-family: Arial, sans-serif; max-width: 300px;">
                <h3 style="margin: 0 0 10px 0; color: #2c3e50;">${commName}</h3>
                ${areaName ? `<p style="margin: 5px 0;"><strong>Area Name:</strong> ${areaName}</p>` : ''}
                ${garfoId ? `<p style="margin: 5px 0;"><strong>GARFO ID:</strong> ${garfoId}</p>` : ''}
                <p style="margin: 5px 0;"><strong>Type:</strong> Lobster Management Area</p>
              </div>
            `;
            
            entity.properties = {
              name: commName,
              areaName: areaName,
              garfoId: garfoId,
              dataType: 'lobsterArea'
            };
            
            // Add label with COMMNAME
            if (commName && commName !== 'Unnamed Lobster Area') {
              const originalFeature = geojsonData.features[index];
              if (originalFeature && originalFeature.geometry && originalFeature.geometry.coordinates) {
                // Handle MultiPolygon geometry
                let allCoordinates = [];
                if (originalFeature.geometry.type === 'MultiPolygon') {
                  originalFeature.geometry.coordinates.forEach(polygon => {
                    if (polygon && polygon[0]) {
                      allCoordinates = allCoordinates.concat(polygon[0]);
                    }
                  });
                } else if (originalFeature.geometry.type === 'Polygon') {
                  allCoordinates = originalFeature.geometry.coordinates[0] || [];
                }
                
                if (allCoordinates.length > 0) {
                  let totalLat = 0, totalLon = 0, count = 0;
                  allCoordinates.forEach(coord => {
                    if (coord && coord.length >= 2) {
                      totalLon += coord[0];
                      totalLat += coord[1];
                      count++;
                    }
                  });
                  
                  if (count > 0) {
                    const centroidLon = totalLon / count;
                    const centroidLat = totalLat / count;
                    
                    // Create a unique ID using the feature index (which is unique in the GeoJSON)
                    // Also include GARFO_ID and sanitize COMMNAME to avoid special characters
                    const sanitizedCommName = commName.replace(/[^a-zA-Z0-9]/g, '-');
                    const uniqueId = `lobster-label-${index}-${garfoId || 'no-id'}-${sanitizedCommName}`;
                    const labelText = commName.replace(/Lobster Management Area\s*/i, 'Area ');

                    
                    // Check if entity with this ID already exists before adding
                    const existingEntity = providers.lobsterAreas.entities.getById(uniqueId);
                    if (!existingEntity) {
                      providers.lobsterAreas.entities.add({
                        id: uniqueId,
                        position: window.Cesium.Cartesian3.fromDegrees(centroidLon, centroidLat),
                        label: {
                          text: labelText,
                          font: '12pt sans-serif',
                          fillColor: window.Cesium.Color.BLACK,
                          outlineColor: window.Cesium.Color.BLACK,
                          outlineWidth: 2,
                          style: window.Cesium.LabelStyle.FILL,
                          pixelOffset: new window.Cesium.Cartesian2(0, 0),
                          horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
                          verticalOrigin: window.Cesium.VerticalOrigin.CENTER,
                          scale: 0.8,
                          showBackground: false, // ← Set to false
                          disableDepthTestDistance: Number.POSITIVE_INFINITY,
                          heightReference: window.Cesium.HeightReference.RELATIVE_TO_GROUND // ← Add this

                        }
                      });
                    } else {
                      console.warn(`⚠️ Label entity with ID ${uniqueId} already exists, skipping duplicate`);
                    }
                  }
                }
              }
            }
          }
        });
        
        console.log('✅ Lobster Management Areas provider created');
      } catch (error) {
        console.error('❌ Failed to create Lobster Management Areas provider:', error);
        providers.lobsterAreas = null;
      }

      // 9-12. RFMO Layers (IATTC, ICCAT, IPHC, WCPFC) - Similar pattern
      // For brevity, I'll create a helper function to initialize RFMO layers
      const initializeRFMOLayer = async (layerName, fileName, color, arcType = null) => {
        try {
          providers[layerName] = new window.Cesium.GeoJsonDataSource();
          
          const response = await fetch(`/${fileName}`);
          const geojsonData = await response.json();
          
          const cleanedData = this.cleanGeoJson(geojsonData, true);
          
          await providers[layerName].load(cleanedData);
          console.log(`✅ ${layerName.toUpperCase()} loaded successfully`);
          
          const entities = Array.from(providers[layerName].entities.values);
          entities.forEach((entity, index) => {
            try {
              if (entity.polygon) {
                const originalFeature = cleanedData.features[index];
                this.processRFMOPolygon(
                  entity,
                  index,
                  originalFeature,
                  providers[layerName],
                  layerName,
                  layerName.toUpperCase(),
                  color,
                  arcType
                );
              }
            } catch (error) {
              console.warn(`⚠️ Error styling ${layerName} entity ${index}:`, error);
            }
          });
          
          console.log(`✅ ${layerName.toUpperCase()} provider created`);
        } catch (error) {
          console.error(`❌ Failed to create ${layerName} provider:`, error);
          providers[layerName] = null;
        }
      };

      // Initialize RFMO layers
      await initializeRFMOLayer('iattc', 'IATTC.geojson', window.Cesium.Color.CYAN);
      await initializeRFMOLayer('iccat', 'ICCAT.geojson', window.Cesium.Color.MAGENTA);
      await initializeRFMOLayer('iphc', 'IPHC.geojson', window.Cesium.Color.LIME, window.Cesium.ArcType.RHUMB_LINE);
      await initializeRFMOLayer('wcpfc', 'WCPFC.geojson', window.Cesium.Color.ORANGE);

      // 12. US Regional Fishery Management Councils
      try {
        providers.fisheriesManagementCouncil = new window.Cesium.GeoJsonDataSource();
        
        const response = await fetch('/fisheries_management_council_simplified.geojson');
        const geojsonData = await response.json();
        
        const { crs, ...dataWithoutCRS } = geojsonData;
        const geojsonCopy = JSON.parse(JSON.stringify(dataWithoutCRS));
        const cleanedData = this.cleanGeoJson(geojsonCopy, false);
        
        await providers.fisheriesManagementCouncil.load(cleanedData);
        console.log('✅ Fisheries Management Council loaded successfully');
        
        const entities = Array.from(providers.fisheriesManagementCouncil.entities.values);
        entities.forEach((entity, index) => {
          try {
            if (entity.polygon) {
              const originalFeature = cleanedData.features[index];
              const fishRegion = originalFeature?.properties?.FishRegion || 'US Regional Fishery Management Councils';
              const centroid = this.calculatePolygonCentroid(originalFeature?.geometry?.coordinates);
              
              providers.fisheriesManagementCouncil.entities.remove(entity);
              
              const geometry = originalFeature?.geometry;
              if (geometry) {
                this.createPolylineOutlines(
                  geometry,
                  providers.fisheriesManagementCouncil,
                  'fisheries-management-council',
                  index,
                  window.Cesium.Color.YELLOW,
                  window.Cesium.ArcType.RHUMB_LINE,
                  false
                );
              }
              
              this.createLabelAtCentroid(
                centroid,
                providers.fisheriesManagementCouncil,
                'fisheries-management-council',
                index,
                fishRegion,
                window.Cesium.Color.YELLOW
              );
            }
          } catch (error) {
            console.warn(`⚠️ Error styling Fisheries Management Council entity ${index}:`, error);
          }
        });
        
        console.log('✅ Fisheries Management Council provider created');
      } catch (error) {
        console.error('❌ Failed to create Fisheries Management Council provider:', error);
        providers.fisheriesManagementCouncil = null;
      }

      // 13. Congressional Districts
      try {
        providers.congressionalDistricts = new window.Cesium.GeoJsonDataSource();
        
        const response = await fetch('/NTAD_Congressional_Districts_1928759543695387395.geojson');
        const geojsonData = await response.json();
        
        const cleanedData = this.cleanGeoJson(geojsonData, true);
        
        await providers.congressionalDistricts.load(cleanedData);
        console.log('✅ Congressional Districts loaded successfully');
        
        const entities = Array.from(providers.congressionalDistricts.entities.values);
        entities.forEach((entity, index) => {
          try {
            if (entity.polygon) {
              const originalFeature = cleanedData.features[index];
              let districtName = originalFeature?.properties?.NAMELSAD || `District ${index + 1}`;
              
              const match = districtName.match(/Congressional District (\d+|\(at Large\))/i);
              if (match) {
                const captured = match[1];
                if (captured.toLowerCase() === '(at large)') {
                  districtName = 'At Large';
                } else {
                  districtName = captured;
                }
              }
              
              const centroid = this.calculatePolygonCentroid(originalFeature?.geometry?.coordinates);
              
              providers.congressionalDistricts.entities.remove(entity);
              
              const geometry = originalFeature?.geometry;
              if (geometry) {
                this.createPolylineOutlines(
                  geometry,
                  providers.congressionalDistricts,
                  'congressional-districts',
                  index,
                  window.Cesium.Color.CYAN
                );
              }
              
              if (centroid) {
                providers.congressionalDistricts.entities.add({
                  id: `congressional-districts-label-${index}-${Date.now()}`,
                  position: window.Cesium.Cartesian3.fromDegrees(centroid.longitude, centroid.latitude),
                  label: {
                    text: districtName,
                    font: '8pt sans-serif',
                    fillColor: window.Cesium.Color.CYAN,
                    outlineWidth: 0,
                    style: window.Cesium.LabelStyle.FILL,
                    horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
                    verticalOrigin: window.Cesium.VerticalOrigin.CENTER,
                    pixelOffset: new window.Cesium.Cartesian2(0, 0),
                    scale: 1.0,
                    showBackground: false,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY
                  }
                });
              }
            }
          } catch (error) {
            console.warn(`⚠️ Error styling Congressional District entity ${index}:`, error);
          }
        });
        
        console.log('✅ Congressional Districts provider created');
      } catch (error) {
        console.error('❌ Failed to create Congressional Districts provider:', error);
        providers.congressionalDistricts = null;
      }

      this.imageryProviders = providers;
      console.log('✅ Reference layer providers initialized:', Object.keys(providers));
    } catch (error) {
      console.error('❌ Error initializing imagery providers:', error);
      throw error;
    }
  }
  
  /**
   * Initialize layers from initialLayers configuration.
   * initialLayers is slug-keyed (kebab-case from layout API).
   */
  async initializeLayers(initialLayers) {
    console.log('🔄 STOCK_SWITCH_DEBUG: Initializing layers from config:', initialLayers);

    for (const [slug, defaultValues] of Object.entries(initialLayers)) {
      const internalKey = this._getInternalKey(slug);
      if (!internalKey) {
        console.log('MAP LAYER ERROR: No layer ID found for slug:', slug);
        continue;
      }
      if (defaultValues === false) {
        await this.removeLayer(slug);
        continue;
      }
      await this.addLayer(slug);
      if (defaultValues && typeof defaultValues === 'object' && !Array.isArray(defaultValues)) {
        await this.setLayerDefaultValues(slug, "default", defaultValues);
      }
    }

    // After processing all layers and filters, trigger re-renders for layers that need data
    // This ensures that if a layer was added before its filter was set, it will still render
    console.log('🔄 STOCK_SWITCH_DEBUG: Post-initialization check for layers needing data', {
      timestamp: new Date().toISOString(),
      activeLayers: Array.from(this.activeLayers),
      lekSpeciesFilter: this.filters.lekSpecies,
      lekSeasonFilter: this.filters.lekSeason,
      lekMonthRangeFilter: this.filters.lekMonthRange
    });
    
    // If lekHabitat is active and lekSpecies filter is set, try to render (component will fetch if needed)
    if (this.activeLayers.has('lekHabitat') && this.filters.lekSpecies) {
      console.log('🔄 STOCK_SWITCH_DEBUG: lekHabitat is active with lekSpecies filter, checking if data needs to be fetched');
      if (this.lekHabitatData.length === 0) {
        console.log('⏳ STOCK_SWITCH_DEBUG: No LEK habitat data, component should fetch based on filter');
        // The component (DataLayerTabs) should detect the filter change and fetch data
        // We can't force it here, but we've set the filter so it should trigger
      } else {
        console.log('✅ STOCK_SWITCH_DEBUG: LEK habitat data exists, rendering');
        await this.renderLekHabitatLayer();
      }
    }
    
    // If portsWithLanding is active, check if data needs to be loaded
    if (this.activeLayers.has('portsWithLanding')) {
      console.log('🔄 STOCK_SWITCH_DEBUG: portsWithLanding is active, checking if data needs to be fetched');
      if (this.portsWithLandingData.length === 0) {
        console.log('⏳ STOCK_SWITCH_DEBUG: No ports with landing data, component should fetch');
        // The component should detect the layer is active and fetch data
      } else {
        console.log('✅ STOCK_SWITCH_DEBUG: Ports with landing data exists, rendering');
        await this.renderPortsWithLandingLayer();
      }
    }

    window.dispatchEvent(new CustomEvent('mapControllerLayersInitialized', { detail: {} }));
  }

  /**
   * Maps layout API component_default_values keys to MapController filter keys.
   * Keyed by slug (kebab-case). Add entries when layout API uses different field names than our filters.
   */
  _getComponentDefaultToFilterMapping() {
    const lekMapping = {
      season: {
        filterKey: 'lekSeason',
        normalize: (v) => (v && typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v),
      },
      species_uuid: { filterKey: 'lekSpecies' },
      species: { filterKey: 'lekSpecies' }, // layout default uses "species" (id or uuid)
    };
    const trawlSurveyMapping = {
      season: {
        filterKey: 'trawlSurveySeason',
        normalize: (v) => (v != null && v !== '' ? String(v).toUpperCase() : v),
      },
      year: { filterKey: 'trawlSurveyYear' },
      species_uuid: { filterKey: 'trawlSurveySpeciesFilter' },
      filter_by_species: {
        filterKey: 'trawlSurveySpeciesFilter',
        normalize: (v) => (typeof v === 'string' && v ? v : null),
      },
      time_of_day: { filterKey: 'trawlSurveyTimeOfDay' },
      depth_min: { filterKey: 'trawlSurveyMinDepth' },
      depth_max: { filterKey: 'trawlSurveyMaxDepth' },
      bot_temp_min: { filterKey: 'trawlSurveyMinBotTemp' },
      bot_temp_max: { filterKey: 'trawlSurveyMaxBotTemp' },
    };
    return {
      'lek': lekMapping,
      'trawl-survey': trawlSurveyMapping,
    };
  }

  async setLayerDefaultValues(slug, key, value) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      for (const [k, v] of Object.entries(value)) {
        await this.setLayerDefaultValues(slug, k, v);
      }
      return;
    }
    // "visible" is layout metadata (enable layer), not a MapController filter - skip it
    if (key === 'visible') return;
    const mapping = this._getComponentDefaultToFilterMapping()[slug]?.[key];
    // Slugs without a mapping (e.g. vessel-tracking) manage their own state via props - skip
    if (!mapping) return;
    const filterKey = mapping.filterKey;
    const filterValue = mapping.normalize ? mapping.normalize(value) : value;
    this.setFilter(filterKey, filterValue);
    if (filterKey === 'lekSeason' && filterValue && filterValue !== 'None') {
      let monthStart, monthEnd;
      switch (String(filterValue)) {
        case 'Winter': monthStart = 12; monthEnd = 2; break;
        case 'Spring': monthStart = 3; monthEnd = 5; break;
        case 'Summer': monthStart = 6; monthEnd = 8; break;
        case 'Fall': monthStart = 9; monthEnd = 11; break;
        default: monthStart = 0; monthEnd = 0; break;
      }
      if (monthStart !== 0 || monthEnd !== 0) {
        this.setFilter('lekMonthRange', { start: monthStart, end: monthEnd });
      }
    }
  }
  

  async setDefaultValue(slug, defaultValues) {
  }

  /**
   * Add a layer to the map. Public API: pass kebab-case slug.
   */
  async addLayer(slug) {
    const layerId = this._getInternalKey(slug);
    if (!layerId) {
      console.warn('MapController.addLayer: unknown slug', slug);
      return;
    }
    if (!this.ready) {
      throw new Error('MapController not ready. Call initialize() first.');
    }

    // For GeoJSON layers, always remove any existing instances first to prevent duplicates
    const geojsonLayers = ['fishingClosedAreas', 'scallopAreas', 'lobsterAreas', 'iattc', 'iccat', 'iphc', 'wcpfc', 'fisheriesManagementCouncil', 'congressionalDistricts'];
    if (geojsonLayers.includes(layerId)) {
      console.log(`🧹 Removing any existing ${layerId} instances before adding...`);
      const wasActive = this.activeLayers.has(layerId);
      if (wasActive) {
        await this.removeLayer(slug);
      } else {
        await this.removeLayer(slug, true);
      }
      this.activeLayers.delete(layerId);
    }

    if (this.activeLayers.has(layerId)) {
      console.log(`Layer ${layerId} is already active`);
      return;
    }

    // Handle RFMO parent/child relationships
    if (layerId === 'internationalFisheryManagement') {
      for (const rfmoLayer of this.rfmoLayers) {
        await this.addLayer(rfmoLayer); // rfmoLayer is both slug and internal key
      }
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      return;
    }

    // Handle individual RFMO layers
    if (this.rfmoLayers.includes(layerId)) {
      if (this.imageryProviders[layerId]) {
        const isAlreadyAdded = this.viewer.dataSources.contains(this.imageryProviders[layerId]);
        if (!isAlreadyAdded) {
          this.viewer.dataSources.add(this.imageryProviders[layerId]);
        }
        this.imageryProviders[layerId].show = true;
      }
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      return;
    }
    
    // Handle enhanced bathymetry (managed by BathymetryManager component, not here)
    if (layerId === 'enhancedBathymetry') {
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      console.log(`🌊 Enhanced bathymetry enabled`);
      return;
    }
    
    // Handle ports (managed separately via entities)
    if (layerId === 'ports') {
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      // Ports visualization is handled by updatePortsVisualization method
      await this.updatePortsVisualization();
      return;
    }
    
    // Handle cold storage facilities
    if (layerId === 'coldStorage') {
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.coldStorageData.length > 0) {
        this.renderColdStorageLayer();
      }
      return;
    }
    
    // Handle ports with landing data
    if (layerId === 'portsWithLanding') {
      console.log('➕ Adding portsWithLanding layer. Data available:', this.portsWithLandingData?.length || 0);
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.portsWithLandingData && this.portsWithLandingData.length > 0) {
        console.log('✅ Data available, rendering immediately');
        await this.renderPortsWithLandingLayer();
      } else {
        console.log('⏳ No data yet, will render when data is set');
      }
      return;
    }
    
    // Handle bottom temperature
    if (layerId === 'bottomTemperature') {
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.bottomTemperatureData.length > 0) {
        this.renderBottomTemperatureLayer();
      }
      return;
    }
    
    // Handle state boundaries
    if (layerId === 'stateBoundaries') {
      console.log('➕ Adding stateBoundaries layer');
      
      // Search for and remove any duplicate state boundaries data sources first
      const dataSources = this.viewer.dataSources;
      for (let i = dataSources.length - 1; i >= 0; i--) {
        const ds = dataSources.get(i);
        
        // Skip if this is our tracked data source (we'll handle it separately)
        if (ds === this.stateBoundariesDataSource) {
          continue;
        }
        
        // Check if this data source contains state boundaries
        if (ds.constructor.name === 'CustomDataSource' && ds.entities && ds.entities.values.length > 0) {
          // Check a sample of entities for state boundary characteristics
          const sampleSize = Math.min(5, ds.entities.values.length);
          let matchCount = 0;
          
          for (let j = 0; j < sampleSize; j++) {
            const entity = ds.entities.values[j];
            const props = entity.properties;
            const dataType = this.getPropValue(props, 'dataType');
            const stateName = this.getPropValue(props, 'stateName');
            const entityId = entity.id || '';
            
            // State boundaries have specific characteristics
            if (dataType === 'stateBoundary' || dataType === 'stateLabel' || 
                !!stateName || entityId.includes('state-boundary')) {
              matchCount++;
            }
          }
          
          if (matchCount > 0) {
            console.log(`⚠️ Removing duplicate state boundaries data source before adding (found ${ds.entities.values.length} entities)`);
            this.viewer.dataSources.remove(ds);
          }
        }
      }
      
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.stateBoundariesData) {
        console.log('✅ State boundaries data available, rendering immediately');
        await this.renderStateBoundariesLayer();
      } else {
        console.log('⏳ No state boundaries data yet, fetching...');
        await this.loadStateBoundaries();
      }
      return;
    }
    
    // Handle LEK habitat data
    if (layerId === 'lekHabitat') {
      console.log('➕ Adding lekHabitat layer');
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.lekHabitatData.length > 0) {
        console.log('✅ LEK habitat data available, rendering immediately');
        await this.renderLekHabitatLayer();
      } else {
        console.log('⏳ No LEK habitat data yet, will fetch when filters are set');
        // Data will be fetched when filters (lekSpecies, lekMonthRange) are set
      }
      return;
    }
    
    // Handle trawl survey stations
    if (layerId === 'trawlSurvey') {
      console.log('➕ Adding trawlSurvey layer. Data available:', this.trawlSurveyData ? 'yes' : 'no');
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.trawlSurveyData) {
        console.log('✅ Trawl survey data available, rendering immediately');
        await this.renderTrawlSurveyLayer();
      } else {
        console.log('⏳ No trawl survey data yet, will render when data is set');
      }
      return;
    }

    // Handle AIS vessel tracking
    if (layerId === 'aisVessels') {
      console.log('➕ Adding aisVessels layer. Data available:', this.aisVesselsData?.length || 0);
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      if (this.aisVesselsData && this.aisVesselsData.length > 0) {
        console.log('✅ AIS data available, rendering immediately');
        await this.renderAISVesselsLayer();
      } else {
        console.log('⏳ No AIS data yet, will render when data is set');
      }
      return;
    }

    // Handle GeoJSON data source layers
    if (geojsonLayers.includes(layerId)) {
      // 🐚 SCALLOP_AREAS_DEBUG: Log entry point for scallop areas
      if (layerId === 'scallopAreas') {
        console.log('🐚 SCALLOP_AREAS_DEBUG: addLayer called for scallopAreas', {
          timestamp: new Date().toISOString(),
          activeLayersHas: this.activeLayers.has(layerId),
          layersBeingAddedHas: this.layersBeingAdded.has(layerId),
          providerExists: !!this.imageryProviders[layerId],
          midAtlanticProviderExists: !!this.imageryProviders.midAtlanticScallopAreas,
          stackTrace: new Error().stack.split('\n').slice(1, 4).join('\n')
        });
      }
      
      if (!this.imageryProviders[layerId]) {
        console.warn(`⚠️ Provider for ${layerId} not yet initialized`);
        if (layerId === 'scallopAreas') {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Provider not initialized, returning early');
        }
        return;
      }
      
      // Guard against concurrent addLayer calls - check if layer is already being added
      // This prevents race conditions on high-latency environments where addLayer
      // might be called multiple times before the first call completes
      if (this.layersBeingAdded.has(layerId)) {
        console.log(`⏸️ ${layerId} layer is currently being added - skipping concurrent add`);
        if (layerId === 'scallopAreas') {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Layer already being added, skipping concurrent add', {
            timestamp: new Date().toISOString(),
            activeLayersHas: this.activeLayers.has(layerId),
            georgesBankInViewer: this.viewer.dataSources.contains(this.imageryProviders.scallopAreas),
            midAtlanticInViewer: this.imageryProviders.midAtlanticScallopAreas ? this.viewer.dataSources.contains(this.imageryProviders.midAtlanticScallopAreas) : false
          });
        }
        return;
      }
      
      // Guard against concurrent addLayer calls - check activeLayers first
      if (this.activeLayers.has(layerId)) {
        console.log(`ℹ️ ${layerId} layer already in activeLayers, checking if data source is in viewer...`);
        const isAlreadyAdded = this.viewer.dataSources.contains(this.imageryProviders[layerId]);
        
        // For scallopAreas, also check Mid-Atlantic
        if (layerId === 'scallopAreas' && this.imageryProviders.midAtlanticScallopAreas) {
          const isMidAtlanticAdded = this.viewer.dataSources.contains(this.imageryProviders.midAtlanticScallopAreas);
          if (isAlreadyAdded && isMidAtlanticAdded) {
            console.log(`✅ ${layerId} layer already active and both data sources in viewer - skipping add`);
            if (layerId === 'scallopAreas') {
              console.log('🐚 SCALLOP_AREAS_DEBUG: Both data sources already in viewer, skipping add', {
                timestamp: new Date().toISOString(),
                georgesBankInViewer: isAlreadyAdded,
                midAtlanticInViewer: isMidAtlanticAdded
              });
            }
            return;
          }
          // If one is missing, continue to add it
          if (layerId === 'scallopAreas') {
            console.log('🐚 SCALLOP_AREAS_DEBUG: One or both data sources missing, will add', {
              timestamp: new Date().toISOString(),
              georgesBankInViewer: isAlreadyAdded,
              midAtlanticInViewer: isMidAtlanticAdded
            });
          }
        } else if (isAlreadyAdded) {
          console.log(`✅ ${layerId} layer already active and in viewer - skipping add`);
          return;
        }
        
        // If it's in activeLayers but not in viewer, something went wrong - continue to add it
        if (!isAlreadyAdded) {
          console.log(`⚠️ ${layerId} is in activeLayers but not in viewer - adding data source`);
        }
      }
      
      // Mark layer as being added to prevent concurrent adds
      this.layersBeingAdded.add(layerId);
      if (layerId === 'scallopAreas') {
        console.log('🐚 SCALLOP_AREAS_DEBUG: Marked layer as being added', {
          timestamp: new Date().toISOString(),
          layersBeingAddedSize: this.layersBeingAdded.size
        });
      }
      
      // Check if this specific provider is already added
      const isAlreadyAdded = this.viewer.dataSources.contains(this.imageryProviders[layerId]);
      
      // For scallopAreas, check if scallop area data sources already exist in viewer
      // This is important because a new MapController instance might have different provider references
      // but the data sources from a previous instance might still be in the viewer
      let scallopAreasAlreadyInViewer = false;
      let georgesBankInViewer = false;
      let midAtlanticInViewer = false;
      
      if (layerId === 'scallopAreas') {
        // Check by reference first
        georgesBankInViewer = this.viewer.dataSources.contains(this.imageryProviders.scallopAreas);
        if (this.imageryProviders.midAtlanticScallopAreas) {
          midAtlanticInViewer = this.viewer.dataSources.contains(this.imageryProviders.midAtlanticScallopAreas);
        }
        
        // Also check by characteristics in case we have a new MapController instance
        // but old data sources are still in the viewer
        if (!georgesBankInViewer || !midAtlanticInViewer) {
          const dataSources = this.viewer.dataSources;
          console.log('🐚 SCALLOP_AREAS_DEBUG: Checking by characteristics (new MapController instance scenario)', {
            timestamp: new Date().toISOString(),
            totalDataSources: dataSources.length,
            georgesBankInViewer,
            midAtlanticInViewer,
            trackedGeorgesBankRef: this.imageryProviders.scallopAreas,
            trackedMidAtlanticRef: this.imageryProviders.midAtlanticScallopAreas
          });
          
          // Log all data sources first to see what we're working with
          const allDataSourcesInfo = [];
          for (let idx = 0; idx < dataSources.length; idx++) {
            const ds = dataSources.get(idx);
            allDataSourcesInfo.push({
              index: idx,
              type: ds.constructor.name,
              hasEntities: !!(ds.entities && ds.entities.values),
              entityCount: ds.entities?.values?.length || 0,
              isTrackedGeorgesBank: ds === this.imageryProviders.scallopAreas,
              isTrackedMidAtlantic: ds === this.imageryProviders.midAtlanticScallopAreas
            });
          }
          console.log('🐚 SCALLOP_AREAS_DEBUG: All data sources in viewer', {
            timestamp: new Date().toISOString(),
            totalDataSources: dataSources.length,
            dataSourcesInfo: allDataSourcesInfo
          });
          
          for (let i = 0; i < dataSources.length; i++) {
            const ds = dataSources.get(i);
            
            // Skip our tracked providers (already checked by reference above)
            const isTrackedGeorgesBank = ds === this.imageryProviders.scallopAreas;
            const isTrackedMidAtlantic = ds === this.imageryProviders.midAtlanticScallopAreas;
            
            if (isTrackedGeorgesBank || isTrackedMidAtlantic) {
              console.log('🐚 SCALLOP_AREAS_DEBUG: Skipping tracked provider in characteristics check', {
                timestamp: new Date().toISOString(),
                index: i,
                isTrackedGeorgesBank,
                isTrackedMidAtlantic,
                dsType: ds.constructor.name
              });
              continue;
            }
            
            console.log('🐚 SCALLOP_AREAS_DEBUG: Examining data source', {
              timestamp: new Date().toISOString(),
              index: i,
              dsType: ds.constructor.name,
              hasEntities: !!(ds.entities && ds.entities.values),
              entityCount: ds.entities?.values?.length || 0,
              isTrackedGeorgesBank,
              isTrackedMidAtlantic
            });
            
            // Check by entity characteristics instead of constructor name (minification-safe)
            // A GeoJSON data source will have entities with properties
            if (ds.entities && ds.entities.values && ds.entities.values.length > 0) {
              // Check multiple entities to be more thorough
              const sampleSize = Math.min(5, ds.entities.values.length);
              let foundScallopArea = false;
              let foundMidAtlantic = false;
              let foundGeorgesBank = false;
              
              const sampleData = [];
              
              for (let j = 0; j < sampleSize; j++) {
                const entity = ds.entities.values[j];
                const props = entity.properties;
                const entityRegion = this.getPropValue(props, 'region');
                const dataType = this.getPropValue(props, 'dataType');
                
                sampleData.push({
                  entityIndex: j,
                  hasProps: !!props,
                  entityRegion,
                  dataType,
                  regionLower: entityRegion?.toLowerCase(),
                  hasGeorgesBank: entityRegion?.toLowerCase().includes('georges bank'),
                  hasMidAtlantic: entityRegion?.toLowerCase().includes('mid-atlantic'),
                  isScallopArea: dataType === 'scallopArea'
                });
                
                if (dataType === 'scallopArea' || 
                    (entityRegion && (entityRegion.toLowerCase().includes('georges bank') || entityRegion.toLowerCase().includes('mid-atlantic')))) {
                  foundScallopArea = true;
                  if (entityRegion && entityRegion.toLowerCase().includes('mid-atlantic')) {
                    foundMidAtlantic = true;
                  } else if (entityRegion && entityRegion.toLowerCase().includes('georges bank')) {
                    foundGeorgesBank = true;
                  }
                }
              }
              
              console.log('🐚 SCALLOP_AREAS_DEBUG: Entity sample analysis', {
                timestamp: new Date().toISOString(),
                index: i,
                sampleSize,
                sampleData,
                foundScallopArea,
                foundMidAtlantic,
                foundGeorgesBank
              });
              
              if (foundScallopArea) {
                if (foundMidAtlantic) {
                  if (!midAtlanticInViewer) {
                    console.log('🐚 SCALLOP_AREAS_DEBUG: Found Mid-Atlantic by characteristics', {
                      timestamp: new Date().toISOString(),
                      entityCount: ds.entities.values.length,
                      sampleSize,
                      dsReference: ds
                    });
                  }
                  midAtlanticInViewer = true;
                }
                if (foundGeorgesBank) {
                  if (!georgesBankInViewer) {
                    console.log('🐚 SCALLOP_AREAS_DEBUG: Found Georges Bank by characteristics', {
                      timestamp: new Date().toISOString(),
                      entityCount: ds.entities.values.length,
                      sampleSize,
                      dsReference: ds
                    });
                  }
                  georgesBankInViewer = true;
                }
              } else {
                console.log('🐚 SCALLOP_AREAS_DEBUG: Data source does not match scallop area characteristics', {
                  timestamp: new Date().toISOString(),
                  index: i,
                  entityCount: ds.entities.values.length,
                  sampleData
                });
              }
            } else {
              console.log('🐚 SCALLOP_AREAS_DEBUG: Data source skipped (no entities)', {
                timestamp: new Date().toISOString(),
                index: i,
                dsType: ds.constructor.name,
                hasEntities: !!(ds.entities && ds.entities.values),
                entityCount: ds.entities?.values?.length || 0
              });
            }
          }
          
          console.log('🐚 SCALLOP_AREAS_DEBUG: After characteristics check', {
            timestamp: new Date().toISOString(),
            georgesBankInViewer,
            midAtlanticInViewer
          });
        }
        
        scallopAreasAlreadyInViewer = georgesBankInViewer && midAtlanticInViewer;
        
        console.log('🐚 SCALLOP_AREAS_DEBUG: Final check result', {
          timestamp: new Date().toISOString(),
          georgesBankInViewer,
          midAtlanticInViewer,
          scallopAreasAlreadyInViewer,
          totalDataSources: this.viewer.dataSources.length
        });
        
        if (scallopAreasAlreadyInViewer) {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Both scallop area data sources already in viewer (by reference or characteristics), skipping add', {
            timestamp: new Date().toISOString(),
            georgesBankInViewer,
            midAtlanticInViewer,
            totalDataSources: this.viewer.dataSources.length
          });
          this.activeLayers.add(layerId);
          this.layersBeingAdded.delete(layerId);
          this._emitLayerChanged(slug, true);
          return;
        } else {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Not all scallop areas in viewer, will proceed to add', {
            timestamp: new Date().toISOString(),
            georgesBankInViewer,
            midAtlanticInViewer,
            totalDataSources: this.viewer.dataSources.length
          });
        }
      }
      
      // Search for and remove any duplicate data sources for ALL GeoJSON layers
      // This prevents duplicates from accumulating - do this BEFORE checking if already added
      const dataSources = this.viewer.dataSources;
      let duplicatesRemoved = 0;
      
      if (layerId === 'scallopAreas') {
        console.log('🐚 SCALLOP_AREAS_DEBUG: Starting duplicate detection', {
          timestamp: new Date().toISOString(),
          totalDataSources: dataSources.length,
          georgesBankProviderExists: !!this.imageryProviders.scallopAreas,
          midAtlanticProviderExists: !!this.imageryProviders.midAtlanticScallopAreas,
          georgesBankAlreadyInViewer: isAlreadyAdded,
          midAtlanticAlreadyInViewer: layerId === 'scallopAreas' ? midAtlanticInViewer : false
        });
      }
      
      for (let i = dataSources.length - 1; i >= 0; i--) {
        const ds = dataSources.get(i);
        
        // Skip if this is our tracked provider (we'll handle it separately)
        if (ds === this.imageryProviders[layerId]) {
          if (layerId === 'scallopAreas') {
            console.log('🐚 SCALLOP_AREAS_DEBUG: Skipping tracked Georges Bank provider in duplicate check');
          }
          continue;
        }
        
        // Also skip Mid-Atlantic scallop areas if we're adding the main scallop areas layer
        if (layerId === 'scallopAreas' && ds === this.imageryProviders.midAtlanticScallopAreas) {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Skipping tracked Mid-Atlantic provider in duplicate check');
          continue;
        }
        
        // Check if this data source matches the layer we're trying to add
        // Use entity characteristics instead of constructor name (minification-safe)
        if (ds.entities && ds.entities.values && ds.entities.values.length > 0) {
          let isDuplicate = false;
          
          // Check based on layer type
          if (layerId === 'fishingClosedAreas') {
            const firstEntity = ds.entities.values[0];
            const props = firstEntity.properties;
            const closureType = this.getPropValue(props, 'closure_type') || this.getPropValue(props, 'CLOSURE_TYPE');
            const dataType = this.getPropValue(props, 'dataType');
            isDuplicate = !!closureType || dataType === 'fishingClosedArea' || dataType === 'Fishing_Closed_Area';
          } else if (layerId === 'fisheriesManagementCouncil') {
            const firstEntity = ds.entities.values[0];
            const entityId = firstEntity.id || '';
            const entityName = firstEntity.name || '';
            const props = firstEntity.properties;
            const councilName = this.getPropValue(props, 'COUNCIL');
            isDuplicate = entityId.includes('fisheries-management-council') ||
                         (entityName && (entityName.toLowerCase().includes('council') || entityName.toLowerCase().includes('fishery'))) ||
                         !!councilName;
          } else if (layerId === 'congressionalDistricts') {
            const sampleSize = Math.min(5, ds.entities.values.length);
            let matchCount = 0;
            for (let j = 0; j < sampleSize; j++) {
              const entity = ds.entities.values[j];
              const entityId = entity.id || '';
              if (entityId.includes('congressional-districts')) matchCount++;
              const cd118fp = this.getPropValue(entity.properties, 'CD118FP');
              if (cd118fp) matchCount++;
            }
            isDuplicate = matchCount > 0;
          } else if (layerId === 'scallopAreas') {
            const firstEntity = ds.entities.values[0];
            const props = firstEntity.properties;
            const entityRegion = this.getPropValue(props, 'region');
            const dataType = this.getPropValue(props, 'dataType');
            isDuplicate = dataType === 'scallopArea' || 
                         (entityRegion && (entityRegion.toLowerCase().includes('georges bank') || entityRegion.toLowerCase().includes('mid-atlantic')));
            
            if (isDuplicate) {
              console.log('🐚 SCALLOP_AREAS_DEBUG: Found duplicate scallop area data source', {
                timestamp: new Date().toISOString(),
                entityCount: ds.entities.values.length,
                dataType,
                entityRegion,
                isTrackedGeorgesBank: ds === this.imageryProviders.scallopAreas,
                isTrackedMidAtlantic: ds === this.imageryProviders.midAtlanticScallopAreas
              });
            }
          } else if (layerId === 'lobsterAreas') {
            const firstEntity = ds.entities.values[0];
            const props = firstEntity.properties;
            const dataType = this.getPropValue(props, 'dataType');
            isDuplicate = dataType === 'lobsterArea';
          } else if (['iattc', 'iccat', 'iphc', 'wcpfc'].includes(layerId)) {
            // RFMO layers - check by entity characteristics
            const firstEntity = ds.entities.values[0];
            const entityId = firstEntity.id || '';
            isDuplicate = entityId.toLowerCase().includes(layerId.toLowerCase());
          }
          
          if (isDuplicate) {
            console.log(`⚠️ Removing duplicate ${layerId} data source before adding (found ${ds.entities.values.length} entities)`);
            if (layerId === 'scallopAreas') {
              console.log('🐚 SCALLOP_AREAS_DEBUG: Removing duplicate before adding', {
                timestamp: new Date().toISOString(),
                entityCount: ds.entities.values.length,
                totalDataSourcesBefore: this.viewer.dataSources.length
              });
            }
            this.viewer.dataSources.remove(ds);
            duplicatesRemoved++;
            if (layerId === 'scallopAreas') {
              console.log('🐚 SCALLOP_AREAS_DEBUG: Duplicate removed', {
                timestamp: new Date().toISOString(),
                totalDataSourcesAfter: this.viewer.dataSources.length
              });
            }
          }
        }
      }
      
      if (duplicatesRemoved > 0) {
        console.log(`🧹 Removed ${duplicatesRemoved} duplicate ${layerId} data source(s)`);
        if (layerId === 'scallopAreas') {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Duplicate removal complete', {
            timestamp: new Date().toISOString(),
            duplicatesRemoved,
            totalDataSources: this.viewer.dataSources.length
          });
        }
      } else if (layerId === 'scallopAreas') {
        console.log('🐚 SCALLOP_AREAS_DEBUG: No duplicates found', {
          timestamp: new Date().toISOString(),
          totalDataSources: this.viewer.dataSources.length
        });
      }
      
      // Now add our provider if it's not already added
      if (!isAlreadyAdded) {
        try {
          this.viewer.dataSources.add(this.imageryProviders[layerId]);
          console.log(`✅ Added ${layerId} GeoJSON layer`);
          
          // For IPHC, hide entities that might cause rendering errors
          if (layerId === 'iphc') {
            const iphcEntities = this.imageryProviders[layerId].entities.values;
            iphcEntities.forEach((entity) => {
              if (entity.polygon) {
                entity.polygon.outline = false;
              }
            });
          }
        } catch (error) {
          console.error(`❌ Error adding ${layerId} layer:`, error);
          // Remove from layersBeingAdded on error
          this.layersBeingAdded.delete(layerId);
          throw error;
        }
      } else {
        console.log(`ℹ️ ${layerId} layer already in viewer, skipping add`);
        // Remove from layersBeingAdded since we're not actually adding
        this.layersBeingAdded.delete(layerId);
      }
      
      // Also add Mid-Atlantic scallop areas if this is the main scallop areas layer
      if (layerId === 'scallopAreas' && this.imageryProviders.midAtlanticScallopAreas) {
        const isMidAtlanticAdded = this.viewer.dataSources.contains(this.imageryProviders.midAtlanticScallopAreas);
        if (!isMidAtlanticAdded) {
          this.viewer.dataSources.add(this.imageryProviders.midAtlanticScallopAreas);
          console.log(`✅ Added Mid-Atlantic scallop areas`);
        }
      }
      
      this.activeLayers.add(layerId);
      // Remove from layersBeingAdded now that it's fully added
      this.layersBeingAdded.delete(layerId);
      this._emitLayerChanged(slug, true);
      return;
    }
    
    // Handle WMS imagery layers
    if (this.imageryProviders[layerId]) {
      const currentProvider = this.imageryProviders[layerId];
      const providerUrl = currentProvider?.url || currentProvider?._url;
      const layers = this.viewer.imageryLayers;
      let alreadyAdded = false;
      
      for (let i = 0; i < layers.length; i++) {
        const layer = layers.get(i);
        const layerProvider = layer.imageryProvider;
        const layerUrl = layerProvider?.url || layerProvider?._url;
        
        if (layerProvider === currentProvider || (providerUrl && layerUrl === providerUrl)) {
          alreadyAdded = true;
          console.log(`⚠️ ${layerId} layer already in viewer, skipping add`);
          break;
        }
      }
      
      if (!alreadyAdded) {
        const imageryLayer = this.viewer.imageryLayers.addImageryProvider(this.imageryProviders[layerId]);
        imageryLayer.alpha = 0.7;
        console.log(`✅ Added ${layerId} WMS layer with alpha: ${imageryLayer.alpha}`);
      }
      
      // Add to activeLayers
      this.activeLayers.add(layerId);
      this._emitLayerChanged(slug, true);
      return;
    }
    
    throw new Error(`No provider found for layer: ${layerId}`);
  }
  
  /**
   * Remove a layer from the map. Public API: pass kebab-case slug.
   * @param {string} slug - The layer slug (kebab-case)
   * @param {boolean} force - If true, remove even if not in activeLayers (useful for cleaning up duplicates)
   */
  async removeLayer(slug, force = false) {
    const layerId = this._getInternalKey(slug);
    if (!layerId) {
      console.warn('MapController.removeLayer: unknown slug', slug);
      return;
    }
    if (!force && !this.activeLayers.has(layerId)) {
      console.log(`Layer ${layerId} is not active`);
      return;
    }

    // Handle RFMO parent/child relationships
    if (layerId === 'internationalFisheryManagement') {
      for (const rfmoLayer of this.rfmoLayers) {
        await this.removeLayer(rfmoLayer);
      }
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle individual RFMO layers
    if (this.rfmoLayers.includes(layerId)) {
      if (this.imageryProviders[layerId]) {
        let removed = false;
        if (this.viewer.dataSources.contains(this.imageryProviders[layerId])) {
          this.viewer.dataSources.remove(this.imageryProviders[layerId]);
          removed = true;
        } else {
          // Search for matching data source
          const dataSources = this.viewer.dataSources;
          for (let i = dataSources.length - 1; i >= 0; i--) {
            const ds = dataSources.get(i);
            if (ds.constructor.name !== 'GeoJsonDataSource' || ds.entities.values.length === 0) {
              continue;
            }
            
            const sampleSize = Math.min(10, ds.entities.values.length);
            let matchCount = 0;
            
            for (let j = 0; j < sampleSize; j++) {
              const entity = ds.entities.values[j];
              const entityId = entity.id || '';
              if (entityId.includes(`${layerId}-outline`) || entityId.includes(`${layerId}-label`)) {
                matchCount++;
              }
              if (entity.polyline || entity.label) {
                if (entityId.includes(layerId)) {
                  matchCount++;
                }
              }
              const entityProps = entity.properties;
              const entName = entity.name || this.getPropValue(entityProps, 'name') || this.getPropValue(entityProps, 'NAME') || '';
              if (entName && entName.toUpperCase().includes(layerId.toUpperCase())) {
                matchCount++;
              }
            }
            
            const firstEntity = ds.entities.values[0];
            const props = firstEntity.properties;
            const hasClosureType = !!this.getPropValue(props, 'closure_type');
            const hasAreaKm2 = !!this.getPropValue(props, 'area_km2');
            const hasCd118fp = !!this.getPropValue(props, 'CD118FP');
            const hasCouncil = !!this.getPropValue(props, 'COUNCIL');
            
            if (matchCount > 0 && !hasClosureType && !hasAreaKm2 && !hasCd118fp && !hasCouncil) {
              this.viewer.dataSources.remove(ds);
              removed = true;
              break;
            }
          }
        }
        
        if (!removed) {
          console.warn(`⚠️ Could not find ${layerId} to remove`);
        }
      }
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle enhanced bathymetry
    if (layerId === 'enhancedBathymetry') {
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      console.log(`🌊 Enhanced bathymetry disabled`);
      return;
    }
    
    // Handle ports
    if (layerId === 'ports') {
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      await this.updatePortsVisualization();
      return;
    }
    
    // Handle cold storage facilities
    if (layerId === 'coldStorage') {
      this.removeColdStorageLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle ports with landing data
    if (layerId === 'portsWithLanding') {
      this.removePortsWithLandingLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle bottom temperature
    if (layerId === 'bottomTemperature') {
      this.removeBottomTemperatureLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle state boundaries
    if (layerId === 'stateBoundaries') {
      console.log('➖ Removing stateBoundaries layer');
      this.removeStateBoundariesLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle LEK habitat data
    if (layerId === 'lekHabitat') {
      console.log('➖ Removing lekHabitat layer');
      this.removeLekHabitatLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle trawl survey stations
    if (layerId === 'trawlSurvey') {
      console.log('➖ Removing trawlSurvey layer');
      this.removeTrawlSurveyLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }

    // Handle AIS vessel tracking
    if (layerId === 'aisVessels') {
      console.log('➖ Removing aisVessels layer');
      this.removeAISVesselsLayer();
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }

    // Handle GeoJSON data source layers
    const geojsonLayers = ['fishingClosedAreas', 'scallopAreas', 'lobsterAreas', 'fisheriesManagementCouncil', 'congressionalDistricts'];
    if (geojsonLayers.includes(layerId)) {
      // Always try to remove the data source, even if not in activeLayers (when force is true)
      let removed = false;
      let georgesBankRemoved = false;
      let midAtlanticRemoved = false;
      
      // For scallop areas, remove both Georges Bank and Mid-Atlantic
      if (layerId === 'scallopAreas') {
        console.log('🐚 SCALLOP_AREAS_DEBUG: removeLayer called for scallopAreas', {
          timestamp: new Date().toISOString(),
          force,
          activeLayersHas: this.activeLayers.has(layerId),
          totalDataSources: this.viewer.dataSources.length,
          georgesBankProviderExists: !!this.imageryProviders.scallopAreas,
          midAtlanticProviderExists: !!this.imageryProviders.midAtlanticScallopAreas
        });
        
        if (this.imageryProviders.scallopAreas && this.viewer.dataSources.contains(this.imageryProviders.scallopAreas)) {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Removing Georges Bank scallop areas', {
            timestamp: new Date().toISOString(),
            totalDataSourcesBefore: this.viewer.dataSources.length
          });
          this.viewer.dataSources.remove(this.imageryProviders.scallopAreas);
          georgesBankRemoved = true;
          console.log('🐚 SCALLOP_AREAS_DEBUG: Georges Bank removed', {
            timestamp: new Date().toISOString(),
            totalDataSourcesAfter: this.viewer.dataSources.length
          });
        } else {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Georges Bank not in viewer or provider missing', {
            timestamp: new Date().toISOString(),
            providerExists: !!this.imageryProviders.scallopAreas,
            inViewer: this.imageryProviders.scallopAreas ? this.viewer.dataSources.contains(this.imageryProviders.scallopAreas) : false
          });
        }
        
        if (this.imageryProviders.midAtlanticScallopAreas && this.viewer.dataSources.contains(this.imageryProviders.midAtlanticScallopAreas)) {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Removing Mid-Atlantic scallop areas', {
            timestamp: new Date().toISOString(),
            totalDataSourcesBefore: this.viewer.dataSources.length
          });
          this.viewer.dataSources.remove(this.imageryProviders.midAtlanticScallopAreas);
          midAtlanticRemoved = true;
          console.log('🐚 SCALLOP_AREAS_DEBUG: Mid-Atlantic removed', {
            timestamp: new Date().toISOString(),
            totalDataSourcesAfter: this.viewer.dataSources.length
          });
        } else {
          console.log('🐚 SCALLOP_AREAS_DEBUG: Mid-Atlantic not in viewer or provider missing', {
            timestamp: new Date().toISOString(),
            providerExists: !!this.imageryProviders.midAtlanticScallopAreas,
            inViewer: this.imageryProviders.midAtlanticScallopAreas ? this.viewer.dataSources.contains(this.imageryProviders.midAtlanticScallopAreas) : false
          });
        }
        
        removed = georgesBankRemoved && midAtlanticRemoved;
        
        console.log('🐚 SCALLOP_AREAS_DEBUG: Tracked providers removal complete', {
          timestamp: new Date().toISOString(),
          georgesBankRemoved,
          midAtlanticRemoved,
          removed,
          totalDataSources: this.viewer.dataSources.length
        });
        
        // Search for remaining scallop area data sources (including duplicates)
        // This is important for cleanup, especially if duplicates were created due to race conditions
        // NOTE: We check ALL data sources by characteristics, not just by reference, because
        // a new MapController instance will have different provider references even if the
        // data sources are already in the viewer from a previous instance
        const dataSources = this.viewer.dataSources;
        console.log('🐚 SCALLOP_AREAS_DEBUG: Searching for remaining scallop area data sources', {
          timestamp: new Date().toISOString(),
          totalDataSources: dataSources.length,
          georgesBankRemoved,
          midAtlanticRemoved,
          trackedGeorgesBankRef: this.imageryProviders.scallopAreas,
          trackedMidAtlanticRef: this.imageryProviders.midAtlanticScallopAreas
        });
        
        for (let i = dataSources.length - 1; i >= 0; i--) {
          const ds = dataSources.get(i);
          
          // Check if this is our tracked provider by reference first
          const isTrackedGeorgesBank = ds === this.imageryProviders.scallopAreas;
          const isTrackedMidAtlantic = ds === this.imageryProviders.midAtlanticScallopAreas;
          
          if (isTrackedGeorgesBank || isTrackedMidAtlantic) {
            console.log('🐚 SCALLOP_AREAS_DEBUG: Skipping tracked provider in search', {
              timestamp: new Date().toISOString(),
              isTrackedGeorgesBank,
              isTrackedMidAtlantic
            });
            continue;
          }
          
          // Check by entity characteristics instead of constructor name (minification-safe)
          if (!ds.entities || !ds.entities.values || ds.entities.values.length === 0) {
            continue;
          }
          
          const firstEntity = ds.entities.values[0];
          const props = firstEntity.properties;
          const entityRegion = this.getPropValue(props, 'region');
          const dataType = this.getPropValue(props, 'dataType');
          
          // Check by characteristics - this will catch data sources from previous MapController instances
          if (dataType === 'scallopArea' || (entityRegion && (entityRegion.toLowerCase().includes('georges bank') || entityRegion.toLowerCase().includes('mid-atlantic')))) {
            const isMidAtlantic = entityRegion && entityRegion.toLowerCase().includes('mid-atlantic');
            console.log('🐚 SCALLOP_AREAS_DEBUG: Found additional scallop area data source to remove', {
              timestamp: new Date().toISOString(),
              isMidAtlantic,
              entityCount: ds.entities.values.length,
              entityRegion,
              dataType,
              totalDataSourcesBefore: this.viewer.dataSources.length,
              isTrackedGeorgesBank,
              isTrackedMidAtlantic,
              dsReference: ds
            });
            try {
              this.viewer.dataSources.remove(ds);
              if (isMidAtlantic) {
                midAtlanticRemoved = true;
              } else {
                georgesBankRemoved = true;
              }
              console.log('🐚 SCALLOP_AREAS_DEBUG: Additional scallop area data source removed', {
                timestamp: new Date().toISOString(),
                isMidAtlantic,
                totalDataSourcesAfter: this.viewer.dataSources.length
              });
            } catch (error) {
              console.warn('⚠️ Error removing scallop area data source:', error);
              console.log('🐚 SCALLOP_AREAS_DEBUG: Error removing additional scallop area data source', {
                timestamp: new Date().toISOString(),
                error: error.message,
                isMidAtlantic
              });
            }
          }
        }
        removed = georgesBankRemoved && midAtlanticRemoved;
        
        console.log('🐚 SCALLOP_AREAS_DEBUG: Final removal status', {
          timestamp: new Date().toISOString(),
          georgesBankRemoved,
          midAtlanticRemoved,
          removed,
          totalDataSources: this.viewer.dataSources.length
        });
      } else {
        // Try direct removal first
        if (this.imageryProviders[layerId] && this.viewer.dataSources.contains(this.imageryProviders[layerId])) {
          this.viewer.dataSources.remove(this.imageryProviders[layerId]);
          removed = true;
        } else {
          // Search for matching data source by characteristics
          const dataSources = this.viewer.dataSources;
          for (let i = dataSources.length - 1; i >= 0; i--) {
            const ds = dataSources.get(i);
            if (ds.constructor.name !== 'GeoJsonDataSource' || ds.entities.values.length === 0) {
              continue;
            }
            
            const firstEntity = ds.entities.values[0];
            const props = firstEntity.properties;
            let matches = false;
            
            if (layerId === 'fishingClosedAreas') {
              const closureType = this.getPropValue(props, 'closure_type') || this.getPropValue(props, 'CLOSURE_TYPE');
              const entityName = firstEntity.name || this.getPropValue(props, 'name') || '';
              matches = !!closureType || (entityName && (entityName.toLowerCase().includes('closed') || entityName.toLowerCase().includes('closure')));
            } else if (layerId === 'congressionalDistricts') {
              let matchCount = 0;
              const sampleSize = Math.min(10, ds.entities.values.length);
              for (let j = 0; j < sampleSize; j++) {
                const entity = ds.entities.values[j];
                const entityId = entity.id || '';
                if (entityId.includes('congressional-districts')) matchCount++;
                if (entity.polyline || entity.label) matchCount++;
                const cd118fp = this.getPropValue(entity.properties, 'CD118FP');
                if (cd118fp) matchCount++;
              }
              matches = matchCount > 0;
            } else if (layerId === 'fisheriesManagementCouncil') {
              let matchCount = 0;
              const sampleSize = Math.min(10, ds.entities.values.length);
              for (let j = 0; j < sampleSize; j++) {
                const entity = ds.entities.values[j];
                const entityId = entity.id || '';
                const entityName = entity.name || '';
                if (entityId.includes('fisheries-management-council')) matchCount++;
                if (entity.polyline || entity.label) matchCount++;
                if (entityName && (entityName.toLowerCase().includes('council') || entityName.toLowerCase().includes('fishery'))) matchCount++;
                const councilName = this.getPropValue(entity.properties, 'COUNCIL');
                if (councilName) matchCount++;
              }
              matches = matchCount > 0;
            }
            
            if (matches) {
              console.log(`🗑️ Found matching ${layerId} data source, removing...`);
              this.viewer.dataSources.remove(ds);
              removed = true;
              // Continue searching for duplicates (don't break)
              // Note: Iterating backwards is safe when removing items
            }
          }
        }
      }
      
      if (removed) {
        console.log(`✅ Removed ${layerId} layer`);
      } else if (!force) {
        console.warn(`⚠️ Could not find ${layerId} GeoJSON layer to remove`);
      }
      
      // Only delete from activeLayers if it was actually in there (or if force is false)
      if (!force || this.activeLayers.has(layerId)) {
        this.activeLayers.delete(layerId);
      }
      this._emitLayerChanged(slug, false);
      return;
    }
    
    // Handle WMS imagery layers
    if (this.imageryProviders[layerId]) {
      const currentProvider = this.imageryProviders[layerId];
      const providerUrl = currentProvider?.url || currentProvider?._url;
      const layers = this.viewer.imageryLayers;
      let removed = false;
      
      for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers.get(i);
        const layerProvider = layer.imageryProvider;
        const layerUrl = layerProvider?.url || layerProvider?._url;
        
        if (layerProvider === currentProvider || (providerUrl && layerUrl === providerUrl)) {
          layers.remove(layer);
          removed = true;
          console.log(`✅ Removed ${layerId} WMS layer`);
          break;
        }
      }
      
      if (!removed) {
        console.warn(`⚠️ Could not find ${layerId} layer to remove (URL: ${providerUrl})`);
      }
      
      this.activeLayers.delete(layerId);
      this._emitLayerChanged(slug, false);
      return;
    }
    
    console.warn(`⚠️ No provider found for layer: ${layerId}`);
    this.activeLayers.delete(layerId);
    this._emitLayerChanged(slug, false);
  }
  
  /**
   * Update ports visualization based on active state
   */
  async updatePortsVisualization() {
    if (!this.viewer || !window.Cesium || !this.portsData || this.portsData.length === 0) {
      return;
    }
    
    if (this.viewer.isDestroyed()) {
      return;
    }
    
    if (!this.viewer.scene || !this.viewer.scene.canvas) {
      return;
    }
    
    const shouldShowPorts = this.activeLayers.has('ports');
    
    if (!shouldShowPorts) {
      // Remove all port entities
      this.portsEntities.forEach(entity => {
        if (!this.viewer.isDestroyed()) {
          try {
            this.viewer.entities.remove(entity);
          } catch (e) {
            // Entity might already be removed
          }
        }
      });
      this.portsEntities = [];
      return;
    }
    
    // Check if ports already exist
    const allEntities = this.viewer.entities.values;
    const existingPortCount = Array.from(allEntities).filter(entity => {
      const dataType = entity.properties?.dataType?.getValue ? 
        entity.properties.dataType.getValue() : 
        entity.properties?.dataType;
      return dataType === 'port';
    }).length;
    
    if (existingPortCount > 0) {
      // Ports already exist, just update our tracking
      this.portsEntities = Array.from(allEntities).filter(entity => {
        const dataType = entity.properties?.dataType?.getValue ? 
          entity.properties.dataType.getValue() : 
          entity.properties?.dataType;
        return dataType === 'port';
      });
      return;
    }
    
    // Add port entities
    this.portsEntities = [];
    this.portsData.forEach((port) => {
      let latitude = null;
      let longitude = null;
      
      if (port.latitude !== undefined && port.latitude !== null && 
          port.longitude !== undefined && port.longitude !== null) {
        latitude = parseFloat(port.latitude);
        longitude = parseFloat(port.longitude);
      } else if (port.lat !== undefined && port.lat !== null && 
                 port.lng !== undefined && port.lng !== null) {
        latitude = parseFloat(port.lat);
        longitude = parseFloat(port.lng);
      } else if (port.lat !== undefined && port.lat !== null && 
                 port.lon !== undefined && port.lon !== null) {
        latitude = parseFloat(port.lat);
        longitude = parseFloat(port.lon);
      } else {
        const notes = port.notes || '';
        const coordMatch = notes.match(/coordinates \(([^,]+),\s*([^)]+)\)/);
        if (coordMatch) {
          latitude = parseFloat(coordMatch[1]);
          longitude = parseFloat(coordMatch[2]);
        }
      }
      
      if (latitude === null || longitude === null || 
          isNaN(latitude) || isNaN(longitude) ||
          latitude < -90 || latitude > 90 ||
          longitude < -180 || longitude > 180) {
        return;
      }
      
      if (this.viewer.isDestroyed()) return;
      
      const portId = port.port_id || port.id || port.name;
      const uniqueId = `ref-layer-port-${portId}-${latitude}-${longitude}`;
      
      try {
        const entity = this.viewer.entities.add({
          id: uniqueId,
          position: window.Cesium.Cartesian3.fromDegrees(longitude, latitude),
          point: {
            pixelSize: 12,
            color: window.Cesium.Color.BLUE,
            outlineColor: window.Cesium.Color.WHITE,
            outlineWidth: 2,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new window.Cesium.NearFarScalar(1.5e2, 1.0, 1.5e7, 0.5),
            translucencyByDistance: new window.Cesium.NearFarScalar(1.5e2, 1.0, 1.5e7, 1.0),
            show: true
          },
          label: {
            text: port.name,
            font: '10pt sans-serif',
            fillColor: window.Cesium.Color.WHITE,
            outlineColor: window.Cesium.Color.BLACK,
            outlineWidth: 2,
            style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
            pixelOffset: new window.Cesium.Cartesian2(0, -25),
            horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
            verticalOrigin: window.Cesium.VerticalOrigin.BOTTOM,
            showBackground: true,
            backgroundColor: window.Cesium.Color.BLUE.withAlpha(0.7),
            backgroundPadding: new window.Cesium.Cartesian2(4, 2),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new window.Cesium.NearFarScalar(1.5e2, 1.0, 8.0e6, 0.0),
            translucencyByDistance: new window.Cesium.NearFarScalar(1.5e2, 1.0, 8.0e6, 1.0),
            show: true
          },
          show: true,
          properties: {
            name: port.name,
            state: port.state,
            latitude: latitude,
            longitude: longitude,
            notes: port.notes,
            created_date: port.created_date,
            dockage_port_fees: port.dockage_port_fees,
            fuel_price_per_l: port.fuel_price_per_l,
            ice_cost_total: port.ice_cost_total,
            insurance: port.insurance,
            ...Object.keys(port).reduce((acc, key) => {
              if (key.includes('_price_per_lb')) {
                acc[key] = port[key];
              }
              return acc;
            }, {}),
            dataType: 'port'
          }
        });
        
        this.portsEntities.push(entity);
      } catch (error) {
        console.error(`❌ Failed to add port entity for ${port.name}:`, error);
      }
    });
    
    if (this.viewer.scene) {
      this.viewer.scene.requestRender();
    }
  }
  
  /**
   * Set ports data (called by components that load ports from API)
   */
  setPortsData(portsData) {
    this.portsData = portsData;
    if (this.activeLayers.has('ports')) {
      this.updatePortsVisualization();
    }
  }
  
  /**
   * Set cold storage data and render if layer is active
   */
  setColdStorageData(coldStorageData) {
    this.coldStorageData = coldStorageData;
    if (this.activeLayers.has('coldStorage')) {
      this.renderColdStorageLayer();
    }
  }
  
  /**
   * Set ports with landing data and render if layer is active
   */
  async setPortsWithLandingData(portsData) {
    console.log('📦 setPortsWithLandingData called with', portsData?.length || 0, 'ports');
    console.log('📦 Active layers:', Array.from(this.activeLayers));
    this.portsWithLandingData = portsData;
    if (this.activeLayers.has('portsWithLanding')) {
      console.log('✅ portsWithLanding is active, calling renderPortsWithLandingLayer');
      await this.renderPortsWithLandingLayer();
    } else {
      console.log('⚠️ portsWithLanding is NOT active, skipping render');
    }
  }
  
  /**
   * Set bottom temperature data and render if layer is active
   */
  setBottomTemperatureData(temperatureData) {
    this.bottomTemperatureData = temperatureData;
    if (this.activeLayers.has('bottomTemperature')) {
      this.renderBottomTemperatureLayer();
    }
  }
  
  /**
   * Set trawl survey data and render if layer is active
   * @param {Object} trawlCatches - Can be { filtered, unfiltered } or plain object keyed by stationId
   */
  async setTrawlSurveyData(trawlCatches) {
    console.log('🎣 setTrawlSurveyData called with', trawlCatches ? (typeof trawlCatches === 'object' && trawlCatches.filtered ? 'filtered/unfiltered' : 'plain object') : 'null');
    this.trawlSurveyData = trawlCatches;
    if (!trawlCatches) {
      await this.removeTrawlSurveyLayer();
      return;
    }
    try {
      await this.renderTrawlSurveyLayer();
    } catch (err) {
      console.error('🎣 setTrawlSurveyData render error:', err);
    }
  }
  
  /**
   * Set callback for cold storage facility clicks
   */
  setColdStorageClickHandler(callback) {
    this.onColdStorageClick = callback;
  }
  
  /**
   * Set callback for ports with landing clicks
   */
  setPortsWithLandingClickHandler(callback) {
    this.onPortsWithLandingClick = callback;
  }

  /**
   * Set callback for trawl survey station clicks
   */
  setTrawlSurveyStationClickHandler(callback) {
    this.onTrawlSurveyStationClick = callback;
  }
  
  /**
   * Set callback for LEK habitat grid cell clicks
   */
  setLekGridCellClickHandler(callback) {
    this.onLekGridCellClick = callback;
  }

  /**
   * Set callback for map empty space clicks (e.g. clear LEK selection)
   */
  setMapEmptyClickHandler(callback) {
    this.onMapEmptyClick = callback;
  }
  
  /**
   * Set API function for popups (e.g., getAtlanticState for ports)
   */
  setApiFunction(functionName, apiFunction) {
    this.apiFunctions[functionName] = apiFunction;
  }
  
  /**
   * Set up global click handler for infrastructure layers
   */
  setupGlobalClickHandler() {
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      return;
    }
    
    // Remove existing handler if any
    if (this.globalClickHandler) {
      this.globalClickHandler.destroy();
      this.globalClickHandler = null;
    }
    
    // Create new handler
    this.globalClickHandler = new window.Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);
    
    this.globalClickHandler.setInputAction((click) => {
      const pickedObject = this.viewer.scene.pick(click.position);

      if (!window.Cesium.defined(pickedObject) || !pickedObject.id) {
        if (this.onMapEmptyClick) {
          this.onMapEmptyClick();
        }
        return;
      }
      
      const entity = pickedObject.id;
      const entityType = this.getPropValue(entity.properties, 'type');
      
      // Handle cold storage clicks
      if (entityType === 'coldStorage') {
        const facilityData = this.getPropValue(entity.properties, 'facilityData');
        if (facilityData) {
          this.showColdStoragePopup(facilityData, click.position);
          if (this.onColdStorageClick) {
            this.onColdStorageClick(facilityData);
          }
        }
        return;
      }
      
      // Handle ports with landing clicks
      if (entityType === 'portWithLanding') {
        const portData = this.getPropValue(entity.properties, 'portData');
        if (portData) {
          this.showPortsWithLandingPopup(portData, click.position);
          if (this.onPortsWithLandingClick) {
            this.onPortsWithLandingClick(portData);
          }
        }
        return;
      }
      
      // Handle trawl survey station clicks
      if (entityType === 'trawlSurvey') {
        const stationData = this.getPropValue(entity.properties, 'stationData');
        if (stationData) {
          this.showTrawlSurveyPopup(stationData, click.position);
          if (this.onTrawlSurveyStationClick) {
            this.onTrawlSurveyStationClick(stationData);
          }
        }
        return;
      }

      // Handle AIS vessel clicks
      if (entityType === 'aisVessel' || entityType === 'aisVesselTrack') {
        const vesselData = this.getPropValue(entity.properties, 'vesselData');
        if (vesselData) {
          this.showAISVesselPopup(vesselData, click.position);
          if (this.onAISVesselClick) {
            this.onAISVesselClick(vesselData);
          }
        }
        return;
      }

      // Handle LEK habitat grid cell clicks (species/month from filters, same as other layers)
      if (entityType === 'lekHabitat') {
        const gridId = this.getPropValue(entity.properties, 'gridId');
        const latitude = this.getPropValue(entity.properties, 'latitude');
        const longitude = this.getPropValue(entity.properties, 'longitude');
        const speciesName = this.getPropValue(entity.properties, 'speciesName');
        const speciesId = this.getPropValue(entity.properties, 'species_id');
        const lekSpecies = this.filters.lekSpecies;
        const lekMonthRange = this.filters.lekMonthRange;
        if (gridId && this.onLekGridCellClick) {
          const speciesIdToUse = speciesId || (typeof lekSpecies === 'number' ? lekSpecies : null);
          const monthStart = lekMonthRange?.start ?? 1;
          const monthEnd = lekMonthRange?.end ?? 12;
          this.onLekGridCellClick({
            gridId,
            latitude,
            longitude,
            speciesName,
            speciesId: speciesIdToUse,
            monthStart,
            monthEnd,
            clickPosition: click.position
          });
        }
        return;
      }

      // Handle fishing closed area clicks (GeoJSON layer uses dataType)
      const dataType = this.getPropValue(entity.properties, 'dataType');
      if (dataType === 'fishingClosedArea') {
        const areaData = {
          name: this.getPropValue(entity.properties, 'name'),
          description: this.getPropValue(entity.properties, 'description'),
          closureType: this.getPropValue(entity.properties, 'closureType'),
          areaSqNm: this.getPropValue(entity.properties, 'areaSqNm'),
          fid: this.getPropValue(entity.properties, 'fid')
        };
        this.showFishingClosedAreaPopup(areaData, click.position);
        return;
      }

      // Bottom temperature uses Cesium's built-in description popup, no custom handler needed
    }, window.Cesium.ScreenSpaceEventType.LEFT_CLICK);
    
    // Hover handler for labels (cold storage only)
    this.globalClickHandler.setInputAction((movement) => {
      const pickedObject = this.viewer.scene.pick(movement.endPosition);
      
      // Hide all cold storage labels first
      if (this.coldStorageEntities) {
        this.coldStorageEntities.forEach(entity => {
          if (entity.label) {
            entity.label.show = false;
          }
        });
      }
      
      // Show label for hovered entity
      if (window.Cesium.defined(pickedObject) && pickedObject.id) {
        const entity = pickedObject.id;
        const entityType = this.getPropValue(entity.properties, 'type');
        if (entityType === 'coldStorage' && entity.label) {
          entity.label.show = true;
        }
      }
    }, window.Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }
  
  /**
   * Create a popup container at the given position (for React/consumer to render into).
   * Used by LEK InfoPanel and other popups that need to render React components.
   * @param {Object} screenPosition - Cesium screen coords { x, y } from click.position, or null for default
   * @param {string} popupId - DOM id for the container (e.g. 'lek-info-popup')
   * @param {Object} defaultPosition - Fallback { x, y } when screenPosition is null
   * @returns {HTMLElement} The container div, appended to document.body
   */
  createPopupContainer(screenPosition, popupId, defaultPosition = { x: 20, y: 80 }) {
    const existing = document.getElementById(popupId);
    if (existing) existing.remove();

    let viewportX, viewportY;
    if (screenPosition && this.viewer?.scene?.canvas) {
      const canvas = this.viewer.scene.canvas;
      const rect = canvas.getBoundingClientRect();
      viewportX = rect.left + screenPosition.x + 15;
      viewportY = rect.top + screenPosition.y + 5;
    } else {
      viewportX = defaultPosition.x;
      viewportY = defaultPosition.y;
    }

    const div = document.createElement('div');
    div.id = popupId;
    div.style.cssText = `
      position: fixed;
      left: ${viewportX}px;
      top: ${viewportY}px;
      z-index: 10000;
      pointer-events: auto;
    `;
    document.body.appendChild(div);
    return div;
  }

  /**
   * Remove a popup container by id
   */
  removePopupContainer(popupId) {
    const el = document.getElementById(popupId);
    if (el?.parentNode) el.remove();
  }

  /**
   * Show popup for cold storage facility
   */
  showColdStoragePopup(facility, screenPosition) {
    // Remove existing popup if any
    const existingPopup = document.getElementById('cold-storage-popup');
    if (existingPopup) {
      existingPopup.remove();
    }
    
    // Convert Cesium screen coordinates to viewport coordinates
    // screenPosition is relative to the canvas, but we need viewport coordinates for position: fixed
    const canvas = this.viewer.scene.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const viewportX = canvasRect.left + screenPosition.x;
    const viewportY = canvasRect.top + screenPosition.y;
    
    // Create popup element
    const popup = document.createElement('div');
    popup.id = 'cold-storage-popup';
    popup.style.cssText = `
      position: fixed;
      left: ${viewportX + 15}px;
      top: ${viewportY + 5}px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
      padding: 16px;
      max-width: 320px;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    
    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
        <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #1e3a8a; max-width: 250px;">
          ${facility.company}
        </h3>
        <button id="close-cold-storage-popup" style="
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          line-height: 1;
        ">×</button>
      </div>
      <div style="border-top: 2px solid #3b82f6; padding-top: 12px;">
        <div style="margin-bottom: 8px;">
          <span style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase;">Location</span>
          <div style="font-size: 13px; color: #1e293b;">${facility.city}, ${facility.state}</div>
        </div>
        ${facility.address ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase;">Address</span>
            <div style="font-size: 13px; color: #1e293b;">${facility.address}</div>
          </div>
        ` : ''}
        ${facility.phoneNumber ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase;">Phone</span>
            <div style="font-size: 13px; color: #1e293b;">
              <a href="tel:${facility.phoneNumber}" style="color: #3b82f6; text-decoration: none;">${facility.phoneNumber}</a>
            </div>
          </div>
        ` : ''}
        ${facility.congressionalDistrict ? `
          <div style="margin-bottom: 8px;">
            <span style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase;">Congressional District</span>
            <div style="font-size: 13px; color: #1e293b;">${facility.congressionalDistrict}</div>
          </div>
        ` : ''}
        <div>
          <span style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase;">Coordinates</span>
          <div style="font-size: 12px; color: #64748b;">${facility.latitude.toFixed(4)}, ${facility.longitude.toFixed(4)}</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(popup);
    
    // Add close button handler
    document.getElementById('close-cold-storage-popup').addEventListener('click', () => {
      popup.remove();
    });
    
    // Close popup when clicking outside
    const closeOnOutsideClick = (e) => {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('click', closeOnOutsideClick);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', closeOnOutsideClick);
    }, 100);
  }
  
  /**
   * Show popup for ports with landing data
   */
  async showPortsWithLandingPopup(port, screenPosition) {
    // Remove existing popup if any
    const existingPopup = document.getElementById('port-popup');
    if (existingPopup) {
      existingPopup.remove();
    }
    
    // Convert Cesium screen coordinates to viewport coordinates
    // screenPosition is relative to the canvas, but we need viewport coordinates for position: fixed
    const canvas = this.viewer.scene.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const viewportX = canvasRect.left + screenPosition.x;
    const viewportY = canvasRect.top + screenPosition.y;
    
    // Create popup element
    const popup = document.createElement('div');
    popup.id = 'port-popup';
    popup.style.cssText = `
      position: fixed;
      left: ${viewportX + 15}px;
      top: ${viewportY + 5}px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
      padding: 16px;
      max-width: 400px;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    
    const landingsDollars = (port.landings_millions_dollars * 1000000).toLocaleString();
    
    // Initial popup content (before state data loads)
    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
        <div>
          <div style="font-size: 16px; font-weight: 600; color: #111827; margin-bottom: 2px;">
            ${port.port_name}
          </div>
          <div style="font-size: 12px; color: #6b7280;">
            ${port.state}
          </div>
        </div>
        <button id="close-port-popup" style="background: none; border: none; font-size: 20px; color: #9ca3af; cursor: pointer; padding: 0; line-height: 1;">
          ×
        </button>
      </div>
      <div style="border-top: 1px solid #e5e7eb; padding-top: 12px;">
        <div style="font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 600; margin-bottom: 6px;">
          Port - 2023 Landings
        </div>
        <div style="font-size: 24px; font-weight: 700; color: #10b981; margin-bottom: 12px;">
          $${landingsDollars}
        </div>
        <div style="font-size: 14px; color: #6b7280; margin-bottom: 16px;">
          ${port.landings_millions_lbs.toFixed(1)}M lbs landed
        </div>
        <div id="state-kpis" style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
          <div style="font-size: 11px; color: #6b7280;">Loading state data...</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(popup);
    
    // Fetch state-level data if API function is available
    if (this.apiFunctions.getAtlanticState) {
      try {
        const stateResponse = await this.apiFunctions.getAtlanticState(port.state);
        if (stateResponse && stateResponse.data) {
          const stateData = stateResponse.data;
          
          const formatMoney = (num) => {
            if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'B';
            return '$' + num.toFixed(0) + 'M';
          };
          
          const formatNumber = (num) => {
            if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
            return num.toLocaleString();
          };
          
          const stateKpisDiv = document.getElementById('state-kpis');
          if (stateKpisDiv) {
            stateKpisDiv.innerHTML = `
              <div style="font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 600; margin-bottom: 8px;">
                ${port.state} State - Fisheries Data (2023)
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
                <div style="background: #f0f9ff; padding: 8px; border-radius: 4px;">
                  <div style="font-size: 10px; color: #0369a1; font-weight: 600; margin-bottom: 2px;">COMMERCIAL SALES</div>
                  <div style="font-size: 16px; font-weight: 700; color: #0c4a6e;">${formatMoney(stateData.commercial_sales_millions)}</div>
                  <div style="font-size: 9px; color: #6b7280; margin-top: 2px;">${formatNumber(stateData.commercial_jobs * 1000)} jobs</div>
                </div>
                <div style="background: #fef3c7; padding: 8px; border-radius: 4px;">
                  <div style="font-size: 10px; color: #b45309; font-weight: 600; margin-bottom: 2px;">RECREATIONAL SALES</div>
                  <div style="font-size: 16px; font-weight: 700; color: #92400e;">${formatMoney(stateData.recreational_sales_millions)}</div>
                  <div style="font-size: 9px; color: #6b7280; margin-top: 2px;">${formatNumber(stateData.recreational_jobs)} jobs</div>
                </div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 10px;">
                <div>
                  <div style="color: #6b7280;">State Commercial Landings</div>
                  <div style="font-weight: 600; color: #111827;">${stateData.commercial_landings_lbs}M lbs</div>
                </div>
                <div>
                  <div style="color: #6b7280;">State Recreational Trips</div>
                  <div style="font-weight: 600; color: #111827;">${stateData.recreational_trips_millions.toFixed(1)}M</div>
                </div>
              </div>
              <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 9px; color: #9ca3af; font-style: italic;">
                State data: NOAA & ACCSP 2023
              </div>
            `;
          }
        } else {
          const stateKpisDiv = document.getElementById('state-kpis');
          if (stateKpisDiv) {
            stateKpisDiv.innerHTML = `
              <div style="font-size: 10px; color: #9ca3af; font-style: italic;">
                State-level data not available for ${port.state}
              </div>
            `;
          }
        }
      } catch (error) {
        console.error('Error fetching state data:', error);
        const stateKpisDiv = document.getElementById('state-kpis');
        if (stateKpisDiv) {
          stateKpisDiv.innerHTML = `
            <div style="font-size: 10px; color: #9ca3af; font-style: italic;">
              Unable to load state data
            </div>
          `;
        }
      }
    } else {
      const stateKpisDiv = document.getElementById('state-kpis');
      if (stateKpisDiv) {
        stateKpisDiv.innerHTML = `
          <div style="font-size: 10px; color: #9ca3af; font-style: italic;">
            State data API not configured
          </div>
        `;
      }
    }
    
    // Add close button handler
    document.getElementById('close-port-popup').addEventListener('click', () => {
      popup.remove();
    });
    
    // Close popup when clicking outside
    const closeOnOutsideClick = (e) => {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('click', closeOnOutsideClick);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', closeOnOutsideClick);
    }, 100);
  }
  
  /**
   * Render cold storage facilities layer
   */
  renderColdStorageLayer() {
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      return;
    }
    
    // Remove existing layer
    this.removeColdStorageLayer();
    
    if (this.coldStorageData.length === 0) {
      return;
    }
    
    console.log('🧊 Rendering cold storage facilities...');
    
    // Create custom data source
    this.coldStorageDataSource = new window.Cesium.CustomDataSource('Cold_Storage');
    
    // Create icon for cold storage
    const createColdStorageIcon = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      
      ctx.beginPath();
      ctx.arc(16, 16, 14, 0, Math.PI * 2);
      ctx.fillStyle = '#3b82f6';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('❄', 16, 16);
      
      return canvas.toDataURL();
    };
    
    const iconDataUrl = createColdStorageIcon();
    
    // Create entities
    this.coldStorageEntities = [];
    this.coldStorageData.forEach((facility) => {
      try {
        const entity = this.coldStorageDataSource.entities.add({
          id: `cold-storage-${facility.id}`,
          name: facility.company,
          position: window.Cesium.Cartesian3.fromDegrees(
            facility.longitude,
            facility.latitude,
            0
          ),
          billboard: {
            image: iconDataUrl,
            width: 24,
            height: 24,
            verticalOrigin: window.Cesium.VerticalOrigin.BOTTOM,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
          },
          label: {
            text: facility.company,
            font: '12px sans-serif',
            fillColor: window.Cesium.Color.WHITE,
            outlineColor: window.Cesium.Color.BLACK,
            outlineWidth: 2,
            style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: window.Cesium.VerticalOrigin.TOP,
            pixelOffset: new window.Cesium.Cartesian2(0, 8),
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            show: false
          },
          properties: {
            type: 'coldStorage',
            facilityData: facility
          }
        });
        
        this.coldStorageEntities.push(entity);
      } catch (error) {
        console.error('Error creating cold storage entity:', error);
      }
    });
    
    // Add to viewer
    this.viewer.dataSources.add(this.coldStorageDataSource);
    console.log(`✅ Rendered ${this.coldStorageEntities.length} cold storage facilities`);
  }
  
  /**
   * Remove cold storage layer
   */
  removeColdStorageLayer() {
    if (this.coldStorageDataSource && this.viewer && !this.viewer.isDestroyed()) {
      if (this.viewer.dataSources.contains(this.coldStorageDataSource)) {
        this.viewer.dataSources.remove(this.coldStorageDataSource);
      }
      this.coldStorageDataSource = null;
      this.coldStorageEntities = [];
    }
  }
  
  /**
   * Render ports with landing data layer
   */
  async renderPortsWithLandingLayer() {
    if (!this.viewer) {
      console.log('⚠️ Cannot render ports: viewer is null');
      return;
    }
    
    if (!window.Cesium) {
      console.log('⚠️ Cannot render ports: Cesium not available');
      return;
    }
    
    if (this.viewer.isDestroyed()) {
      console.log('⚠️ Cannot render ports: viewer is destroyed');
      return;
    }
    
    // Remove existing layer
    this.removePortsWithLandingLayer();
    
    if (!this.portsWithLandingData) {
      console.log('⚠️ Cannot render ports: portsWithLandingData is null/undefined');
      return;
    }
    
    if (!Array.isArray(this.portsWithLandingData)) {
      console.log('⚠️ Cannot render ports: portsWithLandingData is not an array', typeof this.portsWithLandingData);
      return;
    }
    
    if (this.portsWithLandingData.length === 0) {
      console.log('⚠️ Cannot render ports: portsWithLandingData is empty array');
      return;
    }
    
    console.log('🚢 Rendering ports with landing data...', this.portsWithLandingData.length, 'ports');
    
    // Create custom data source
    this.portsWithLandingDataSource = new window.Cesium.CustomDataSource('Ports_With_Landing');
    
    // Create icon for ports
    const createPortIcon = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      
      ctx.beginPath();
      ctx.arc(16, 16, 14, 0, Math.PI * 2);
      ctx.fillStyle = '#10b981';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚓', 16, 16);
      
      return canvas.toDataURL();
    };
    
    const iconDataUrl = createPortIcon();
    
    // Create entities
    this.portsWithLandingEntities = [];
    this.portsWithLandingData.forEach((port, index) => {
      try {
        // Validate port data
        if (!port.longitude || !port.latitude) {
          console.warn(`⚠️ Port ${index} missing coordinates:`, port);
          return;
        }
        
        if (!port.id && !port.port_id) {
          console.warn(`⚠️ Port ${index} missing id:`, port);
          return;
        }
        
        const portId = port.id || port.port_id;
        const portName = port.port_name || port.name || `Port ${portId}`;
        
        const entity = this.portsWithLandingDataSource.entities.add({
          id: `port-landing-${portId}`,
          name: portName,
          position: window.Cesium.Cartesian3.fromDegrees(
            parseFloat(port.longitude),
            parseFloat(port.latitude),
            0
          ),
          billboard: {
            image: iconDataUrl,
            width: 24,
            height: 24,
            verticalOrigin: window.Cesium.VerticalOrigin.BOTTOM,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
          },
          properties: {
            type: 'portWithLanding',
            portData: port
          }
        });
        
        this.portsWithLandingEntities.push(entity);
      } catch (error) {
        console.error('Error creating port with landing entity:', error);
      }
    });
    
    // Add to viewer
    try {
      await this.viewer.dataSources.add(this.portsWithLandingDataSource);
      console.log(`✅ Rendered ${this.portsWithLandingEntities.length} ports with landing data`);
      console.log('✅ Data source added to viewer. Total data sources:', this.viewer.dataSources.length);
      
      // Force a render
      if (this.viewer.scene) {
        this.viewer.scene.requestRender();
      }
    } catch (error) {
      console.error('❌ Error adding ports data source to viewer:', error);
      throw error;
    }
  }
  
  /**
   * Remove ports with landing layer
   */
  removePortsWithLandingLayer() {
    if (this.portsWithLandingDataSource && this.viewer && !this.viewer.isDestroyed()) {
      if (this.viewer.dataSources.contains(this.portsWithLandingDataSource)) {
        this.viewer.dataSources.remove(this.portsWithLandingDataSource);
      }
      this.portsWithLandingDataSource = null;
      this.portsWithLandingEntities = [];
    }
  }
  
  /**
   * Render bottom temperature layer
   */
  renderBottomTemperatureLayer() {
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      return;
    }
    
    // Remove existing layer
    this.removeBottomTemperatureLayer();
    
    if (this.bottomTemperatureData.length === 0) {
      return;
    }
    
    console.log('🌡️ Rendering bottom temperature data...');
    
    // Temperature-to-color mapping for bottom temperature visualization
    const getTemperatureColor = (temp) => {
      if (temp < 0) return '#0000FF';
      if (temp < 5) return '#4169E1';
      if (temp < 10) return '#00BFFF';
      if (temp < 15) return '#00FF7F';
      if (temp < 20) return '#FFFF00';
      if (temp < 25) return '#FFA500';
      return '#FF0000';
    };
    
    // Create custom data source
    this.bottomTemperatureDataSource = new window.Cesium.CustomDataSource('Bottom_Temperature');
    
    // Create entities
    this.bottomTemperatureData.forEach((point) => {
      try {
        const color = getTemperatureColor(point.temperature);
        const cesiumColor = window.Cesium.Color.fromCssColorString(color);
        
        this.bottomTemperatureDataSource.entities.add({
          position: window.Cesium.Cartesian3.fromDegrees(
            point.longitude,
            point.latitude,
            0
          ),
          point: {
            pixelSize: 8,
            color: cesiumColor,
            outlineColor: window.Cesium.Color.WHITE,
            outlineWidth: 1,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND
          },
          description: `
            <div style="font-family: Arial; padding: 10px;">
              <h3 style="margin-top: 0;">Bottom Temperature</h3>
              <p><strong>Temperature:</strong> ${point.temperature.toFixed(1)}°C (${(point.temperature * 9/5 + 32).toFixed(1)}°F)</p>
              <p><strong>Depth:</strong> ${point.depth.toFixed(1)} m (${(point.depth * 3.28084).toFixed(1)} ft)</p>
              <p><strong>Location:</strong> ${point.latitude.toFixed(3)}°N, ${Math.abs(point.longitude).toFixed(3)}°W</p>
              <p><strong>Time:</strong> ${new Date(point.time).toLocaleString()}</p>
            </div>
          `
        });
      } catch (error) {
        console.error('Error creating temperature point entity:', error);
      }
    });
    
    // Add to viewer
    this.viewer.dataSources.add(this.bottomTemperatureDataSource).then(() => {
      console.log(`✅ Rendered ${this.bottomTemperatureData.length} temperature points`);
    });
  }
  
  /**
   * Remove bottom temperature layer
   */
  removeBottomTemperatureLayer() {
    if (this.bottomTemperatureDataSource && this.viewer && !this.viewer.isDestroyed()) {
      if (this.viewer.dataSources.contains(this.bottomTemperatureDataSource)) {
        this.viewer.dataSources.remove(this.bottomTemperatureDataSource, true);
      }
      this.bottomTemperatureDataSource = null;
    }
  }
  
  /**
   * Load state boundaries GeoJSON data
   */
  async loadStateBoundaries() {
    if (this.stateBoundariesData) {
      // Already loaded
      return;
    }
    
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      console.log('⚠️ Cannot load state boundaries: viewer not ready or destroyed');
      return;
    }
    
    console.log('🗺️ Loading state boundaries...');
    
    try {
      // Fetch GeoJSON data
      const response = await fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const geojsonData = await response.json();
      this.stateBoundariesData = geojsonData;
      console.log('✅ State boundaries GeoJSON loaded');
      
      // Render if layer is active
      if (this.activeLayers.has('stateBoundaries')) {
        await this.renderStateBoundariesLayer();
      }
    } catch (error) {
      console.error('❌ Error loading state boundaries:', error);
      this.stateBoundariesData = null;
    }
  }
  
  /**
   * Render state boundaries layer
   */
  async renderStateBoundariesLayer() {
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      console.log('⚠️ Cannot render state boundaries: viewer not ready or destroyed');
      return;
    }
    
    // First, aggressively remove any existing state boundaries data sources
    // BUT don't remove from activeLayers - we're rendering, not removing!
    this.removeStateBoundariesLayerDataOnly();
    
    // Also search for any remaining state boundaries data sources that might not be tracked
    const dataSources = this.viewer.dataSources;
    for (let i = dataSources.length - 1; i >= 0; i--) {
      const ds = dataSources.get(i);
      
      if (ds.constructor.name === 'CustomDataSource' && ds.entities && ds.entities.values.length > 0) {
        // Check a sample of entities for state boundary characteristics
        const sampleSize = Math.min(5, ds.entities.values.length);
        let matchCount = 0;
        
        for (let j = 0; j < sampleSize; j++) {
          const entity = ds.entities.values[j];
          const props = entity.properties;
          const dataType = this.getPropValue(props, 'dataType');
          const stateName = this.getPropValue(props, 'stateName');
          const entityId = entity.id || '';
          
          // State boundaries have specific characteristics
          if (dataType === 'stateBoundary' || dataType === 'stateLabel' || 
              !!stateName || entityId.includes('state-boundary')) {
            matchCount++;
          }
        }
        
        if (matchCount > 0) {
          console.log(`🧹 Removing duplicate state boundaries data source found in renderStateBoundariesLayer (${ds.entities.values.length} entities)`);
          this.viewer.dataSources.remove(ds);
        }
      }
    }
    
    if (!this.stateBoundariesData) {
      console.log('⚠️ Cannot render state boundaries: no data available');
      await this.loadStateBoundaries();
      if (!this.stateBoundariesData) {
        return;
      }
    }
    
    console.log('🗺️ Rendering state boundaries...');
    
    // Create CustomDataSource for isolation
    const dataSource = new window.Cesium.CustomDataSource('stateBoundaries');
    this.stateBoundariesDataSource = dataSource;
    
    // Track which states have been labeled to avoid duplicates
    const labeledStates = new Set();
    
    // Process each feature
    this.stateBoundariesData.features.forEach((feature) => {
      const stateName = feature.properties.name;
      const geometry = feature.geometry;
      
      // Process different geometry types
      if (geometry.type === 'Polygon') {
        this.processStateBoundaryPolygon(dataSource, geometry.coordinates, stateName, labeledStates);
      } else if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((polygon) => {
          this.processStateBoundaryPolygon(dataSource, polygon, stateName, labeledStates);
        });
      }
    });
    
    // Add the data source to the viewer
    await this.viewer.dataSources.add(dataSource);
    this.stateBoundariesEntities = Array.from(dataSource.entities.values);
    console.log(`✅ Rendered ${this.stateBoundariesEntities.length} state boundary entities`);
  }
  
  /**
   * Process a single state boundary polygon
   */
  processStateBoundaryPolygon(dataSource, coordinates, stateName, labeledStates) {
    // First ring is the exterior boundary
    const exteriorRing = coordinates[0];
    const positions = exteriorRing.map(coord =>
      window.Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
    );
    
    // Create polyline for the boundary
    dataSource.entities.add({
      polyline: {
        positions: positions,
        width: 1.5,
        material: new window.Cesium.PolylineDashMaterialProperty({
          color: window.Cesium.Color.BLACK.withAlpha(0.5),
          dashLength: 10.0,
          dashPattern: parseInt('1111111100000000', 2)
        }),
        clampToGround: true
      },
      properties: {
        dataType: 'stateBoundary',
        stateName: stateName
      }
    });
    
    // Process holes (interior rings) - like Great Lakes
    for (let i = 1; i < coordinates.length; i++) {
      const holeRing = coordinates[i];
      const holePositions = holeRing.map(coord =>
        window.Cesium.Cartesian3.fromDegrees(coord[0], coord[1])
      );
      
      dataSource.entities.add({
        polyline: {
          positions: holePositions,
          width: 1.5,
          material: new window.Cesium.PolylineDashMaterialProperty({
            color: window.Cesium.Color.BLACK.withAlpha(0.5),
            dashLength: 10.0,
            dashPattern: parseInt('1111111100000000', 2)
          }),
          clampToGround: true
        },
        properties: {
          dataType: 'stateBoundary',
          stateName: stateName
        }
      });
    }
    
    // Add label at polygon centroid (only once per state)
    if (!labeledStates.has(stateName)) {
      labeledStates.add(stateName);
      
      // Calculate centroid
      let x = 0, y = 0, z = 0;
      positions.forEach(position => {
        x += position.x;
        y += position.y;
        z += position.z;
      });
      const centroid = new window.Cesium.Cartesian3(
        x / positions.length,
        y / positions.length,
        z / positions.length
      );
      
      // Create label entity
      dataSource.entities.add({
        position: centroid,
        label: {
          text: stateName,
          font: '11px sans-serif',
          fillColor: window.Cesium.Color.BLACK,
          outlineColor: window.Cesium.Color.WHITE,
          outlineWidth: 3,
          style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
          heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: true
        },
        properties: {
          dataType: 'stateLabel',
          stateName: stateName
        }
      });
    }
  }
  
  /**
   * Render trawl survey stations layer
   */
  async renderTrawlSurveyLayer() {
    console.log('🎣 renderTrawlSurveyLayer CALLED');
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      console.log('🎣 renderTrawlSurveyLayer EXIT: viewer not ready or destroyed');
      return;
    }

    if (!this.trawlSurveyData) {
      console.log('🎣 renderTrawlSurveyLayer EXIT: no data (this.trawlSurveyData is null/undefined)');
      return;
    }
    
    // Handle data structure: can be { filtered, unfiltered } or plain object or array
    let effectiveTrawlCatches = null;
    let unfilteredTrawlCatches = null;

    const toArray = (data) => {
      if (!data || typeof data !== 'object') return null;
      if (Array.isArray(data)) return data;
      if (data.results && Array.isArray(data.results)) return data.results;
      if (data.data && Array.isArray(data.data)) return data.data;
      if (data.catches && Array.isArray(data.catches)) return data.catches;
      if (data.items && Array.isArray(data.items)) return data.items;
      return null;
    };

    const groupCatchesByStation = (data) => {
      const arr = toArray(data);
      if (arr && arr.length > 0) {
        const byStation = {};
        arr.forEach((row, idx) => {
          const id = row.station_id ?? row.id ?? row.tow_id ?? row.stationid ?? `station_${idx}`;
          const key = String(id);
          if (!byStation[key]) byStation[key] = [];
          byStation[key].push(row);
        });
        return byStation;
      }
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const keys = Object.keys(data);
        if (keys.length === 0) return null;
        const firstVal = data[keys[0]];
        if (Array.isArray(firstVal)) return data;
        return groupCatchesByStation([data]);
      }
      return null;
    };

    if (typeof this.trawlSurveyData === 'object') {
      if (this.trawlSurveyData.filtered != null && this.trawlSurveyData.unfiltered != null) {
        effectiveTrawlCatches = groupCatchesByStation(this.trawlSurveyData.filtered) ?? groupCatchesByStation(this.trawlSurveyData.unfiltered);
        unfilteredTrawlCatches = groupCatchesByStation(this.trawlSurveyData.unfiltered) ?? effectiveTrawlCatches;
      }
      if (!effectiveTrawlCatches && Object.keys(this.trawlSurveyData).length > 0 && !this.trawlSurveyData.filtered) {
        effectiveTrawlCatches = groupCatchesByStation(this.trawlSurveyData);
        unfilteredTrawlCatches = effectiveTrawlCatches;
      }
    }

    if (!effectiveTrawlCatches || Object.keys(effectiveTrawlCatches).length === 0) {
      const raw = this.trawlSurveyData;
      const peek = Array.isArray(raw)
        ? `array[${raw.length}]`
        : typeof raw === 'object' && raw !== null
          ? `keys: ${Object.keys(raw).join(', ')}`
          : String(raw);
      console.log('🎣 renderTrawlSurveyLayer EXIT: no valid catch data. raw=', peek);
      await this.removeTrawlSurveyLayer();
      return;
    }

    const getLatLon = (c) => {
      const rawLat = c.decdeg_beg_lat ?? c.latitude ?? c.lat ?? c.beg_lat ?? c.start_lat ?? c.dec_lat ?? c.lat_beg;
      const rawLon = c.decdeg_beg_lon ?? c.longitude ?? c.lon ?? c.beg_lon ?? c.start_lon ?? c.dec_lon ?? c.lon_beg;
      const lat = rawLat != null ? Number(rawLat) : NaN;
      const lon = rawLon != null ? Number(rawLon) : NaN;
      return { lat: Number.isFinite(lat) ? lat : NaN, lon: Number.isFinite(lon) ? lon : NaN };
    };

    console.log(`🎣 Rendering trawl survey stations. Total: ${Object.keys(effectiveTrawlCatches).length}`);
    
    // Get filters from MapController
    const timeOfDayFilter = this.filters.trawlSurveyTimeOfDay;
    const minDepth = this.filters.trawlSurveyMinDepth;
    const maxDepth = this.filters.trawlSurveyMaxDepth;
    const minBotTemp = this.filters.trawlSurveyMinBotTemp;
    const maxBotTemp = this.filters.trawlSurveyMaxBotTemp;
    const depthRange = {
      min: minDepth !== null && minDepth !== undefined ? minDepth : 0,
      max: maxDepth !== null && maxDepth !== undefined ? maxDepth : 200
    };
    const botTempRange = {
      min: minBotTemp !== null && minBotTemp !== undefined ? minBotTemp : -10,
      max: maxBotTemp !== null && maxBotTemp !== undefined ? maxBotTemp : 30
    };
    
    console.log(`🎣 Filters: timeOfDay=${timeOfDayFilter || 'none'}, depth=${depthRange.min}-${depthRange.max}m, botTemp=${botTempRange.min}-${botTempRange.max}°C`);
    
    // Helper function to determine time category from datetime string
    const getTimeCategory = (dateTimeStr) => {
      if (!dateTimeStr) return null;
      try {
        const [datePart, timePart] = dateTimeStr.split(' ');
        if (!datePart || !timePart) return null;
        
        const [month, day, year] = datePart.split('/');
        const [hour, minute, second] = timePart.split(':');
        
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second || 0));
        const hourOfDay = date.getHours();
        const minutesOfDay = date.getMinutes();
        const totalMinutes = hourOfDay * 60 + minutesOfDay;
        
        if (totalMinutes >= 300 && totalMinutes < 420) return 'Dawn';
        else if (totalMinutes >= 420 && totalMinutes < 1080) return 'Daytime';
        else if (totalMinutes >= 1080 && totalMinutes < 1200) return 'Dusk';
        else return 'Nighttime';
      } catch (error) {
        console.warn('Error parsing datetime:', dateTimeStr, error);
        return null;
      }
    };
    
    // Reuse existing data source or create new one
    if (!this.trawlSurveyDataSource) {
      this.trawlSurveyDataSource = new window.Cesium.CustomDataSource('TRAWL_SURVEY');
      this.trawlSurveyDataSource.show = true;
      await this.viewer.dataSources.add(this.trawlSurveyDataSource);
    } else {
      this.trawlSurveyDataSource.show = true;
      // Clear existing entities to avoid ID conflicts
      // Use removeAll with a callback to ensure it completes
      const entityCount = this.trawlSurveyDataSource.entities.values.length;
      if (entityCount > 0) {
        this.trawlSurveyDataSource.entities.removeAll();
        // Wait for entities to be fully removed by checking the collection
        let attempts = 0;
        while (this.trawlSurveyDataSource.entities.values.length > 0 && attempts < 10) {
          await new Promise(resolve => setTimeout(resolve, 10));
          attempts++;
        }
      }
    }
    
    let renderedCount = 0;
    const firstEntry = Object.entries(effectiveTrawlCatches)[0];
    if (firstEntry && firstEntry[1]?.[0]) {
      const [sampleStationId, sampleCatches] = firstEntry;
      const sample = sampleCatches[0];
      const { lat: sampleLat, lon: sampleLon } = getLatLon(sample);
      console.log('🎣 Trawl sample record keys:', Object.keys(sample), '→ lat:', sampleLat, 'lon:', sampleLon);
    }

    Object.entries(effectiveTrawlCatches).forEach(([stationId, catches]) => {
      if (!Array.isArray(catches) || catches.length === 0) return;

      const firstCatch = catches[0];
      const { lat: firstLat, lon: firstLon } = getLatLon(firstCatch);
      if (!Number.isFinite(firstLat) || !Number.isFinite(firstLon)) return;

      const lat = firstLat;
      const lon = firstLon;

      // Filter by time of day if filter is set
      if (timeOfDayFilter && timeOfDayFilter !== '') {
        const stationTimeCategory = getTimeCategory(firstCatch.begin_est_towdate ?? firstCatch.begin_est_tow_date ?? firstCatch.tow_date);
        if (stationTimeCategory !== timeOfDayFilter) {
          return;
        }
      }
      
      // Filter by depth range if filter is set
      const stationDepth = firstCatch.avg_depth !== null && firstCatch.avg_depth !== undefined
        ? firstCatch.avg_depth
        : firstCatch.set_depth ?? firstCatch.depth;
      
      if (stationDepth !== null && stationDepth !== undefined) {
        if (depthRange.min > 0 || depthRange.max < 200) {
          if (stationDepth < depthRange.min || stationDepth > depthRange.max) {
            return;
          }
        }
      }
      
      // Filter by bottom temperature range if filter is set
      const stationBotTemp = firstCatch.bot_temp;
      
      // Only apply filter if range is not default (-10 to 30)
      if (botTempRange.min > -10 || botTempRange.max < 30) {
        // If station has bot_temp data, filter it
        if (stationBotTemp !== null && stationBotTemp !== undefined) {
          if (stationBotTemp < botTempRange.min || stationBotTemp > botTempRange.max) {
            return;
          }
        } else {
          // If station doesn't have bot_temp data and filter is active, exclude it
          // (This ensures only stations with temperature data are shown when filter is active)
          return;
        }
      }
      
      // Get unfiltered catches for popup display
      const unfilteredCatches = (unfilteredTrawlCatches && unfilteredTrawlCatches[stationId]) 
        ? unfilteredTrawlCatches[stationId] 
        : catches;
      
      // Aggregate species data
      const speciesMap = new Map();
      unfilteredCatches.forEach(catchRecord => {
        const speciesName = catchRecord.species_name || 'Unknown';
        if (!speciesMap.has(speciesName)) {
          speciesMap.set(speciesName, {
            name: speciesName,
            expCatchNum: 0,
            expCatchWt: 0
          });
        }
        const speciesData = speciesMap.get(speciesName);
        speciesData.expCatchNum += catchRecord.exp_catch_num || 0;
        speciesData.expCatchWt += catchRecord.exp_catch_wt || 0;
      });
      
      // Check if entity already exists and remove it first to avoid conflicts
      const entityId = `trawl_station_${stationId}`;
      const existingEntity = this.trawlSurveyDataSource.entities.getById(entityId);
      if (existingEntity) {
        this.trawlSurveyDataSource.entities.remove(existingEntity);
      }
      
      this.trawlSurveyDataSource.entities.add({
        id: entityId,
        name: `Trawl Survey Station ${stationId}`,
        position: window.Cesium.Cartesian3.fromDegrees(lon, lat, 0),
        point: {
          pixelSize: 8,
          color: window.Cesium.Color.LIGHTGREEN,
          outlineColor: window.Cesium.Color.BLACK,
          outlineWidth: 2,
          heightReference: window.Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: true
        },
        label: {
          text: stationId.substring(stationId.length - 3) || stationId,
          font: '10px sans-serif',
          fillColor: window.Cesium.Color.WHITE,
          outlineColor: window.Cesium.Color.BLACK,
          outlineWidth: 1,
          style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
          pixelOffset: new window.Cesium.Cartesian2(0, -12),
          heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
          scale: 0.8,
          eyeOffset: new window.Cesium.Cartesian3(0, 0, -1000),
          show: true
        },
        properties: {
          type: 'trawlSurvey',
          stationId: stationId,
          latitude: lat,
          longitude: lon,
          setDepth: firstCatch.set_depth,
          endDepth: firstCatch.end_depth,
          botTemp: firstCatch.bot_temp,
          cruise6: firstCatch.cruise6,
          begin_est_towdate: firstCatch.begin_est_towdate,
          speciesCount: speciesMap.size,
          catchesCount: unfilteredCatches.length,
          speciesList: Array.from(speciesMap.values()),
          catches: unfilteredCatches,
          dataType: 'trawlSurvey',
          stationData: {
            stationId: stationId,
            latitude: lat,
            longitude: lon,
            setDepth: firstCatch.set_depth,
            endDepth: firstCatch.end_depth,
            botTemp: firstCatch.bot_temp,
            cruise6: firstCatch.cruise6,
            begin_est_towdate: firstCatch.begin_est_towdate,
            catches: unfilteredCatches,
            speciesList: Array.from(speciesMap.values())
          }
        }
      });
      renderedCount++;
    });
    
    this.trawlSurveyEntities = Array.from(this.trawlSurveyDataSource.entities.values);
    console.log(`✅ Rendered ${renderedCount} trawl survey stations (filtered from ${Object.keys(effectiveTrawlCatches).length} total)`);
    this.viewer.scene.requestRender();
  }
  
  /**
   * Remove trawl survey layer
   */
  async removeTrawlSurveyLayer() {
    if (this.trawlSurveyDataSource && this.viewer && !this.viewer.isDestroyed()) {
      // Clear entities first to avoid conflicts
      if (this.trawlSurveyDataSource.entities) {
        this.trawlSurveyDataSource.entities.removeAll();
      }
      
      if (this.viewer.dataSources.contains(this.trawlSurveyDataSource)) {
        this.viewer.dataSources.remove(this.trawlSurveyDataSource, true);
      }
      this.trawlSurveyDataSource = null;
      this.trawlSurveyEntities = [];
      
      // Wait a frame to ensure removal is complete
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  
  /**
   * Show popup for trawl survey station
   */
  showTrawlSurveyPopup(stationData, screenPosition) {
    // Remove existing popup if any
    const existingPopup = document.getElementById('trawl-survey-popup');
    if (existingPopup) {
      existingPopup.remove();
    }
    
    // Convert Cesium screen coordinates to viewport coordinates
    const canvas = this.viewer.scene.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const viewportX = canvasRect.left + screenPosition.x;
    const viewportY = canvasRect.top + screenPosition.y;
    
    // Create popup element
    const popup = document.createElement('div');
    popup.id = 'trawl-survey-popup';
    popup.style.cssText = `
      position: fixed;
      left: ${viewportX + 15}px;
      top: ${viewportY + 5}px;
      background: white;
      border: 1px solid #333;
      border-radius: 6px;
      padding: 8px;
      z-index: 10000;
      pointer-events: none;
      max-width: 280px;
      width: 280px;
      max-height: 70vh;
      height: auto;
      overflow-y: auto;
      overflow-x: hidden;
      font-size: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    
    const stationId = stationData.stationId?.substring(stationData.stationId.length - 3) || stationData.stationId || 'Unknown';
    const catches = stationData.catches || [];
    
    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
        <h4 style="margin: 0 0 4px; font-size: 14px; font-weight: 600;">Station ${stationId}</h4>
        <button id="close-trawl-survey-popup" style="
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          line-height: 1;
        ">×</button>
      </div>
      ${stationData.begin_est_towdate ? `<div style="margin-bottom: 4px;"><b>Date:</b> ${stationData.begin_est_towdate}</div>` : ''}
      ${stationData.setDepth !== undefined && stationData.setDepth !== null ? `<div style="margin-bottom: 4px;"><b>Depth:</b> ${stationData.setDepth} meters</div>` : ''}
      ${stationData.botTemp !== undefined && stationData.botTemp !== null ? `<div style="margin-bottom: 4px;"><b>Bottom Temperature:</b> ${stationData.botTemp.toFixed(2)}°C</div>` : ''}
      <div style="border-bottom: 1px solid #ccc; margin: 8px 0; padding-bottom: 8px;"></div>
      ${catches.length > 0 ? `
        <div style="margin-bottom: 8px;">
          ${catches
            .filter(catchRecord => catchRecord.exp_catch_wt && catchRecord.exp_catch_wt > 0)
            .sort((a, b) => (b.exp_catch_wt || 0) - (a.exp_catch_wt || 0))
            .map((catchRecord, index) => `
              <div key="${catchRecord.catch_id || index}" style="margin-bottom: 4px;">
                <b>${catchRecord.species_name || 'Unknown'}:</b> ${catchRecord.exp_catch_wt?.toFixed(1) || '0.0'} lbs
              </div>
            `).join('')}
          <div style="border-top: 1px solid #ccc; margin-top: 8px; padding-top: 8px; font-weight: bold;">
            <b>Total:</b> ${catches.reduce((sum, catchRecord) => sum + (catchRecord.exp_catch_wt || 0), 0).toFixed(1)} lbs
          </div>
        </div>
      ` : '<div style="color: #6b7280; font-style: italic;">No catch data available</div>'}
    `;
    
    document.body.appendChild(popup);
    
    // Add close button handler
    document.getElementById('close-trawl-survey-popup').addEventListener('click', () => {
      popup.remove();
    });
    
    // Close popup when clicking outside
    const closeOnOutsideClick = (e) => {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('click', closeOnOutsideClick);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', closeOnOutsideClick);
    }, 100);
  }

  /**
   * Show popup for fishing closed area (GeoJSON layer)
   */
  showFishingClosedAreaPopup(areaData, screenPosition) {
    const existingPopup = document.getElementById('fishing-closed-area-popup');
    if (existingPopup) {
      existingPopup.remove();
    }

    const canvas = this.viewer.scene.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const viewportX = canvasRect.left + screenPosition.x;
    const viewportY = canvasRect.top + screenPosition.y;

    const popup = document.createElement('div');
    popup.id = 'fishing-closed-area-popup';
    popup.style.cssText = `
      position: fixed;
      left: ${viewportX + 15}px;
      top: ${viewportY + 5}px;
      background: white;
      border: 1px solid #333;
      border-radius: 6px;
      padding: 8px;
      z-index: 10000;
      max-width: 280px;
      width: 280px;
      max-height: 70vh;
      height: auto;
      overflow-y: auto;
      overflow-x: hidden;
      font-size: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
        <h4 style="margin: 0 0 4px; color: #2c3e50;">${areaData.name || 'Unnamed Area'}</h4>
        <button id="close-fishing-closed-area-popup" style="
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          line-height: 1;
        ">×</button>
      </div>
      <div><b>Description:</b> ${areaData.description || 'No description'}</div>
      <div><b>Closure Type:</b> ${areaData.closureType || 'Unknown'}</div>
      ${areaData.areaSqNm ? `<div><b>Area:</b> ${areaData.areaSqNm} sq nm</div>` : ''}
      ${areaData.fid ? `<div><b>Feature ID:</b> ${areaData.fid}</div>` : ''}
    `;

    document.body.appendChild(popup);

    document.getElementById('close-fishing-closed-area-popup').addEventListener('click', () => {
      popup.remove();
    });

    const closeOnOutsideClick = (e) => {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('click', closeOnOutsideClick);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', closeOnOutsideClick);
    }, 100);
  }

  // ============================================================================
  // AIS VESSEL TRACKING LAYER METHODS
  // ============================================================================

  /**
   * Set AIS vessel data and render the layer
   */
  setAISData(aisData, displayOptions = {}) {
    this.aisVesselsData = aisData || [];
    if (displayOptions.mapStyle) {
      this.aisDisplayOptions.mapStyle = displayOptions.mapStyle;
    }
    console.log(`📍 Set AIS data: ${this.aisVesselsData.length} positions`);
    if (this.activeLayers.has('aisVessels')) {
      this.renderAISVesselsLayer();
    }
  }

  /**
   * Render AIS vessels layer with enhanced visualization
   */
  async renderAISVesselsLayer() {
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      return;
    }

    // Remove existing layer
    this.removeAISVesselsLayer();

    if (!this.aisVesselsData || this.aisVesselsData.length === 0) {
      console.log('⚠️ No AIS data to render');
      return;
    }

    // Backend now handles scallop vessel filtering - use data as-is
    const renderData = this.aisVesselsData;
    const totalPositions = renderData.length;

    // Count unique vessels for UI
    const uniqueVessels = new Set(renderData.map(p => p.mmsi || p.vessel_name)).size;

    console.log(`🚢 Received ${totalPositions} scallop vessel positions (${uniqueVessels} unique vessels)`);

    // Dispatch event with vessel count for UI
    window.dispatchEvent(new CustomEvent('aisFilteredCount', {
      detail: {
        uniqueVessels: uniqueVessels,
        totalPositions: totalPositions,
        originalPositions: totalPositions
      }
    }));

    console.log(`🚢 Rendering AIS vessels layer (${renderData.length} positions, style: ${this.aisDisplayOptions.mapStyle})...`);

    // Create custom data source
    this.aisVesselsDataSource = new window.Cesium.CustomDataSource('AIS_Vessels');
    this.aisVesselsEntities = [];

    // ==================== ENHANCED FISHING CLASSIFICATION ====================
    // Combines: (1) Geographic filtering with actual SAM polygons, (2) Speed analysis, (3) Heading consistency

    // Actual scallop management area polygons from GeoJSON files
    // Georges Bank SAMs
    const georgesBankPolygons = [
      { name: 'NLS-West', coords: [[-68.989,40.341],[-68.985,40.337],[-69.151,40.330],[-69.210,40.434],[-69.358,40.423],[-69.369,40.587],[-69.185,40.628],[-69.0,40.598],[-68.989,40.341]] },
      { name: 'NLS-South', coords: [[-68.620,40.509],[-68.620,40.501],[-68.989,40.516],[-68.989,40.591],[-68.871,40.594],[-68.860,40.643],[-68.856,40.710],[-68.624,40.710],[-68.620,40.509]] },
      { name: 'NLS-North', coords: [[-67.395,40.836],[-67.395,40.970],[-67.262,41.060],[-67.232,41.205],[-66.834,41.205],[-66.661,40.993],[-66.775,40.914],[-66.790,40.836],[-67.395,40.836]] },
      { name: 'CL2-Access', coords: [[-67.395,40.821],[-66.804,40.821],[-67.395,40.568],[-67.395,40.821]] },
      { name: 'CL2-Ext', coords: [[-68.251,40.390],[-68.255,40.613],[-68.033,40.680],[-68.015,40.766],[-67.956,40.766],[-67.893,40.747],[-67.399,40.974],[-67.406,40.561],[-67.708,40.516],[-67.823,40.479],[-68.063,40.505],[-68.247,40.382],[-68.251,40.390]] },
      { name: 'SF', coords: [[-67.391,41.521],[-67.395,41.521],[-67.387,41.677],[-67.262,41.685],[-67.188,41.610],[-67.373,41.495],[-67.391,41.521]] },
      { name: 'CL1-South', coords: [[-68.539,40.777],[-68.638,40.888],[-68.262,41.156],[-68.251,41.026],[-68.277,40.970],[-68.358,40.870],[-68.369,40.806],[-68.531,40.780],[-68.539,40.777]] },
      { name: 'CL1-Access', coords: [[-68.472,40.341],[-68.465,40.349],[-68.458,40.464],[-68.465,40.505],[-68.605,40.516],[-68.605,40.717],[-68.620,40.725],[-68.845,40.728],[-68.819,40.818],[-68.852,40.844],[-69.004,41.205],[-69.137,41.212],[-69.266,41.395],[-69.166,41.387],[-68.948,41.313],[-68.830,41.208],[-68.900,41.194],[-68.443,40.643],[-68.339,40.624],[-68.317,40.583],[-68.266,40.579],[-68.258,40.386],[-68.472,40.341]] },
      { name: 'GSC', coords: [[-68.827,41.194],[-68.808,41.145],[-68.738,41.067],[-68.679,40.985],[-68.365,41.194],[-68.262,41.194],[-68.269,41.164],[-68.624,40.918],[-68.653,40.926],[-68.863,41.179],[-68.827,41.197],[-68.827,41.194]] },
      { name: 'NF', coords: [[-68.247,41.041],[-68.140,41.082],[-68.129,41.223],[-68.199,41.313],[-67.801,41.447],[-67.627,41.562],[-67.480,41.462],[-67.402,41.462],[-67.413,41.670],[-67.768,41.566],[-68.022,41.454],[-68.185,41.376],[-68.351,41.216],[-68.251,41.216],[-68.240,41.045],[-68.247,41.041]] }
    ];

    // Mid-Atlantic SAMs
    const midAtlanticPolygons = [
      { name: 'BI', coords: [[-71.810,40.367],[-71.896,40.500],[-71.757,40.583],[-71.609,40.553],[-71.583,40.621],[-71.400,40.493],[-71.417,40.418],[-71.556,40.440],[-71.810,40.367]] },
      { name: 'ET', coords: [[-73.962,38.094],[-74.380,38.101],[-74.371,38.529],[-74.241,38.642],[-73.561,38.642],[-73.570,38.371],[-73.962,38.094]] },
      { name: 'DMV', coords: [[-74.565,37.320],[-74.530,37.516],[-74.452,37.486],[-73.956,38.071],[-74.574,38.071],[-74.774,37.561],[-74.731,37.463],[-74.792,37.320],[-74.565,37.313],[-74.565,37.320]] },
      { name: 'HCS', coords: [[-73.643,38.667],[-73.187,39.202],[-72.630,39.194],[-73.535,38.421],[-73.535,38.661],[-73.643,38.667]] },
      { name: 'NYB-1', coords: [[-73.433,38.377],[-72.955,38.790],[-72.772,39.030],[-73.512,38.414],[-73.433,38.362],[-73.433,38.377]] },
      { name: 'NYB-2', coords: [[-73.907,39.664],[-73.916,39.972],[-73.872,39.957],[-73.690,39.874],[-72.619,39.221],[-73.246,39.228],[-73.907,39.664]] },
      { name: 'LI', coords: [[-72.744,39.307],[-72.718,39.382],[-72.578,39.442],[-72.543,39.532],[-72.456,39.585],[-72.456,39.682],[-72.326,39.712],[-72.169,39.697],[-71.995,39.847],[-71.864,39.907],[-71.812,40.072],[-71.621,40.148],[-71.951,40.485],[-72.961,40.223],[-73.118,40.095],[-73.466,40.012],[-73.675,39.885],[-72.744,39.314],[-72.744,39.307]] }
    ];

    // Combine all scallop area polygons
    const allScallopPolygons = [...georgesBankPolygons, ...midAtlanticPolygons];

    // Ray casting algorithm for point-in-polygon test
    const pointInPolygon = (lat, lon, polygon) => {
      let inside = false;
      const coords = polygon.coords;
      for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
        const xi = coords[i][0], yi = coords[i][1];
        const xj = coords[j][0], yj = coords[j][1];

        const intersect = ((yi > lat) !== (yj > lat)) &&
          (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    };

    // Approximate US East Coast line for offshore distance check
    // These are rough coastal reference points from Maine to Virginia
    const coastlinePoints = [
      [-70.0, 43.5],  // Southern Maine
      [-70.8, 42.3],  // Cape Ann, MA
      [-70.0, 41.7],  // Cape Cod tip
      [-71.3, 41.5],  // Rhode Island
      [-72.0, 41.0],  // Connecticut
      [-73.8, 40.6],  // Long Island
      [-74.0, 40.4],  // New Jersey north
      [-74.2, 39.4],  // New Jersey south
      [-75.0, 38.5],  // Delaware Bay
      [-75.5, 37.5],  // Virginia
      [-76.0, 36.5],  // Cape Hatteras area
    ];

    // Calculate minimum distance to coastline (approximate)
    const distanceToCoast = (lat, lon) => {
      let minDist = Infinity;
      for (const [coastLon, coastLat] of coastlinePoints) {
        // Simple Euclidean distance (good enough for rough filtering)
        const dist = Math.sqrt(Math.pow(lat - coastLat, 2) + Math.pow(lon - coastLon, 2));
        if (dist < minDist) minDist = dist;
      }
      // Convert to approximate nautical miles (1 degree ≈ 60 nm)
      return minDist * 60;
    };

    // Build heading consistency map for each vessel
    // Group positions by vessel and analyze heading stability
    const vesselHeadingData = {};
    renderData.forEach(p => {
      if (!p.mmsi) return;
      if (!vesselHeadingData[p.mmsi]) {
        vesselHeadingData[p.mmsi] = [];
      }
      vesselHeadingData[p.mmsi].push({
        timestamp: p.timestamp || p.time,
        cog: p.cog || p.course || 0,
        sog: p.sog || 0,
        lat: p.lat,
        lon: p.lon
      });
    });

    // Analyze heading consistency for each vessel's positions
    // Scallop dredging involves maintaining consistent heading during tows
    const headingConsistency = {};
    Object.entries(vesselHeadingData).forEach(([mmsi, positions]) => {
      // Sort by timestamp
      positions.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // For each position, check heading consistency with nearby positions
      positions.forEach((pos, idx) => {
        const posKey = `${mmsi}-${pos.lat}-${pos.lon}`;

        // Look at surrounding positions (window of 5)
        const windowSize = 5;
        const startIdx = Math.max(0, idx - Math.floor(windowSize / 2));
        const endIdx = Math.min(positions.length - 1, idx + Math.floor(windowSize / 2));

        const headings = [];
        for (let i = startIdx; i <= endIdx; i++) {
          if (positions[i].sog >= 2.0 && positions[i].sog <= 4.5) {
            headings.push(positions[i].cog);
          }
        }

        // Calculate heading variance (lower = more consistent = likely dredging)
        if (headings.length >= 3) {
          // Handle circular nature of headings (0-360)
          const avgHeading = headings.reduce((a, b) => a + b, 0) / headings.length;
          const variance = headings.reduce((sum, h) => {
            let diff = Math.abs(h - avgHeading);
            if (diff > 180) diff = 360 - diff; // Handle wraparound
            return sum + diff * diff;
          }, 0) / headings.length;

          // Low variance (< 400 = ~20 degree std dev) indicates consistent heading
          headingConsistency[posKey] = variance < 400;
        } else {
          headingConsistency[posKey] = false;
        }
      });
    });

    // Enhanced classification function
    const classifyPosition = (p) => {
      const sog = p.sog || 0;
      const atSea = p.at_sea == 1 || p.at_sea === true;
      const lat = p.lat;
      const lon = p.lon;

      // If not at sea, it's in port
      if (!atSea) return 'inPort';

      // Check if far enough offshore (at least 15 nautical miles)
      // This is required for ANY fishing classification
      const distNM = distanceToCoast(lat, lon);
      const isOffshore = distNM >= 15;

      // Can't be fishing if too close to coast
      if (!isOffshore) return 'transit';

      // Check if in dredging speed range (2.5-3.8 knots)
      const inDredgingSpeed = sog >= 2.5 && sog <= 3.8;

      // Check if in known scallop ground (already has offshore check, but we've verified above)
      const inFishingGround = allScallopPolygons.some(polygon => pointInPolygon(lat, lon, polygon));

      // Check heading consistency
      const posKey = `${p.mmsi}-${lat}-${lon}`;
      const hasConsistentHeading = headingConsistency[posKey] || false;

      // FISHING classification requires:
      // 1. At least 15nm offshore (checked above) AND
      // 2. In dredging speed range AND
      // 3. Either in known scallop ground OR has consistent heading pattern
      if (inDredgingSpeed && (inFishingGround || hasConsistentHeading)) {
        return 'fishing';
      }

      // Everything else at sea is transit
      return 'transit';
    };

    // Detailed logging for debugging
    const fishingCount = renderData.filter(p => classifyPosition(p) === 'fishing').length;
    const transitCount = renderData.filter(p => classifyPosition(p) === 'transit').length;
    const inPortCount = renderData.filter(p => classifyPosition(p) === 'inPort').length;
    const totalCount = renderData.length;

    // Log speed distribution for debugging (matches tight 2.5-3.8 kn fishing band)
    const speeds = renderData.map(p => p.sog || 0);
    const avgSpeed = speeds.length > 0 ? (speeds.reduce((a, b) => a + b, 0) / speeds.length).toFixed(2) : 0;
    const slowCount = speeds.filter(s => s < 2.5).length;
    const fishingSpeedCount = speeds.filter(s => s >= 2.5 && s <= 3.8).length;
    const fastCount = speeds.filter(s => s > 3.8).length;

    // Geographic breakdown with detailed stats
    const offshoreCount = renderData.filter(p => distanceToCoast(p.lat, p.lon) >= 15).length;
    const inPolygonCount = renderData.filter(p => {
      const distNM = distanceToCoast(p.lat, p.lon);
      if (distNM < 15) return false;
      return allScallopPolygons.some(polygon => pointInPolygon(p.lat, p.lon, polygon));
    }).length;
    const consistentHeadingCount = Object.values(headingConsistency).filter(v => v === true).length;

    console.log(`📊 AIS Data breakdown (${totalCount} total):`);
    console.log(`   🎣 Fishing: ${fishingCount} (${((fishingCount/totalCount)*100).toFixed(1)}%)`);
    console.log(`   🚢 Transit: ${transitCount} (${((transitCount/totalCount)*100).toFixed(1)}%)`);
    console.log(`   ⚓ In Port: ${inPortCount} (${((inPortCount/totalCount)*100).toFixed(1)}%)`);
    console.log(`   📈 Speed bands: <2.5kn: ${slowCount}, 2.5-3.8kn: ${fishingSpeedCount}, >3.8kn: ${fastCount}, avg: ${avgSpeed}kn`);
    console.log(`   🌊 Offshore (>15nm): ${offshoreCount} (${((offshoreCount/totalCount)*100).toFixed(1)}%)`);
    console.log(`   🗺️ In SAM polygons: ${inPolygonCount} (${((inPolygonCount/totalCount)*100).toFixed(1)}%)`);
    console.log(`   🧭 Consistent heading: ${consistentHeadingCount} positions`);

    // Debug: Log first 5 data points to check field names
    if (renderData.length > 0) {
      console.log('🔍 Sample data point fields:', Object.keys(renderData[0]));
      console.log('🔍 First 5 scallop vessel positions:');
      renderData.slice(0, 5).forEach((p, i) => {
        console.log(`   ${i}: sog=${p.sog}, speed=${p.speed}, at_sea=${p.at_sea}, likely_dredging=${p.likely_dredging}`);
      });
    }

    // Render based on map style
    if (this.aisDisplayOptions.mapStyle === 'scatterPoints') {
      // Elegant scatter points - small and smooth for density visualization

      // Bright neon colors
      // Point colors - no glow for better performance
      const pointColors = {
        fishing: '#FF69B4',    // Bright neon pink for fishing
        transit: '#A855F7',    // Bright neon purple for transit
        inPort: '#FDE68A'      // Pastel yellow for port
      };

      // Batch rendering to prevent UI freeze
      const BATCH_SIZE = 2000;
      const batches = [];
      for (let i = 0; i < renderData.length; i += BATCH_SIZE) {
        batches.push(renderData.slice(i, i + BATCH_SIZE));
      }

      console.log(`📦 Rendering ${renderData.length} points in ${batches.length} batches`);

      const processBatch = (batchIndex, startIndex) => {
        if (batchIndex >= batches.length) {
          console.log(`✅ Finished rendering all ${renderData.length} AIS points`);
          return;
        }

        const batch = batches[batchIndex];
        batch.forEach((position, i) => {
          try {
            if (!position.lon || !position.lat) return;

            const classification = classifyPosition(position);
            const isFishing = classification === 'fishing';
            const isTransit = classification === 'transit';

            // Simple small points - no outline for better performance
            const baseSize = isFishing ? 4 : (isTransit ? 3 : 2.5);
            const color = isFishing ? pointColors.fishing :
                          isTransit ? pointColors.transit : pointColors.inPort;

            const entity = this.aisVesselsDataSource.entities.add({
              id: `ais-position-${startIndex + i}`,
              name: position.vessel_name || `Vessel ${position.mmsi}`,
              position: window.Cesium.Cartesian3.fromDegrees(position.lon, position.lat, 0),
              point: {
                pixelSize: baseSize,
                color: window.Cesium.Color.fromCssColorString(color).withAlpha(0.85),
                heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
              },
              properties: {
                type: 'aisVessel',
                vesselData: position
              }
            });
            this.aisVesselsEntities.push(entity);
          } catch (error) {
            // Silent fail for individual points
          }
        });

        // Process next batch after a small delay to keep UI responsive
        setTimeout(() => processBatch(batchIndex + 1, startIndex + batch.length), 10);
      };

      // Start batch processing
      processBatch(0, 0);

    } else if (this.aisDisplayOptions.mapStyle === 'trajectoryLines') {
      // Trajectory lines - clean, uniform thin lines
      const lineColor = '#F4D4CB';  // Light peach
      const lineWidth = 1;          // Uniform thin lines
      const lineOpacity = 0.85;

      // Group positions by vessel
      const vesselGroups = {};
      renderData.forEach(position => {
        if (!position.mmsi) return;
        if (!vesselGroups[position.mmsi]) {
          vesselGroups[position.mmsi] = [];
        }
        vesselGroups[position.mmsi].push(position);
      });

      Object.entries(vesselGroups).forEach(([mmsi, positions]) => {
        // Sort by time for proper line connection
        positions.sort((a, b) => new Date(a.hour) - new Date(b.hour));
        if (positions.length < 2) return;

        // Build one continuous line per vessel
        const coords = [];
        positions.forEach(p => {
          if (p.lon && p.lat) {
            coords.push(p.lon, p.lat);
          }
        });

        if (coords.length >= 4) { // At least 2 points
          this.aisVesselsDataSource.entities.add({
            id: `ais-track-${mmsi}`,
            polyline: {
              positions: window.Cesium.Cartesian3.fromDegreesArray(coords),
              width: lineWidth,
              material: window.Cesium.Color.fromCssColorString(lineColor).withAlpha(lineOpacity),
              clampToGround: true
            }
          });
        }
      });

      // Log trajectory stats
      console.log(`🛤️ Trajectory visualization: ${Object.keys(vesselGroups).length} vessels rendered (clean lines, no markers)`);

    } else if (this.aisDisplayOptions.mapStyle === 'heatMap') {
      // Professional heatmap with Plasma colormap and percentile-based scaling
      const baseGridSize = 0.04; // Slightly finer grid for better detail
      const grid = {};

      // Build density grid with Gaussian kernel for smooth blending
      renderData.forEach(position => {
        if (!position.lon || !position.lat) return;

        // 5x5 kernel for smooth appearance
        for (let dx = -2; dx <= 2; dx++) {
          for (let dy = -2; dy <= 2; dy++) {
            const gridX = Math.floor(position.lon / baseGridSize) + dx;
            const gridY = Math.floor(position.lat / baseGridSize) + dy;
            const key = `${gridX},${gridY}`;

            // Gaussian weight based on distance
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 2.5) continue;
            const weight = Math.exp(-dist * dist / 1.8); // Tighter kernel

            if (!grid[key]) {
              grid[key] = {
                count: 0,
                lat: gridY * baseGridSize + baseGridSize / 2,
                lon: gridX * baseGridSize + baseGridSize / 2,
                gridX,
                gridY
              };
            }
            grid[key].count += weight;
          }
        }
      });

      // Filter out very low density cells and prepare for percentile calculation
      const cells = Object.values(grid).filter(cell => cell.count > 0.3);

      if (cells.length === 0) {
        console.log('⚠️ No cells with sufficient density for heatmap');
        return;
      }

      // Sort by count to compute percentiles
      const sortedByCount = [...cells].sort((a, b) => a.count - b.count);
      const totalCells = sortedByCount.length;

      // Create a map of count -> percentile rank
      const countToPercentile = new Map();
      sortedByCount.forEach((cell, index) => {
        const percentile = index / (totalCells - 1 || 1);
        countToPercentile.set(cell.count, percentile);
      });

      // Plasma colormap: dark purple → magenta/pink → orange → bright yellow
      // This is a perceptually uniform colormap with excellent contrast
      const getPlasmaColor = (t) => {
        // t is 0-1 (percentile)
        let r, g, b;

        if (t < 0.13) {
          // Dark purple/indigo (0.05, 0.03, 0.53) → (0.14, 0.04, 0.56)
          const s = t / 0.13;
          r = 13 + s * 23;
          g = 8 + s * 2;
          b = 135 + s * 8;
        } else if (t < 0.25) {
          // Deep purple (0.14, 0.04, 0.56) → (0.29, 0.00, 0.63)
          const s = (t - 0.13) / 0.12;
          r = 36 + s * 38;
          g = 10 - s * 10;
          b = 143 + s * 18;
        } else if (t < 0.38) {
          // Purple to magenta (0.29, 0.00, 0.63) → (0.50, 0.13, 0.53)
          const s = (t - 0.25) / 0.13;
          r = 74 + s * 54;
          g = 0 + s * 33;
          b = 161 - s * 26;
        } else if (t < 0.5) {
          // Magenta to pink/red (0.50, 0.13, 0.53) → (0.72, 0.24, 0.36)
          const s = (t - 0.38) / 0.12;
          r = 128 + s * 56;
          g = 33 + s * 28;
          b = 135 - s * 43;
        } else if (t < 0.63) {
          // Pink/red to orange (0.72, 0.24, 0.36) → (0.89, 0.36, 0.19)
          const s = (t - 0.5) / 0.13;
          r = 184 + s * 43;
          g = 61 + s * 31;
          b = 92 - s * 43;
        } else if (t < 0.75) {
          // Orange (0.89, 0.36, 0.19) → (0.96, 0.52, 0.10)
          const s = (t - 0.63) / 0.12;
          r = 227 + s * 18;
          g = 92 + s * 41;
          b = 49 - s * 23;
        } else if (t < 0.88) {
          // Orange to yellow-orange (0.96, 0.52, 0.10) → (0.99, 0.71, 0.15)
          const s = (t - 0.75) / 0.13;
          r = 245 + s * 7;
          g = 133 + s * 48;
          b = 26 + s * 12;
        } else {
          // Yellow-orange to bright yellow (0.99, 0.71, 0.15) → (0.94, 0.98, 0.13)
          const s = (t - 0.88) / 0.12;
          r = 252 - s * 12;
          g = 181 + s * 69;
          b = 38 - s * 5;
        }

        // Opacity: very transparent so base map is clearly visible
        const alpha = 0.12 + t * 0.28; // Range: 0.12 to 0.4

        return window.Cesium.Color.fromBytes(
          Math.round(Math.min(255, Math.max(0, r))),
          Math.round(Math.min(255, Math.max(0, g))),
          Math.round(Math.min(255, Math.max(0, b))),
          Math.round(alpha * 255)
        );
      };

      // Sort cells by count (low to high) so high-density renders on top
      const sortedCells = [...cells].sort((a, b) => a.count - b.count);

      // Only show cells above 60th percentile (top 40% of activity)
      const minPercentileThreshold = 0.6;

      // Render as overlapping ellipses - only high activity zones
      sortedCells.forEach((cell, index) => {
        // Get percentile rank for this cell's count
        const rawPercentile = countToPercentile.get(cell.count) || 0;

        // Skip cells below threshold - don't show low activity areas
        if (rawPercentile < minPercentileThreshold) return;

        // Remap percentile: 60th→0, 100th→1 for color mapping
        const remappedPercentile = (rawPercentile - minPercentileThreshold) / (1 - minPercentileThreshold);

        // Apply slight power transform for even more contrast at the top
        const percentile = Math.pow(remappedPercentile, 1.5);

        const color = getPlasmaColor(percentile);

        // Size varies with remapped percentile
        const size = baseGridSize * (0.85 + remappedPercentile * 0.5);
        const sizeMeters = size * 111000;

        const entity = this.aisVesselsDataSource.entities.add({
          id: `ais-heat-${index}`,
          position: window.Cesium.Cartesian3.fromDegrees(cell.lon, cell.lat, 0),
          ellipse: {
            semiMajorAxis: sizeMeters * 0.7,
            semiMinorAxis: sizeMeters * 0.7,
            material: color,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            outline: false
          },
          properties: {
            type: 'aisHeatCell',
            count: cell.count,
            percentile: Math.round(rawPercentile * 100)
          }
        });
        this.aisVesselsEntities.push(entity);
      });

      // Add star markers for top 3 hot zones - one per management area only
      // Group cells by which SAM polygon they belong to
      const zonesBySAM = {};
      sortedCells.forEach(cell => {
        const sam = allScallopPolygons.find(polygon => pointInPolygon(cell.lat, cell.lon, polygon));
        if (sam) {
          const samName = sam.name;
          // Keep track of highest density cell per SAM
          if (!zonesBySAM[samName] || cell.count > zonesBySAM[samName].count) {
            zonesBySAM[samName] = { ...cell, samName };
          }
        }
      });

      // Get the top zone from each SAM, sort by count, take top 3
      const top3HotZones = Object.values(zonesBySAM)
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

      // Add star markers for each top hot zone
      top3HotZones.forEach((zone, idx) => {
        const starEntity = this.aisVesselsDataSource.entities.add({
          id: `ais-hotzone-star-${idx}`,
          position: window.Cesium.Cartesian3.fromDegrees(zone.lon, zone.lat, 100),
          label: {
            text: '★',
            font: '12px sans-serif',
            fillColor: window.Cesium.Color.fromCssColorString('#FFD700'), // Gold color
            outlineColor: window.Cesium.Color.BLACK,
            outlineWidth: 1,
            style: window.Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: window.Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: window.Cesium.HorizontalOrigin.CENTER,
            heightReference: window.Cesium.HeightReference.RELATIVE_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
          },
          properties: {
            type: 'aisHotZoneStar',
            count: zone.count,
            rank: idx + 1,
            samName: zone.samName
          }
        });
        this.aisVesselsEntities.push(starEntity);
      });

      const samNames = top3HotZones.map(z => z.samName).join(', ');
      console.log(`⭐ Added ${top3HotZones.length} hot zone stars (one per SAM): ${samNames}`);

    } else if (this.aisDisplayOptions.mapStyle === 'animatedTrajectory') {
      // Animated trajectory - lines draw progressively like story maps
      // Colors inspired by deep ocean theme with glowing warm highlights
      const colors = {
        fishing: '#FFB347',    // Warm orange/amber glow for fishing
        transit: '#4ECDC4',     // Teal/cyan for transit
        inPort: '#95A5A6',      // Muted gray for port
        vesselMarker: '#FF6B6B', // Coral red for vessel position
        trail: '#FFE66D'        // Golden yellow for trail glow
      };

      // Group positions by vessel
      const vesselGroups = {};
      renderData.forEach(position => {
        if (!position.mmsi) return;
        if (!vesselGroups[position.mmsi]) {
          vesselGroups[position.mmsi] = [];
        }
        vesselGroups[position.mmsi].push(position);
      });

      // Enhanced classification (uses same logic as classifyPosition)
      const classifySegment = (p) => {
        const sog = p.sog || 0;
        const atSea = p.at_sea == 1 || p.at_sea === true;
        const lat = p.lat;
        const lon = p.lon;

        if (!atSea) return 'inPort';

        // Check if far enough offshore (at least 15 nautical miles)
        const distNM = distanceToCoast(lat, lon);
        if (distNM < 15) return 'transit';

        // Check if in dredging speed range AND (in scallop ground OR consistent heading)
        const inDredgingSpeed = sog >= 2.5 && sog <= 3.8;
        const inFishingGround = allScallopPolygons.some(polygon => pointInPolygon(lat, lon, polygon));
        const posKey = `${p.mmsi}-${lat}-${lon}`;
        const hasConsistentHeading = headingConsistency[posKey] || false;

        if (inDredgingSpeed && (inFishingGround || hasConsistentHeading)) {
          return 'fishing';
        }
        return 'transit';
      };

      // Reset animation state
      this.aisAnimationState.vesselPaths = {};
      let maxPositions = 0;

      Object.entries(vesselGroups).forEach(([mmsi, positions]) => {
        // Sort by time
        positions.sort((a, b) => new Date(a.hour) - new Date(b.hour));
        if (positions.length < 2) return;

        maxPositions = Math.max(maxPositions, positions.length);

        // Track entities for this vessel
        const vesselEntities = {
          segments: [],
          trailPoints: [],
          vesselMarker: null
        };

        // Create all line segments (initially hidden)
        for (let i = 0; i < positions.length - 1; i++) {
          const p1 = positions[i];
          const p2 = positions[i + 1];
          const activity = classifySegment(p1);
          const isFishing = activity === 'fishing';
          const segmentColor = isFishing ? colors.fishing : colors.transit;

          const segmentId = `ais-anim-${mmsi}-seg-${i}`;
          this.aisVesselsDataSource.entities.add({
            id: segmentId,
            polyline: {
              positions: window.Cesium.Cartesian3.fromDegreesArray([p1.lon, p1.lat, p2.lon, p2.lat]),
              width: isFishing ? 4 : 2.5,
              material: new window.Cesium.PolylineGlowMaterialProperty({
                glowPower: 0.25,
                color: window.Cesium.Color.fromCssColorString(segmentColor).withAlpha(0.9)
              }),
              clampToGround: true,
              show: false // Initially hidden
            }
          });
          vesselEntities.segments.push(segmentId);
        }

        // Create trail points (firefly effect - initially hidden)
        positions.forEach((pos, idx) => {
          const activity = classifySegment(pos);
          const isFishing = activity === 'fishing';
          const pointId = `ais-anim-${mmsi}-pt-${idx}`;

          this.aisVesselsDataSource.entities.add({
            id: pointId,
            position: window.Cesium.Cartesian3.fromDegrees(pos.lon, pos.lat, 0),
            point: {
              pixelSize: isFishing ? 5 : 3,
              color: window.Cesium.Color.fromCssColorString(isFishing ? colors.fishing : colors.transit).withAlpha(0.7),
              outlineColor: window.Cesium.Color.WHITE.withAlpha(0.3),
              outlineWidth: 1,
              heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              show: false // Initially hidden
            }
          });
          vesselEntities.trailPoints.push(pointId);
        });

        // Create vessel marker (the "head" that moves along the path)
        const firstPos = positions[0];
        const vesselMarkerId = `ais-anim-${mmsi}-vessel`;
        const entity = this.aisVesselsDataSource.entities.add({
          id: vesselMarkerId,
          name: firstPos.vessel_name || `Vessel ${mmsi}`,
          position: window.Cesium.Cartesian3.fromDegrees(firstPos.lon, firstPos.lat, 0),
          point: {
            pixelSize: 12,
            color: window.Cesium.Color.fromCssColorString(colors.vesselMarker),
            outlineColor: window.Cesium.Color.WHITE,
            outlineWidth: 2,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            show: false // Initially hidden
          },
          properties: {
            type: 'aisAnimatedVessel',
            vesselData: firstPos
          }
        });
        vesselEntities.vesselMarker = vesselMarkerId;
        this.aisVesselsEntities.push(entity);

        // Store path data for animation
        this.aisAnimationState.vesselPaths[mmsi] = {
          positions,
          entities: vesselEntities
        };
      });

      // Set animation frame count (based on longest path, with some smoothing)
      this.aisAnimationState.totalFrames = maxPositions * 3; // 3 frames per position for smooth animation
      this.aisAnimationState.currentFrame = 0;

      console.log(`🎬 Animated trajectory ready: ${Object.keys(vesselGroups).length} vessels, ${this.aisAnimationState.totalFrames} frames`);

      // Dispatch event to notify UI that animation is ready
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('aisAnimationReady', {
          detail: {
            vesselCount: Object.keys(vesselGroups).length,
            totalFrames: this.aisAnimationState.totalFrames
          }
        }));
      }
    }

    // Add to viewer
    try {
      await this.viewer.dataSources.add(this.aisVesselsDataSource);
      console.log(`✅ Rendered ${this.aisVesselsEntities.length} AIS entities`);

      if (this.viewer.scene) {
        this.viewer.scene.requestRender();
      }
    } catch (error) {
      console.error('❌ Error adding AIS data source to viewer:', error);
    }
  }

  /**
   * Remove AIS vessels layer
   */
  removeAISVesselsLayer() {
    // Stop any running animation
    this.stopAISAnimation();

    if (this.aisVesselsDataSource && this.viewer && !this.viewer.isDestroyed()) {
      if (this.viewer.dataSources.contains(this.aisVesselsDataSource)) {
        this.viewer.dataSources.remove(this.aisVesselsDataSource);
      }
      this.aisVesselsDataSource = null;
      this.aisVesselsEntities = [];
    }
  }

  /**
   * Start AIS trajectory animation playback
   */
  startAISAnimation() {
    if (this.aisAnimationState.isPlaying) return;
    if (!this.aisAnimationState.vesselPaths || Object.keys(this.aisAnimationState.vesselPaths).length === 0) {
      console.log('⚠️ No vessel paths to animate');
      return;
    }

    this.aisAnimationState.isPlaying = true;
    console.log('▶️ Starting AIS animation playback');

    const animate = () => {
      if (!this.aisAnimationState.isPlaying) return;

      const { currentFrame, totalFrames, animationSpeed } = this.aisAnimationState;

      if (currentFrame < totalFrames) {
        this.updateAnimationFrame(currentFrame);
        this.aisAnimationState.currentFrame += animationSpeed;
        this.aisAnimationState.animationId = requestAnimationFrame(animate);
      } else {
        // Animation complete
        this.aisAnimationState.isPlaying = false;
        this.aisAnimationState.currentFrame = totalFrames;
        console.log('✓ AIS animation complete');

        // Dispatch event to notify UI
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('aisAnimationComplete'));
        }
      }
    };

    this.aisAnimationState.animationId = requestAnimationFrame(animate);
  }

  /**
   * Pause AIS trajectory animation
   */
  pauseAISAnimation() {
    this.aisAnimationState.isPlaying = false;
    if (this.aisAnimationState.animationId) {
      cancelAnimationFrame(this.aisAnimationState.animationId);
      this.aisAnimationState.animationId = null;
    }
    console.log('⏸️ AIS animation paused');
  }

  /**
   * Stop and reset AIS animation
   */
  stopAISAnimation() {
    this.pauseAISAnimation();
    this.aisAnimationState.currentFrame = 0;
    this.aisAnimationState.isPlaying = false;
  }

  /**
   * Seek to specific frame in animation
   */
  seekAISAnimation(frameNumber) {
    const wasPlaying = this.aisAnimationState.isPlaying;
    this.pauseAISAnimation();

    this.aisAnimationState.currentFrame = Math.min(
      Math.max(0, frameNumber),
      this.aisAnimationState.totalFrames
    );

    this.updateAnimationFrame(this.aisAnimationState.currentFrame);

    if (wasPlaying) {
      this.startAISAnimation();
    }
  }

  /**
   * Set animation playback speed
   */
  setAISAnimationSpeed(speed) {
    this.aisAnimationState.animationSpeed = speed;
  }

  /**
   * Update the visualization for a specific animation frame
   */
  updateAnimationFrame(frame) {
    if (!this.aisVesselsDataSource || !this.viewer || this.viewer.isDestroyed()) return;

    const progress = frame / this.aisAnimationState.totalFrames;

    // Update each vessel's trajectory visualization
    Object.entries(this.aisAnimationState.vesselPaths).forEach(([_mmsi, pathData]) => {
      const { positions, entities } = pathData;
      const visibleCount = Math.floor(positions.length * progress);

      // Update line segments visibility/opacity
      entities.segments.forEach((segmentId, idx) => {
        const entity = this.aisVesselsDataSource.entities.getById(segmentId);
        if (entity && entity.polyline) {
          const isVisible = idx < visibleCount - 1;
          entity.polyline.show = isVisible;
        }
      });

      // Update vessel marker position (the "head" of the animation)
      if (entities.vesselMarker && visibleCount > 0 && visibleCount <= positions.length) {
        const markerEntity = this.aisVesselsDataSource.entities.getById(entities.vesselMarker);
        if (markerEntity) {
          const currentPos = positions[Math.min(visibleCount - 1, positions.length - 1)];
          markerEntity.position = window.Cesium.Cartesian3.fromDegrees(currentPos.lon, currentPos.lat, 0);
          markerEntity.show = true;
        }
      }

      // Update trail points visibility
      entities.trailPoints.forEach((pointId, idx) => {
        const entity = this.aisVesselsDataSource.entities.getById(pointId);
        if (entity) {
          entity.show = idx < visibleCount;
        }
      });
    });

    // Dispatch progress event for UI
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('aisAnimationProgress', {
        detail: { frame, totalFrames: this.aisAnimationState.totalFrames, progress }
      }));
    }
  }

  /**
   * Get current animation state
   */
  getAISAnimationState() {
    return { ...this.aisAnimationState };
  }

  /**
   * Show popup for AIS vessel
   */
  showAISVesselPopup(vesselData, screenPosition) {
    // Remove existing popup if any
    const existingPopup = document.getElementById('ais-vessel-popup');
    if (existingPopup) {
      existingPopup.remove();
    }

    // Convert Cesium screen coordinates to viewport coordinates
    const canvas = this.viewer.scene.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const viewportX = canvasRect.left + screenPosition.x;
    const viewportY = canvasRect.top + screenPosition.y;

    // Create popup element
    const popup = document.createElement('div');
    popup.id = 'ais-vessel-popup';
    popup.style.cssText = `
      position: fixed;
      left: ${viewportX + 15}px;
      top: ${viewportY + 5}px;
      background: #1a1a2e;
      border: 1px solid #3d3d5c;
      border-radius: 8px;
      padding: 12px;
      z-index: 10000;
      pointer-events: auto;
      min-width: 220px;
      max-width: 280px;
      font-size: 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      color: #e5e7eb;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const statusText = vesselData.likely_dredging === 1 ? 'Fishing (Dredging)' :
                       vesselData.at_sea === 1 ? 'Transiting' : 'In Port';
    const statusColor = vesselData.likely_dredging === 1 ? '#ec4899' :
                        vesselData.at_sea === 1 ? '#8b5cf6' : '#6b7280';

    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px;">
        <div>
          <h4 style="margin: 0 0 4px; font-size: 14px; font-weight: 600; color: #fff;">${vesselData.vessel_name || 'Unknown Vessel'}</h4>
          <div style="font-size: 11px; color: #9ca3af;">MMSI: ${vesselData.mmsi || 'N/A'}</div>
        </div>
        <button id="close-ais-vessel-popup" style="
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #9ca3af;
          padding: 0;
          line-height: 1;
        ">×</button>
      </div>

      <div style="display: flex; align-items: center; margin-bottom: 12px; padding: 8px; background: rgba(0,0,0,0.2); border-radius: 4px;">
        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${statusColor}; margin-right: 8px;"></span>
        <span style="font-weight: 500;">${statusText}</span>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 11px;">
        <div>
          <span style="color: #9ca3af;">Speed:</span>
          <div style="font-weight: 500;">${vesselData.sog?.toFixed(1) || '0.0'} kts</div>
        </div>
        <div>
          <span style="color: #9ca3af;">Distance to Port:</span>
          <div style="font-weight: 500;">${vesselData.dist_to_port_nm?.toFixed(1) || 'N/A'} nm</div>
        </div>
        <div>
          <span style="color: #9ca3af;">Nearest Port:</span>
          <div style="font-weight: 500;">${vesselData.nearest_port || 'N/A'}</div>
        </div>
        <div>
          <span style="color: #9ca3af;">Time:</span>
          <div style="font-weight: 500;">${vesselData.hour ? new Date(vesselData.hour).toLocaleString() : 'N/A'}</div>
        </div>
      </div>

      <div style="margin-top: 12px; padding-top: 10px; border-top: 1px solid #3d3d5c; font-size: 10px; color: #6b7280;">
        Lat: ${vesselData.lat?.toFixed(4) || 'N/A'}, Lon: ${vesselData.lon?.toFixed(4) || 'N/A'}
      </div>
    `;

    document.body.appendChild(popup);

    // Add close button handler
    document.getElementById('close-ais-vessel-popup').addEventListener('click', () => {
      popup.remove();
    });

    // Close popup when clicking outside
    const closeOnOutsideClick = (e) => {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('click', closeOnOutsideClick);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', closeOnOutsideClick);
    }, 100);
  }

  /**
   * Remove state boundaries data source only (without removing from activeLayers)
   * Used when re-rendering to clean up old data sources
   */
  removeStateBoundariesLayerDataOnly() {
    let removedCount = 0;
    
    // Remove tracked data source
    if (this.stateBoundariesDataSource && this.viewer && !this.viewer.isDestroyed()) {
      if (this.viewer.dataSources.contains(this.stateBoundariesDataSource)) {
        this.viewer.dataSources.remove(this.stateBoundariesDataSource, true);
        removedCount++;
      }
      this.stateBoundariesDataSource = null;
    }
    
    // Clear entities array
    this.stateBoundariesEntities = [];
    
    // Also search for any remaining state boundaries data sources
    try {
      const dataSources = this.viewer.dataSources;
      for (let i = dataSources.length - 1; i >= 0; i--) {
        const ds = dataSources.get(i);
        
        if (ds.constructor.name === 'CustomDataSource' && ds.entities && ds.entities.values.length > 0) {
          // Check a sample of entities for state boundary characteristics
          const sampleSize = Math.min(5, ds.entities.values.length);
          let matchCount = 0;
          
          for (let j = 0; j < sampleSize; j++) {
            const entity = ds.entities.values[j];
            const props = entity.properties;
            const dataType = this.getPropValue(props, 'dataType');
            const stateName = this.getPropValue(props, 'stateName');
            const entityId = entity.id || '';
            
            // State boundaries have specific characteristics
            if (dataType === 'stateBoundary' || dataType === 'stateLabel' || 
                !!stateName || entityId.includes('state-boundary')) {
              matchCount++;
            }
          }
          
          if (matchCount > 0) {
            console.log(`🧹 Removing duplicate state boundaries data source (${ds.entities.values.length} entities)`);
            this.viewer.dataSources.remove(ds, true);
            removedCount++;
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Error searching for state boundaries data sources:', error);
    }
    
    if (removedCount === 0) {
      console.log('ℹ️ No state boundaries data source found to remove');
    } else {
      console.log(`✅ Removed ${removedCount} state boundaries data source(s)`);
    }
  }
  
  /**
   * Remove state boundaries layer (removes from activeLayers and data sources)
   */
  removeStateBoundariesLayer() {
    if (!this.viewer || this.viewer.isDestroyed()) {
      return;
    }
    
    let removedCount = 0;
    
    // First, try to remove using tracked reference
    if (this.stateBoundariesDataSource) {
      try {
        if (this.viewer.dataSources.contains(this.stateBoundariesDataSource)) {
          this.viewer.dataSources.remove(this.stateBoundariesDataSource, true);
          console.log('✅ Removed state boundaries data source from viewer (via tracked reference)');
          removedCount++;
        }
      } catch (error) {
        console.warn('⚠️ Error removing state boundaries data source (via tracked reference):', error);
      }
      this.stateBoundariesDataSource = null;
    }
    
    // Also search through all data sources to find any state boundaries data sources
    // This handles cases where the reference might be lost
    try {
      const dataSources = this.viewer.dataSources;
      for (let i = dataSources.length - 1; i >= 0; i--) {
        const dataSource = dataSources.get(i);
        if (!dataSource) continue;
        
        // Check if this is a state boundaries data source by:
        // 1. Name matches
        // 2. Entity IDs contain state boundary identifiers
        // 3. Entity properties contain stateBoundary or stateLabel dataType
        let isStateBoundaries = false;
        
        if (dataSource.name === 'stateBoundaries') {
          isStateBoundaries = true;
        } else if (dataSource.entities && dataSource.entities.values.length > 0) {
          // Check a sample of entities for state boundary characteristics
          const sampleSize = Math.min(10, dataSource.entities.values.length);
          let matchCount = 0;
          
          for (let j = 0; j < sampleSize; j++) {
            const entity = dataSource.entities.values[j];
            const props = entity.properties;
            if (!props) continue;
            
            // Try to get property values (handle both Cesium Property and plain object)
            const dataType = props.getValue ? props.getValue().dataType : props.dataType;
            const stateName = props.getValue ? props.getValue().stateName : props.stateName;
            const entityId = entity.id || '';
            
            // State boundaries have specific characteristics
            if (dataType === 'stateBoundary' || dataType === 'stateLabel' || 
                !!stateName || entityId.includes('state-boundary')) {
              matchCount++;
            }
          }
          
          // If a significant portion of sampled entities match, consider it a state boundaries layer
          if (matchCount > 0) {
            isStateBoundaries = true;
          }
        }
        
        if (isStateBoundaries) {
          console.log('🧹 Found state boundaries data source in viewer (by search), removing...', {
            name: dataSource.name,
            entityCount: dataSource.entities ? dataSource.entities.values.length : 0,
            isTracked: dataSource === this.stateBoundariesDataSource
          });
          try {
            this.viewer.dataSources.remove(dataSource, true);
            removedCount++;
            // Continue searching for more duplicates (don't break)
          } catch (error) {
            console.warn('⚠️ Error removing state boundaries data source:', error);
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Error searching for state boundaries data sources:', error);
    }
    
    // Clear entities array
    this.stateBoundariesEntities = [];
    
    // Also remove from activeLayers if present
    if (this.activeLayers.has('stateBoundaries')) {
      this.activeLayers.delete('stateBoundaries');
    }
    
    if (removedCount === 0) {
      console.log('ℹ️ No state boundaries data source found to remove');
    } else {
      console.log(`✅ Removed ${removedCount} state boundaries data source(s)`);
    }
  }
  
  /**
   * Set LEK habitat data and render if layer is active
   */
  async setLekHabitatData(data) {
    this.lekHabitatData = data || [];
    if (this.activeLayers.has('lekHabitat')) {
      await this.renderLekHabitatLayer();
    }
  }
  
  /**
   * Get LEK habitat data
   */
  getLekHabitatData() {
    return this.lekHabitatData;
  }
  
  /**
   * Render LEK habitat layer (heatmap)
   */
  async renderLekHabitatLayer() {
    if (!this.viewer || !window.Cesium || this.viewer.isDestroyed()) {
      console.log('⚠️ Cannot render LEK habitat: viewer not ready or destroyed');
      return;
    }
    
    this.removeLekHabitatLayer();
    
    if (!this.lekHabitatData || this.lekHabitatData.length === 0) {
      console.log('⚠️ Cannot render LEK habitat: no data available');
      return;
    }
    
    console.log('🐟 Rendering LEK habitat heatmap...', this.lekHabitatData.length, 'cells');
    
    // Create custom data source
    this.lekHabitatDataSource = new window.Cesium.CustomDataSource('LEK_Habitat');
    
    // Color function for probability
    const getLEKProbabilityColor = (probability) => {
      if (probability >= 0.8) return window.Cesium.Color.RED.withAlpha(0.8);
      if (probability >= 0.6) return window.Cesium.Color.ORANGE.withAlpha(0.7);
      if (probability >= 0.4) return window.Cesium.Color.YELLOW.withAlpha(0.6);
      if (probability >= 0.2) return window.Cesium.Color.GREEN.withAlpha(0.5);
      return window.Cesium.Color.BLUE.withAlpha(0.4);
    };
    
    // Create entities for each grid cell
    this.lekHabitatEntities = [];
    this.lekHabitatData.forEach((cell, index) => {
      try {
        const probability = cell.probability_total || cell.base_weighted_yield || 0;
        const color = getLEKProbabilityColor(probability);
        
        const entity = this.lekHabitatDataSource.entities.add({
          id: `lek_habitat_${cell.grid_id}_${Date.now()}_${index}`,
          name: `Grid Cell ${cell.grid_id}`,
          rectangle: {
            coordinates: window.Cesium.Rectangle.fromDegrees(
              cell.longitude - 0.01,
              cell.latitude - 0.01,
              cell.longitude + 0.01,
              cell.latitude + 0.01
            ),
            material: color,
            height: 0,
            extrudedHeight: 0,
            outline: false,
            heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            zIndex: 0
          },
          properties: {
            type: 'lekHabitat',
            gridId: cell.grid_id,
            latitude: cell.latitude,
            longitude: cell.longitude,
            speciesName: cell.species_name || 'Unknown Species',
            species_id: cell.species_id,
            probabilityTotal: probability,
          }
        });
        
        this.lekHabitatEntities.push(entity);
      } catch (error) {
        console.error('Error creating LEK habitat entity:', error);
      }
    });
    
    // Add to viewer
    await this.viewer.dataSources.add(this.lekHabitatDataSource);
    console.log(`✅ Rendered ${this.lekHabitatEntities.length} LEK habitat grid cells`);
  }
  
  /**
   * Remove LEK habitat layer
   */
  removeLekHabitatLayer() {
    if (this.lekHabitatDataSource && this.viewer && !this.viewer.isDestroyed()) {
      if (this.viewer.dataSources.contains(this.lekHabitatDataSource)) {
        this.viewer.dataSources.remove(this.lekHabitatDataSource, true);
      }
      this.lekHabitatDataSource = null;
      this.lekHabitatEntities = [];
    }
  }
  
  /**
   * Toggle a layer on/off. Public API: pass kebab-case slug.
   */
  async toggleLayer(slug) {
    if (this.isLayerActive(slug)) {
      await this.removeLayer(slug);
    } else {
      await this.addLayer(slug);
    }
  }

  /**
   * Check if a layer is active. Public API: pass kebab-case slug.
   */
  isLayerActive(slug) {
    const internalKey = this._getInternalKey(slug);
    return internalKey ? this.activeLayers.has(internalKey) : false;
  }

  /**
   * Get all active layer slugs (kebab-case).
   */
  getActiveLayers() {
    return Array.from(this.activeLayers).map((internalKey) => INTERNAL_KEY_TO_SLUG[internalKey]).filter(Boolean);
  }

  /**
   * Get a specific layer by slug. Returns null if not active.
   */
  getLayer(slug) {
    if (!this.isLayerActive(slug)) return null;
    return { id: slug, active: true };
  }
  
  /**
   * Set a filter value
   */
  async setFilter(filterType, value) {
    if (!(filterType in this.filters)) {
      throw new Error(`setFilter: Unknown filter type: ${filterType}`);
    }
    
    const previousValue = this.filters[filterType];
    this.filters[filterType] = value;
    console.log(`✅ Set filter ${filterType}:`, value, previousValue !== value ? '(changed)' : '(unchanged)');
    
    // If LEK habitat layer is active and relevant filters changed, trigger data fetch
    if (this.activeLayers.has('lekHabitat') && 
        (filterType === 'lekSpecies' || filterType === 'lekMonthRange')) {
      // Data fetching will be handled by the component that sets the filter
      // (e.g., DataLayerTabs will fetch and call setLekHabitatData)
      console.log('🔄 LEK habitat layer active, filters updated - component should fetch data', {
        filterType,
        previousValue,
        newValue: value,
        lekSpecies: this.filters.lekSpecies,
        lekMonthRange: this.filters.lekMonthRange
      });
    }
    
    // If trawl survey layer is active and relevant filters changed, re-render
    if (this.activeLayers.has('trawlSurvey') && 
        (filterType === 'trawlSurveyTimeOfDay' || 
         filterType === 'trawlSurveyMinDepth' || 
         filterType === 'trawlSurveyMaxDepth' ||
         filterType === 'trawlSurveyMinBotTemp' ||
         filterType === 'trawlSurveyMaxBotTemp')) {
      console.log('🔄 Trawl survey layer active, filters updated - re-rendering');
      await this.renderTrawlSurveyLayer();
    }
  }
  
  /**
   * Clear a filter
   */
  clearFilter(filterType) {
    if (!(filterType in this.filters)) {
      throw new Error(`clearFilter: Unknown filter type: ${filterType}`);
    }
    
    const defaultValue = filterType.includes('Filter') || filterType.includes('Simulation') 
      ? false 
      : null;
    
    this.filters[filterType] = defaultValue;
    console.log(`✅ Cleared filter: ${filterType}`);
  }
  
  /**
   * Get a filter value
   */
  getFilter(filterType) {
    return this.filters[filterType] ?? null;
  }
  
  /**
   * Get all filters
   */
  getFilters() {
    return { ...this.filters };
  }
  
  /**
   * Check if controller is ready
   */
  isReady() {
    return this.ready;
  }
  
  /**
   * Clear all active layers and filters (but keep providers)
   * Used when navigating between pages with different initialLayers configurations
   * @param {Object} [upcomingLayers] - The initialLayers about to be applied. If it includes
   *   play-scallop-simulation or play-groundfish-simulation, we do NOT stop the clock so the
   *   fleet animation can continue (avoids race where clear runs after animation starts).
   */
  async clearAllLayersAndFilters(upcomingLayers = {}) {
    console.log('🧹 Clearing all layers and filters before applying new initialLayers');

    const hasFleetLayers =
      ('play-scallop-simulation' in upcomingLayers && upcomingLayers['play-scallop-simulation'] !== false) ||
      ('play-groundfish-simulation' in upcomingLayers && upcomingLayers['play-groundfish-simulation'] !== false);
    if (this.viewer && !this.viewer.isDestroyed() && this.viewer.clock && !hasFleetLayers) {
      console.log('⏹ Stopping fleet animations (stopping Cesium clock)');
      this.viewer.clock.shouldAnimate = false;
      const now = window.Cesium.JulianDate.now();
      this.viewer.clock.currentTime = now.clone();
    }

    console.log('🧹 Aggressively removing all GeoJSON layers from viewer...');
    const geojsonSlugs = ['noaa-fishing-closed-areas', 'scallop-areas', 'iattc', 'iccat', 'iphc', 'wcpfc', 'us-regional-fishery-mgmt-councils', 'us-congressional-districts'];
    for (const slug of geojsonSlugs) {
      try {
        await this.removeLayer(slug);
        const internalKey = this._getInternalKey(slug);
        if (internalKey) this.activeLayers.delete(internalKey);
      } catch (error) {
        console.warn(`⚠️ Error removing GeoJSON layer ${slug} during clear:`, error);
      }
    }

    console.log('🧹 Explicitly removing state boundaries layer (before other layers)');
    this.removeStateBoundariesLayer();
    this.activeLayers.delete('stateBoundaries');

    const slugsToRemove = Object.keys(SLUG_TO_INTERNAL_KEY);
    for (const slug of slugsToRemove) {
      const internalKey = this._getInternalKey(slug);
      if (!this.activeLayers.has(internalKey)) continue;
      try {
        await this.removeLayer(slug);
      } catch (error) {
        console.warn(`⚠️ Error removing layer ${slug} during clear:`, error);
      }
    }

    console.log('🧹 Final check: removing state boundaries layer again');
    this.removeStateBoundariesLayer();
    this.activeLayers.delete('stateBoundaries');
    
    // Clear all filters
    Object.keys(this.filters).forEach(key => {
      this.clearFilter(key);
    });
    
    // Clear infrastructure layers data (but keep data sources structure)
    this.coldStorageData = [];
    this.coldStorageEntities = [];
    this.portsWithLandingData = [];
    this.portsWithLandingEntities = [];
    this.bottomTemperatureData = [];
    this.stateBoundariesData = null;
    this.stateBoundariesEntities = [];
    this.lekHabitatData = [];
    this.lekHabitatEntities = [];
    this.trawlSurveyData = null;
    this.trawlSurveyEntities = [];
    this.aisVesselsData = [];
    this.aisVesselsEntities = [];

    // Emit a custom event to notify components that filters/layers were cleared
    // This allows components to reset their internal state (e.g., lastFetchParamsRef)
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('mapControllerLayersCleared', {
        detail: { timestamp: Date.now() }
      }));
      console.log('📢 STOCK_SWITCH_DEBUG: Dispatched mapControllerLayersCleared event');
    }
    
    // Clear fleet animation entities (if any remain)
    if (this.viewer && !this.viewer.isDestroyed()) {
      // Remove any entities that might be fleet-related
      // Note: FleetAnimationControls manages its own entities, but we stop the clock above
      // The component should react to filter changes and clear its own entities
    }
    
    console.log('✅ All layers and filters cleared');
  }
  
  /**
   * Clear all layers from the viewer directly (without relying on activeLayers)
   * Used when creating a new MapController instance to remove orphaned layers
   */
  async _clearAllLayersFromViewer() {
    if (!this.viewer || this.viewer.isDestroyed()) {
      return;
    }
    
    console.log('🧹 Searching viewer for all MapController-managed layers to remove...');
    
    // Stop fleet animations
    if (this.viewer.clock) {
      this.viewer.clock.shouldAnimate = false;
      const now = window.Cesium.JulianDate.now();
      this.viewer.clock.currentTime = now.clone();
    }
    
    const geojsonSlugs = ['noaa-fishing-closed-areas', 'scallop-areas', 'iattc', 'iccat', 'iphc', 'wcpfc', 'us-regional-fishery-mgmt-councils', 'us-congressional-districts', 'state-boundaries'];
    for (const slug of geojsonSlugs) {
      try {
        await this.removeLayer(slug);
      } catch (error) {
        // Ignore errors - layer might not exist
      }
    }
    
    // Explicitly search for and remove state boundaries (most problematic)
    this.removeStateBoundariesLayer();
    
    // Remove infrastructure layers by searching for their data sources
    const dataSources = this.viewer.dataSources;
    for (let i = dataSources.length - 1; i >= 0; i--) {
      const ds = dataSources.get(i);
      if (!ds) continue;
      
      // Check if this is a CustomDataSource that might be from MapController
      if (ds.constructor.name === 'CustomDataSource') {
        // Check for cold storage, ports, bottom temp, LEK habitat characteristics
        if (ds.entities && ds.entities.values.length > 0) {
          const sampleSize = Math.min(3, ds.entities.values.length);
          for (let j = 0; j < sampleSize; j++) {
            const entity = ds.entities.values[j];
            const props = entity.properties;
            if (!props) continue;
            
            const dataType = props.getValue ? props.getValue().dataType : props.dataType;
            
            // Remove infrastructure layer data sources
            if (dataType === 'coldStorage' || dataType === 'portWithLanding' || 
                dataType === 'bottomTemperature' || dataType === 'lekHabitat' ||
                dataType === 'stateBoundary' || dataType === 'stateLabel') {
              console.log(`🧹 Removing orphaned ${dataType} data source from viewer`);
              this.viewer.dataSources.remove(ds, true);
              break; // Found a match, remove and move on
            }
          }
        }
      }
    }
    
    console.log('✅ Finished clearing layers from viewer');
  }
  
  /**
   * Cleanup - remove all layers and reset state
   */
  async cleanup() {
    const layersToRemove = Array.from(this.activeLayers);
    for (const internalKey of layersToRemove) {
      const slug = INTERNAL_KEY_TO_SLUG[internalKey];
      if (slug) await this.removeLayer(slug);
    }
    
    // Clear filters
    Object.keys(this.filters).forEach(key => {
      this.clearFilter(key);
    });
    
    // Clear providers
    this.imageryProviders = {};
    this.portsEntities = [];
    this.portsData = [];
    
    // Clear infrastructure layers data
    this.coldStorageData = [];
    this.coldStorageEntities = [];
    this.portsWithLandingData = [];
    this.portsWithLandingEntities = [];
    this.bottomTemperatureData = [];
    this.stateBoundariesData = null;
    this.stateBoundariesEntities = [];
    this.lekHabitatData = [];
    this.lekHabitatEntities = [];
    this.trawlSurveyData = null;
    this.trawlSurveyEntities = [];
    this.aisVesselsData = [];
    this.aisVesselsEntities = [];

    // Destroy click handler
    if (this.globalClickHandler) {
      this.globalClickHandler.destroy();
      this.globalClickHandler = null;
    }

    // Clear callbacks and API functions
    this.onColdStorageClick = null;
    this.onPortsWithLandingClick = null;
    this.onTrawlSurveyStationClick = null;
    this.onAISVesselClick = null;
    this.onLekGridCellClick = null;
    this.onMapEmptyClick = null;
    this.apiFunctions = {};
    
    console.log('✅ MapController cleaned up');
  }
}

export default MapController;
